import {
  aliasKind, hasRecallChip, probeValuesForRecalls,
  angleModeFor, addVersion, applyOptionsVersion, branchSetsToCheck, removeVersion, versionRollKey, writeOptionsVersion, visibleTargeted, roleForMode, rolesForMode, displayEvaluation,
  isReachable,
  mcqReachability,
  branchesUnreachableInRange, checkBranchCoverage, enumerateFormulaValues, formulaBoundsCheap, formulaComboCount,
  checkOptionFormulaIssues,
  evaluateEquation,
  evaluateMCQ,
  getEquationVariableNames,
  mcqBranchRoleIssue,
  parseThreshold,
  resolveEffectiveOptions,
  resolveMatchedBranch,
  TRIG_CALL_PATTERN,
  syncScenarioBranches, type ScenarioLeaderVersion, remapVersionsToScenarios, drawnScenarioId, markScenarioReviewed,
  unreviewedCount, pickScenarioBranch, resolveScenarioOptions, scenarioInForce, feedbackForScenarioRow,
  type DisplayVersion, type RoleVersion, type ScenarioRoleBranch,
  type AngleMode,
  type MCQBlockData,
  type MCQOption,
  type MCQFeedback,
  type FeedbackState,
  type ConditionalRoleBranch,
  activeFeedbackForBranch } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, Check, GripVertical, Image as ImageIcon, ImageOff, Images, Minus, Pin, SquareCheck, Trash2, Volume2, VolumeX, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimatedImage, pictureName, useAnimatedAssetIds } from './animated-image';
import { useAnswerRefScope, useRolledWithRecalls } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { type BlockIssue, duplicateKeyIgnoringBlank, findDuplicateGroups, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { requestBranchJump, useBranchJumpTarget } from './branch-jump';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { CollapsedInputBlock, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { SlashedLabel, chipToneClass, UnreachableChip } from './eval-chip';
import { FeedbackBranchPager } from './feedback-branch-pager';
import { FeedbackCard, ChipDivider, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { FormulaField, FormulaWithValues } from './formula-field';
import { checkOptionChipCollisions, richTextToFormula as optionTextToFormula, stripHtmlForLabel } from './formula-plaintext';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { LibraryModal } from './library-modal';
import { MediaKindIcon } from './media-kind-icon';
import { fmtLocale } from './number-format';
import { reorderByIds, shuffleWithAnchors } from './option-order';
import { OptionsSection } from './options-section';
import { recallOptionSpaces, recallSpacesKey } from './recall-option-spaces';
import { RichTextEditor } from './rich-text-editor';
import { ScenarioLinkMenu } from './scenario-link-menu';
import { ScenarioRemapModal } from './scenario-remap-modal';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { ScenarioStimulus } from './scenario-stimulus';
import { WaveformPlayer } from './sound-waveform-player';
import { StrandWarning, useArmSettle } from './strand-warning';
import { TriggerDropdown } from './trigger-dropdown';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenRowDeleteWarning } from './use-driven-row-dependents';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, hasEmptyFeedbackContent, normalizeFeedback, positiveTriggerForMode, resetContentIfCustomized, retriggerPositiveSlot } from './use-input-block-feedback';
import { makeRowBranch, nestedBranching, type SeedMode } from './use-nested-branching';
import { useRecallAffordance } from './use-recall-affordance';
import { useRestoredData } from './use-restored-data';
import { useScenarioRowProjection } from './use-scenario-rows';
import { useDelayedStickyError, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useVariableMechanismAllowed } from './use-variable-affordance';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { ANSWER_COLUMN_CLASS, BlockQuestion, AnswerDisplayFrame, formulaToRichText, hasRollableVariables, htmlHasVariableChips, recallAliasIdsIn, labelChipHtml, PreviewHtml, processVariableChipHTML, renderChipsForDisplay, rollVariableValues, useChipScope, variableChipNames } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { useVersionInForce } from './version-in-force';
import { VersionSelector } from './version-selector';

import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { RadioGroup } from '@/components/ui/radio-group';
import { TextField } from '@/components/ui/text-field';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { assetsApi, peekAsset, type AssetSummary } from '@/lib/assets-api';
import { catchAllTitle, legendStatesFor, catchAllChips, mcqChips, mcqOptionPreviewNode } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { mcqTargetedTitleText } from '@/lib/block-state-labels';
import { sanitizeAuthoredHtml } from '@/lib/sanitize-authored-html';

// ─── Legacy format migration ──────────────────────────────────────────────────

function normalizeMCQLegacy(raw: unknown): unknown {
  const r = raw as Record<string, unknown> | undefined;
  if (r && !Array.isArray(r?.states) && (r.unansweredTitle || r.unanswered || r.unansweredTrigger)) {
    return {
      states: [{
        id: crypto.randomUUID(), type: 'targeted',
        title: (r.unansweredTitle as string) ?? '',
        trigger: (r.unansweredTrigger as string) ?? 'unanswered',
        content: (r.unanswered as string) ?? '',
        evaluation: (r.unansweredEvaluation as FeedbackState['evaluation']) ?? 'neutral',
      }],
    };
  }
  return raw;
}/** Bridges "jump to this branch" from the live-test widget (the collapsed canvas card) to the
 * editor — identical contract to Dropdown's own `requestBranchJump`: they're separate mounted
 * instances of this component, with no other channel to hand off a value like this. See that
 * function's own doc comment in dropdown-block.tsx for the two-path (already-open vs.
 * not-yet-open) reasoning. */
// ─── Types ────────────────────────────────────────────────────────────────────

interface MCQBlockProps {
  block: Block;
  /** Needed only for the per-option media picker (LibraryModal) — same props Image/Sound blocks
   * already receive via the same shared `sharedProps`/`baseProps` spread in task-content.tsx. */
  experienceId: string;
  taskId?: string;
  isPreview: boolean;
  /** Bumped once per click of Preview's single bottom Submit — see task-content.tsx's own doc
   * comment. Only meaningful while `isPreview`; unused otherwise. */
  previewSubmitSignal?: number;
  /** `opts` carries `normalization`, for a write the block makes on its own behalf — see the
   *  media self-heal below, and `BlockUpdateOptions`. */
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

interface TriggerOption {
  value: string;
  label: string;
  preview?: string;
  /** Present only for a media option — lets the trigger chip/dropdown render an image/sound icon
   * and bracket the label, so it never reads as if `preview` were real student-facing text. */
  mediaKind?: 'image' | 'audio';
  evaluation?: 'correct' | 'incorrect' | 'neutral';
}

/** Small image/sound glyph used everywhere a media option's Label needs to visually announce
 * "this isn't the text shown to students" — feedback/Flow/trigger chips, never the actual
 * student-facing render (which shows the real thumbnail/player instead). */
// ─── Helpers ──────────────────────────────────────────────────────────────────

/** A new option, for an event handler. NEVER call this during render — see DEFAULT_OPTIONS. */
function makeOption(): MCQOption {
  return { id: crypto.randomUUID(), text: '', role: 'neutral', anchored: false };
}

/**
 * The two options a block that has never been edited starts with.
 *
 * Fixed ids, not `makeOption()`. `initData` runs during render — seven times per render in this
 * file — so minting ids here gave a block whose `data` carries no `options` a brand-new pair of
 * option ids on EVERY render. Measured: one click on such a block minted 18 fresh UUIDs.
 *
 * That broke the block in a way that looked like a grading bug and wasn't. The teacher clicks the
 * radio holding id X; by the time `evaluateMCQ` runs, `correctIdSet` holds a different, newly
 * minted id, so the set-match fails and every answer graded Incorrect — including the one marked
 * correct. The test widget's own selection died the same way, which is why the radio never showed
 * as checked. Nothing was ever neutral and no role was wrong; the answer key was fine and its
 * subject kept being replaced underneath it.
 *
 * Ids only have to be unique WITHIN a block's option list — feedback triggers are `option_<id>`
 * inside this block's own feedback, and AssetUsage keys optionId alongside blockId — so constants
 * are sufficient, and being constant is the whole point.
 */
const DEFAULT_OPTIONS: readonly MCQOption[] = [
  { id: 'default-option-1', text: '', role: 'correct', anchored: false },
  { id: 'default-option-2', text: '', role: 'incorrect', anchored: false },
];

function initData(raw: Record<string, unknown>): MCQBlockData {
  type RawOption = { id: string; text: string; formula?: string; media?: MCQOption['media']; role?: MCQOption['role']; isCorrect?: boolean; anchored?: boolean };
  const opts = raw.options as RawOption[] | undefined;
  return {
    question: (raw.question as string) ?? '',
    options: opts?.length
      ? opts.map((o) => ({ id: o.id, text: o.text, formula: o.formula, media: o.media, role: o.role ?? (o.isCorrect ? 'correct' : 'neutral'), anchored: o.anchored ?? false }))
      // Fresh copies, so a caller mutating its options can't write through to the shared constant.
      : DEFAULT_OPTIONS.map(o => ({ ...o })),
    allowMultiple: (raw.allowMultiple as boolean) ?? false,
    randomizeOrder: (raw.randomizeOrder as boolean) ?? false,
    shape: (['vertical', 'horizontal'].includes(raw.shape as string) ? raw.shape : 'vertical') as MCQBlockData['shape'],
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string)
      ? raw.gradingMode as MCQBlockData['gradingMode']
      : 'graded'),
    feedback: ((raw.feedback ?? raw.behavior) ?? { states: [] }) as MCQBlockData['feedback'],
    conditionalEquation: raw.conditionalEquation as MCQBlockData['conditionalEquation'],
    conditionalBranches: raw.conditionalBranches as ConditionalRoleBranch[] | undefined,
    conditionalBranchesDraft: raw.conditionalBranchesDraft as MCQBlockData['conditionalBranchesDraft'],
    optionsAsEquations: (raw.optionsAsEquations as boolean) ?? false,
    angleMode: (['degrees', 'radians'].includes(raw.angleMode as string) ? raw.angleMode as AngleMode : 'degrees'),
    angleModes: (raw.angleModes as Record<string, AngleMode> | undefined),
    /* Carried through explicitly, because this function builds a NEW object rather than spreading
       `raw` — anything not named here is silently dropped, which is how the question bank
       vanished the first time it was saved. */
    versions: raw.versions as MCQBlockData['versions'],
    /* Same hazard, same fix: a scenario link that is read back as absent is a block that
       silently stops following, with its per-scenario answers orphaned behind it. */
    followsBlockId: raw.followsBlockId as MCQBlockData['followsBlockId'],
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as MCQBlockData['drivenBy'],
    scenarioBranches: raw.scenarioBranches as MCQBlockData['scenarioBranches'],
  };
}

/** Human-readable label for a single option — its own text (or, when `optionsAsEquations` is on,
 * its raw unresolved formula string — same source `MCQTriggerChip`'s own `preview` already reads,
 * see the option-row preview at `optionsAsEquations ? opt.formula : opt.text`), or blank when the
 * option itself is blank. Deliberately does NOT fall back to a positional "Option 2" placeholder:
 * that would read as real, already-authored content and hide the fact that the option needs to be
 * filled in — an obviously-incomplete label (nothing) is the honest signal, matching Dropdown's
 * own `dropdownOptionLabel`. `index`/`t` are unused now but kept in the signature so every call
 * site (and mcqTargetedTitle below) doesn't need touching again if a future positional need
 * reappears. Deliberately shows the raw formula, not an evaluated number — this label backs
 * titles/chips that persist across rolls (Flow condition text, feedback titles), so baking in
 * today's random roll's value would go stale the moment variables re-roll. */
/** The title lives in `block-state-labels` now: Flow reads the same derivation this writes with,
 *  instead of reading whatever this last wrote into the state. */
/** The mechanically-derived, non-editable title for a targeted feedback state — always
 * reflects exactly which option (selected or, for multi-select, required-but-absent) it
 * targets, so two feedbacks can only collide on title when they genuinely target the same
 * condition (a real setup error), never by coincidence. */
const mcqTargetedTitle = mcqTargetedTitleText;

/** Re-derives every targeted feedback title against the current options array — needed
 * whenever an option's text OR position changes, since a blank option's label falls back to its
 * position ("Option 2") and any option's title embeds its current text. */
function resyncMcqTargetedTitles(options: MCQOption[], states: FeedbackState[], t: (key: string, opts?: Record<string, unknown>) => string, optionsAsEquations?: boolean): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted' || !s.trigger.startsWith('option_')) return s;
    const isAbsent = s.trigger.endsWith('_absent');
    const id = isAbsent ? s.trigger.slice('option_'.length, -'_absent'.length) : s.trigger.slice('option_'.length);
    const idx = options.findIndex(o => o.id === id);
    if (idx === -1) return s;
    return { ...s, title: mcqTargetedTitle(options[idx]!, idx, isAbsent, t, optionsAsEquations) };
  });
}

/** Targeted feedback's evaluation always mirrors its target option's role, wherever the
 * trigger targets a present option (`option_<id>`) — keeps the two from silently drifting
 * apart, same fix as Dropdown's `resyncDropdownTargetedEvaluations`. The "Not Selected"
 * (`_absent`) trigger has no underlying role to derive from — nothing in the option list
 * represents "the option was left unselected" — so it's excluded and stays freely cyclable.
 *
 * Includes `role === 'correct'` — this state becomes functionally unreachable the instant its
 * own option is correct (correct-first-terminal means it's never even consulted), so the actual
 * evaluation value stops mattering at runtime either way, but it must still update: Flow's
 * branch-collapsing (flow-utils.ts's feedbackStatesOf) relies on evaluation === 'correct' to
 * detect "this branch's copy is dead, exclude it from this condition's scope" — leaving it stale
 * here silently broke that detection, letting a targeted state for a now-correct option keep
 * showing up as a live Flow condition. The block's own editor was never affected by the staleness
 * (isTargetedStaleForActive already determines visibility via a direct role lookup, not this
 * field), which is exactly why this went unnoticed until Flow started trusting evaluation. */
function resyncMcqTargetedEvaluations(options: MCQOption[], states: FeedbackState[]): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted' || !s.trigger.startsWith('option_') || s.trigger.endsWith('_absent')) return s;
    const id = s.trigger.slice('option_'.length);
    const opt = options.find(o => o.id === id);
    if (!opt) return s;
    return { ...s, evaluation: opt.role };
  });
}

/** Resolves what an option should actually DISPLAY as — identical contract to Dropdown's
 * `dropdownOptionDisplayText`. When `optionsAsEquations` is on, the option's `formula` is
 * evaluated and the RESULT is what's shown (a plain formatted number, never the formula string
 * itself); otherwise falls through to `.text` exactly as before this feature existed. Blank/
 * unevaluable stays blank, same "obviously incomplete, never a placeholder" rule as everywhere
 * else — a HTML-safe plain string either way, safe to feed anywhere `.text` (rich HTML) was
 * previously read, including `dangerouslySetInnerHTML`. */
function mcqOptionDisplayText(
  option: MCQOption,
  optionsAsEquations: boolean | undefined,
  resolvedVariables: Record<string, number>,
  locale: string,
  angleMode: AngleMode,
): string {
  if (!optionsAsEquations) return option.text;
  if (!option.formula || !option.formula.trim()) return '';
  const v = evaluateEquation(option.formula, resolvedVariables, angleMode);
  return v === null ? '' : fmtLocale(v, locale);
}

// ─── Authoring-time warnings ────────────────────────────────────────────────────
// One computation, read by every surface (collapsed bar, test-panel diagnostics, in-editor
// banner) instead of each maintaining its own similar-but-not-identical checks — mirrors
// Dropdown's `dropdownIssues` exactly, extended for `allowMultiple` only where the branch
// role-count check itself needs it (see `mcqBranchRoleIssue`).
/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `mcqIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function mcqBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return mcqIssues(buildNormalizedMCQData(initData(block.data), ctx.t), ctx);
}

export function mcqIssues(d: MCQBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables, recalls = {} } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryAlternativeCorrectStates = t('blocks.areas.alternativeAnswerSetup');
  const categoryFeedback = t('blocks.areas.feedback');
  // A media option has real, selectable student-facing content regardless of whether its Label
  // (an internal identifier, not displayed content) is filled in — it's never "blank" the way an
  // option with no text/formula and no media genuinely has nothing for a student to see or pick.
  //
  // In equations mode, checks `formula` OR `text`, deliberately not formula-only — `text` is left
  // untouched by the toggle, so a pre-existing option with real content still genuinely has
  // content the moment the toggle flips, even before its new formula is authored. Formula-only
  // would make "all options blank" fire the instant a teacher turns equations mode on (before
  // they've had any chance to type a single formula), which is a false alarm about content that
  // was never actually removed — Ordering hit the more severe version of this same bug (it hid an
  // entire display surface, not just an error message; see that block's own doc comment on
  // `orderingItemIsBlank`) but the underlying reasoning is identical here.
  /*
   * UNREVIEWED ROWS, counted — §4.4.
   *
   * The strip inside the answer-setup box only shows for the row you are on, so a teacher who
   * never pages to scenario 5 never learns that scenario 5 was never decided. That is the exact
   * failure the flag exists to prevent, moved one level out: work that looks finished and is not.
   *
   * An INCOMPLETION, not a defect — every row has a working answer key; what is missing is
   * somebody's confirmation that the copy is right for that scenario.
   */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  const optionIsBlank = (o: MCQOption) =>
    o.media ? false : d.optionsAsEquations ? (!o.formula?.trim() && !o.text.replace(/<\/?p>/g, '').trim()) : !o.text.replace(/<\/?p>/g, '').trim();
  const questionBlank = !d.question.replace(/<\/?p>/g, '').trim();
  const allOptionsBlank = d.options.every(optionIsBlank);

  if (questionBlank) issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  if (allOptionsBlank) {
    issues.push({ key: 'options-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.allBlankWarning') });
  } else if (d.gradingMode === 'graded') {
    // Same "branching makes the static role vestigial" reasoning as Dropdown — see
    // dropdownIssues' own comment.
    if (d.conditionalBranches && d.conditionalBranches.length > 0) {
      d.conditionalBranches.forEach((b, i) => {
        const branchCorrect = d.options.find(o => b.roles[o.id] === 'correct');
        if (branchCorrect && optionIsBlank(branchCorrect)) {
          issues.push({ key: `branch-${b.id}-correct-blank`, severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.diagnosticBranchCorrectBlank', { number: i + 1 }) });
        }
      });
    } else {
      const staticCorrect = d.options.find(o => o.role === 'correct');
      if (!staticCorrect) {
        issues.push({ key: 'no-correct-option', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.answers.noCorrectAnswers') });
      } else if (optionIsBlank(staticCorrect)) {
        issues.push({ key: 'correct-option-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.correctBlankWarning') });
      }
    }
  }
  // A blank DISTRACTOR (not the correct option, already covered above) is exactly as confusing
  // to a student as a blank Ordering item — an unlabeled, empty option tile — regardless of
  // whether its `role` happens to make the gap easy for a teacher to overlook while authoring.
  // Ordering has no per-option role to hide behind, so it already flags this per-item; this is
  // the same real student-facing gap, closed here too (2026-08-02 parity fix). Not gated on
  // `allOptionsBlank`/`gradingMode` — a single blank formula among otherwise-filled options is
  // exactly as real a problem in survey/optional mode as in graded.
  if (d.optionsAsEquations && d.options.some(o => !o.media && !o.formula?.trim())) {
    issues.push({ key: 'option-formula-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaBlankWarning') });
  }

  // Exact-duplicate option text is a real fairness/correctness hazard, not just clutter — a
  // student can't tell two identical-looking options apart (worst in allowMultiple mode: selecting
  // both fires both options' feedback simultaneously, a self-contradictory result), the same
  // fairness standard already used for optionsAsEquations' own value-collision check. Skipped
  // entirely in optionsAsEquations mode — two IDENTICAL formulas are already guaranteed to collide
  // on every roll, a case checkOptionFormulaIssues' own (more general, evaluated-value) collision
  // detection already catches; comparing raw formula strings here would just be a redundant,
  // weaker version of that check. Deliberately exact string equality on the raw stored HTML, not
  // case/accent-normalized or stripped-to-plain-text — two options that only look the same after a
  // formatting difference (e.g. one bold, one not) are NOT flagged, only options whose stored
  // content is byte-for-byte identical. `duplicateKeyIgnoringBlank` (not a bare `.trim()`) is load-
  // bearing here, not decorative — a RichTextEditor's "empty" state serializes to `<p></p>`, so
  // without it every blank option would register as an identical non-empty string and get flagged
  // as a duplicate of every other blank option (a real bug, caught live once Ordering's own
  // duplicate-item check surfaced the same false positive on a block with 2+ blank fields).
  if (!d.optionsAsEquations) {
    // Media options are excluded here — their `text` is the internal label (feedback/Flow/
    // reports), not student-facing content, so two media options sharing a label is meaningless
    // to flag; what actually matters for them is a shared assetId, checked separately below.
    for (const group of findDuplicateGroups(d.options.filter(o => !o.media), o => duplicateKeyIgnoringBlank(o.text))) {
      issues.push({
        key: `duplicate-${group.map(o => o.id).join('-')}`,
        fields: group.map(o => o.id),
        severity: 'error',
        kind: 'defect',
        category: categoryAnswerSetup,
        detail: t('blocks.options.duplicateOptionWarning', { text: stripHtmlForLabel(group[0]!.text) }),
      });
    }
  }

  // Two options pointing at the exact same asset — flagged the same way as identical text,
  // exact-match only (no fuzzy comparison; two different assets can't be meaningfully compared
  // for "similarity" the way two text strings can).
  for (const group of findDuplicateGroups(d.options.filter(o => o.media), o => o.media!.assetId)) {
    issues.push({
      key: `duplicate-media-${group.map(o => o.id).join('-')}`,
      fields: group.map(o => o.id),
      severity: 'error',
      kind: 'defect',
      category: categoryAnswerSetup,
      detail: t('blocks.mcq.duplicateMediaWarning'),
    });
  }

  // A DEFECT, not an incompletion, and the trigger is why: `o.media && !o.text` fires only once
  // the teacher has attached media. Adding the image is the authored act; the missing Label is the
  // contradiction it creates, which is exactly the defect definition. An untouched option row is
  // the absence, and it has its own key.
  //
  // The consequences are concrete rather than cosmetic: the option drops out of the Correct chip
  // (optionCorrectChipLabel filters blanks, so the chip disappears if it was the only correct one)
  // and its targeted-feedback title reads "Selected " with nothing after it. mcqOptionLabel's
  // positional fallback now stops the blank render; this reports the underlying setup problem.
  //
  // Reachable mostly by deliberately CLEARING a label, since handleMediaSelect seeds one from the
  // asset's filename on insert.
  // One issue total, not one per option — a block with several media options all missing
  // a Label doesn't need the identical message repeated once per option.
  const optionsMissingLabel = d.options.filter(o => o.media && !o.text.replace(/<\/?p>/g, '').trim());
  if (optionsMissingLabel.length > 0) {
    issues.push({ key: 'media-label-blank', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.mcq.mediaLabelBlankWarning') });
  }

  // Missing alt-text on an image option is a real accessibility error, not a warning — without it
  // a screen-reader user has no way to know what the option even is. A defect on the same reading
  // as the Label above: the trigger is an image having been chosen, so this is a required companion
  // left blank, not a field nobody has reached yet. Audio deliberately has no
  // equivalent check: a transcript specific enough to distinguish two "which sound is X" options
  // is specific enough to leak the answer, so there's no safe text to require there (see
  // [[project_mcq_ordering_media_options_scoping]] — accepted limitation, not an oversight).
  const imageOptionsMissingAlt = d.options.filter(o => o.media?.kind === 'image' && !o.media.altText?.trim());
  /* An ANIMATED option owes a motion description for the same reason the Image block's does --
     alt text describes the first frame and the content is in what changes (WCAG 1.2.1).
     `kind: 'defect'` matches the alt-text check directly above rather than the Image block's
     `incompletion`; the two blocks disagree about this already, and a row that is a defect for a
     missing alt and an incompletion for a missing description would be the stranger pair. */
  const animatedOptionsMissingMotion = d.options.filter(
    o => o.media?.kind === 'image' && o.media.animated === true && !o.media.motionDescription?.trim(),
  );
  if (animatedOptionsMissingMotion.length > 0) {
    issues.push({ key: 'media-motion-description-missing', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.mcq.mediaMotionDescriptionRequired') });
  }
  if (imageOptionsMissingAlt.length > 0) {
    issues.push({ key: 'media-alt-text-missing', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.mcq.mediaAltTextRequired') });
  }

  // Everything below requires an explicit action (turning on branching, typing an equation,
  // marking a role) — see dropdownIssues' own comment for why none of this needs the grace
  // period, and why it's NOT gated on `!allOptionsBlank` either.
  /*
   * PER VERSION, not once for whatever version happens to be on screen.
   *
   * These checks read version-resolved data, so a banked block was only ever validated for the
   * version being displayed: a coverage gap authored in version 3 stayed invisible until someone
   * stepped to it. `branchSetsToCheck` yields one obligation per version (or the block's own when
   * there is no bank), so the answer no longer depends on what is selected — which also retires a
   * stale-memo hazard, since the result is now the same whichever version is in force.
   *
   * A version with branches has an obligation; one without does not and contributes nothing.
   */
  if (d.gradingMode === 'graded') {
    for (const set of branchSetsToCheck<ConditionalRoleBranch>(d)) {
      const branches = set.branches ?? [];
      if (branches.length === 0) continue;
      /** Keys must differ per set, or two sets' gaps collapse into one issue. */
      const vkey = (k: string) => (set.versionIndex !== null ? `v${set.versionIndex}-${k}`
        : set.scenarioIndex !== null ? `s${set.scenarioIndex}-${k}` : k);
      /** And the message must say WHICH one, or it names a condition nobody can find. */
      const vlabel = (detail: string) => (set.versionIndex !== null
        ? t('blocks.branching.versionScopedIssue', { number: set.versionIndex, detail })
        : set.scenarioIndex !== null
          ? t('blocks.branching.scenarioScopedIssue', { number: set.scenarioIndex, detail })
          : detail);
      const equationTrimmed = set.equation?.trim();
      if (!equationTrimmed) {
        issues.push({ key: vkey('no-equation'), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticNoEquation')) });
      } else if (evaluateEquation(equationTrimmed, minSampleValues(variables), angleModeFor(d, 'conditionalEquation')) === null) {
        issues.push({ key: vkey('invalid-equation'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.equationInvalidWarning')) });
      } else {
        // The domain narrows coverage to the values the equation can actually produce when it is a
        // bare scenario-selector variable; null everywhere else, which keeps the continuous check.
        const domainValues = enumerateFormulaValues(equationTrimmed, variables);
        // Too many values to build the set, but still a discrete variable. The real-line gap
        // analysis would then name regions it can never produce ("<0, >0" for a 1-to-100
        // variable), so gaps are suppressed and the teacher is told coverage went unchecked.
        const comboCount = domainValues ? null : formulaComboCount(equationTrimmed, variables);
        const discreteButUnchecked = comboCount !== null;
        const coverage = checkBranchCoverage(branches, domainValues);
        const gapIssues = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
        const overlapIssues = coverage.issues.filter(i => i.matchingBranchIds.length > 0);
        if (gapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-gap'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.missingCoverage', { list: formatCoverageList(gapIssues, t, 'gap') })) });
        }
        if (overlapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-overlap'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlapIssues, t, 'overlap') })) });
        }
        if (discreteButUnchecked) {
          issues.push({ key: vkey('coverage-unchecked'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
            detail: vlabel(t('blocks.branching.coverageUnchecked', { count: comboCount })) });
        }
        // Reachability survives a domain too large to enumerate: bounds alone prove a threshold
        // outside them can never be met, and for a bare variable the bounds are its own min and max.
        const cheapBounds = domainValues ? null : formulaBoundsCheap(equationTrimmed, variables, angleModeFor(d, 'conditionalEquation'));
        if (cheapBounds) {
          branchesUnreachableInRange(branches, cheapBounds.lo, cheapBounds.hi).forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: cheapBounds.lo, hi: cheapBounds.hi })) });
          });
        }
        // A condition no attainable value can satisfy: its whole answer setup is dead. A WARNING, not
        // an error — nothing a student does can reach it, so it cannot mis-grade anyone, and where it
        // does cause harm the coverage gap beside it already says so at error severity. Matches
        // `feedback-overlaps-correct`, the existing "authored but can never fire" issue.
        if (domainValues && domainValues.length > 0) {
          coverage.unreachableBranchIds.forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: domainValues[0], hi: domainValues[domainValues.length - 1] })) });
          });
        }
      }
      branches.forEach((b, i) => {
        const roleIssue = mcqBranchRoleIssue(b, d.options, d.allowMultiple);
        if (roleIssue === 'none-correct') issues.push({ key: vkey(`branch-${b.id}-none`), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.options.diagnosticBranchNoneCorrect', { number: i + 1 })) });
        if (roleIssue === 'multiple-correct') issues.push({ key: vkey(`branch-${b.id}-multi`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.options.diagnosticBranchMultipleCorrect', { number: i + 1 })) });
        if (b.operator === 'between') {
          const lo = parseThreshold(b.threshold);
          const hi = parseThreshold(b.thresholdHigh);
          if (Number.isFinite(lo) && Number.isFinite(hi) && lo > hi) {
            issues.push({ key: vkey(`branch-${b.id}-inverted`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticBranchInverted', { number: i + 1 })) });
          }
        }
      });
    }
  }

  // Both errors — same fairness reasoning as Dropdown's own checkOptionFormulaIssues consumption.
  if (d.optionsAsEquations) {
    // Every variable, not just the rollable ones: a constant or an equation is as referenceable
    // from an option formula as a random, and filtering them out reported the option unevaluable.
    const formulaVars = variables;
    const { issues: formulaIssues } = checkOptionFormulaIssues(d.options, formulaVars, id => angleModeFor(d, `options.${id}.formula`));
    for (const issue of formulaIssues) {
      if (issue.kind === 'unevaluable') {
        issues.push({ key: `option-${issue.optionIds[0]}-unevaluable`, fields: issue.optionIds, severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaUnevaluable') });
      }
    }
    // One issue total, not one per colliding pair — with 3+ options all pairwise colliding
    // (A-B, A-C, B-C), a teacher doesn't need the identical message three times over.
    if (formulaIssues.some(i => i.kind === 'collision')) {
      issues.push({ key: 'option-collision', fields: formulaIssues.flatMap(i => i.optionIds), severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaCollision') });
    }
  } else {
    // Warning, not error — unlike a full-formula collision (which means two options are
    // mathematically identical for that combo), this is a plain-text option that happens to embed
    // a variable chip (e.g. "Mama {n}" / "Mama {m}") — a coincidence of the specific ranges
    // chosen, not a guarantee the options are the same thing. Same message as the equation-mode
    // collision above: "could show the same value" reads correctly for either case.
    const chipVars = variables;
    const { issues: chipIssues } = checkOptionChipCollisions(d.options, chipVars, recalls);
    // One issue total, not one per colliding pair — same reasoning as the equation-mode case above.
    if (chipIssues.length > 0) {
    // ERROR, matching the equation-mode collision above, because it IS the same check: both
    // enumerate every combination of the declared variable domains and flag a pair that matches in
    // any one of them. Neither proves the two are always equal. This was a warning on the theory
    // that a chip collision is "a coincidence of the ranges chosen" while a formula collision is
    // mathematical identity — but `checkOptionFormulaIssues` samples the same finite domain and
    // reports the same kind of coincidence, so the distinction was not real.
    //
    // It had the severity backwards, too. In equation mode a teacher can read two formulas side by
    // side and reason about whether they collide; with chips in plain text the collision is
    // invisible without enumerating rolls mentally. The harder case to spot was the quieter one.
      issues.push({ key: 'option-chip-collision', fields: chipIssues.flatMap(i => i.optionIds), severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaCollision') });
    }
  }

  // Validation asks the SAME question grading does. A targeted state in the top-level set can never
  // fire once branches are configured — `activeFeedbackForBranch` strips them in the unresolved
  // fallback — so a blank one is unreachable by construction and reporting it sent a teacher
  // looking for something the editor cannot even show them.
  const hasMissingFeedbackText = hasEmptyFeedbackContent(activeFeedbackForBranch(undefined, d.feedback, (d.conditionalBranches?.length ?? 0) > 0).states) ||
    !!d.conditionalBranches?.some(b => hasEmptyFeedbackContent(b.feedback.states));
  if (hasMissingFeedbackText) issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });

  return issues;
}

// ─── Test panel derivation ──────────────────────────────────────────────────
// Mirrors Dropdown's own "Test panel derivation" section — one derivation, shared by the
// desktop collapsed/isSelected card and the phone editor's own "Test" tab, plus the interactive
// isPreview branch below.

interface MCQTestData {
  resolvedVariables: Record<string, number>;
  effectiveOptions: MCQOption[];
  matchedBranch: ConditionalRoleBranch | null;
  matchedBranchIndex: number;
  matchedEquationValue: number | null;
  testResult: FeedbackState[] | null;
  resolvedD: MCQBlockData;
  testChips: TestBarChip[];
  hasChips: boolean;
  hasRollable: boolean;
}

/** Backfills default feedback text/titles for a block read straight from `block.data` — identical
 * contract to Dropdown's own `buildNormalizedDropdownData`, shared by both non-editing render
 * paths (the collapsed/isSelected test widget and the student-facing Preview) so they can never
 * drift into two different sets of defaults. */
/**
 * Normalised block data, with the drawn question VERSION already in force.
 *
 * Version first, then normalise: a version carries its own feedback, and it has to go
 * through the same defaulting the block's own does or a versioned block would miss the
 * titles a plain one gets. `applyOptionsVersion` is identity when there are no versions, so
 * this is unchanged for every block that has never had one authored.
 */
function buildNormalizedMCQData(
  rawD: MCQBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, from the session roll. */
  versionId?: string,
): MCQBlockData {
  const rawVersioned = applyOptionsVersion(rawD, versionId);
  return {
    ...rawVersioned,
    feedback: normalizeFeedback(
      rawVersioned.feedback,
      {
        unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
        unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
        incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
        incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
        correctTitle: rawVersioned.gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle')
          : rawVersioned.gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle')
          : t('blocks.feedbackDefaults.anyResponseTitle'),
        correctContent: rawVersioned.gradingMode === 'graded' ? `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`
          : rawVersioned.gradingMode === 'survey' ? `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`
          : `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
      },
      [],
      positiveTriggerForMode(rawVersioned.gradingMode),
    ),
  };
}

// Fired-feedback display — Preview and the live test widget both show this identically once an
// answer has been submitted. Identical logic to Dropdown's own `renderDropdownFiredFeedback`,
// kept as a separate copy rather than a shared export for the same reason as
// `formatCoverageRegionCompact` above (no shared non-block-scoped home for a two-block-only
// helper this small). Resolves variable chips in the feedback content against the same rolled
// values the question/options already display with — a chip in feedback text is authored the
// same way as one in a question/option, so it needs the same substitution before render, not
// the raw `{var}` markup.
//
// `default` states (No Answer/Correct/Incorrect/any_answer/any_response) only ever fire alone —
// `evaluateMCQ` returns the `correct` state in total isolation, terminally, before it ever
// considers targeted states, so they keep their existing one-box-per-state look untouched.
// `targeted` (targeted) states are the ones that can fire in parallel (MCQ's multi-select can
// match several at once) and are structurally never `evaluation: 'correct'` — a targeted state's
// evaluation is either derived from its option's role (locked, never 'correct') or, for an
// `_absent` trigger, teacher-cyclable but confined to `['neutral', 'incorrect']` in the editor —
// so grouping them into just an incorrect and a neutral cluster is exhaustive, not a simplification
// that drops a case. Each cluster shows its icon once with every message in that cluster listed
// beneath it, instead of repeating the same icon/color box per message.
/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderMCQFiredFeedback(testResult: FeedbackState[] | null, rolledValues: Record<string, string>): React.ReactNode {
  return <FiredFeedback states={testResult} rolledValues={rolledValues} />;
}

/** Shared by `mcqStateCondition` and `computeMCQTestData`'s own per-option chip expansion below —
 * the same blank-stays-blank preview rendering used everywhere else an option's text is shown.
 * For a media option, `opt.text` is its Label (never shown to students) — icon-prefixed and
 * bracketed here so it never reads like real option text. */
/**
 * MCQ's two default conditions as one chip list — the derivation the feedback panel and the test
 * legend both draw.
 *
 * This block had the most copies of any: the legend built `TestBarChip`s from
 * `reachability.correct.{required,forbidden,optional}`, the panel built spans from its own
 * `uncoveredCorrect` / `allIncorrectOptions` / `allNeutralOptions`, and each rendered option labels
 * through its own near-identical helper (`mcqOptionPreviewNode` against `optContent`). Three pairs
 * of copies for one condition, and they had already drifted in two places:
 *
 *   - The optional chips. The legend drew them at full strength with an italic "(optional)" tag;
 *     the panel drew them dimmed and italic with a plain one.
 *   - The required set. `reachability.correct.required` is EVERY correct option — which is what
 *     `evaluateMCQ` actually demands, and what the reachability tests pin. The panel filtered by
 *     targeted coverage, so a correct option carrying stale targeted feedback silently dropped out
 *     of the answer key the teacher was reading.
 *
 * The reachability version wins on both counts: it is the tested one.
 *
 * SHAPE, for multi-select, is a conjunction with a tail: every correct option present AND every
 * incorrect one absent (drawn slashed — the slash negates the CONDITION, "X was not selected", not
 * the value), then the neutral options trailing with no connector, because a neutral option
 * constrains nothing and joining it with "and" would claim a requirement that does not exist.
 *
 * Single-select needs none of that: picking the one correct option already specifies correctness.
 *
 * Default Incorrect ENUMERATES rather than summarizing, and is the one catch-all in this family
 * that can. MCQ's option set is closed, so the complement of the correct answer is a short, known
 * list — there is no "any other selection" a student could make. That is why `blocks.mcq.chipAnyOther`
 * was never written: nothing should have been reaching for it.
 */

/** Resolves the human-readable "condition" a feedback state represents for the test bar legend —
 * same job as Dropdown's `dropdownStateCondition`. MCQ's `correct` trigger is handled entirely
 * separately in `computeMCQTestData` now (expanded into one chip per option for allowMultiple,
 * since there's no single "the" correct option to point at the way Dropdown's radio behavior has
 * one) — this function is only ever called here for `unanswered`/`any_answer`/`any_response`/
 * `incorrect`/targeted (targeted) states, plus the single-select `correct` fallback. */
function mcqStateCondition(
  state: FeedbackState, d: MCQBlockData, t: (key: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
  if (state.trigger === 'unanswered') return <>{t('blocks.feedbackDefaults.noAnswerTitle')}</>;
  if (state.trigger === 'any_answer') return <>{t('blocks.feedbackDefaults.anyAnswerTitle')}</>;
  if (state.trigger === 'any_response') return <>{t('blocks.feedbackDefaults.anyResponseTitle')}</>;
  if (state.trigger === 'correct') {
    const correctOptions = d.options.filter(o => o.role === 'correct');
    if (correctOptions.length === 0) return null;
    return (
      <>
        {correctOptions.map((o, i) => (
          <React.Fragment key={o.id}>
            {i > 0 && ' + '}
            {mcqOptionPreviewNode(o)}
          </React.Fragment>
        ))}
      </>
    );
  }
  if (state.trigger === 'incorrect') return <>{t('blocks.feedbackDefaults.incorrectTitle')}</>;
  const isAbsent = state.trigger.endsWith('_absent');
  const optionId = state.trigger.startsWith('option_')
    ? (isAbsent ? state.trigger.slice('option_'.length, -'_absent'.length) : state.trigger.slice('option_'.length))
    : null;
  if (optionId == null) return null;
  const opt = d.options.find(o => o.id === optionId);
  if (!opt) return null;
  return mcqOptionPreviewNode(opt);
}

function computeMCQTestData(
  d: MCQBlockData,
  variables: TaskVariable[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  rolledValues: Record<string, string>,
  submittedSelectedIds: string[],
  hasSubmitted: boolean,
  locale: string,
  /**
   * The leader's variant in force, when this block follows one.
   *
   * Passed in rather than derived: the three callers below know it from three different places
   * — the task-wide preview roll, the editor's own pager, the phone test tab — and a function
   * that went looking for it would have to know about all three.
   */
  scenarioId?: string,
): MCQTestData {
  const resolvedVariables: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((entry): entry is [string, number] => !isNaN(entry[1]))
  );
  // Branches are only ever consulted in graded mode — see evaluateMCQ's own gate.
  //
  // A FOLLOWER resolves against the scenario in force instead of an equation — the same split
  // `evaluateMCQ` makes internally, mirrored here because this builds what the teacher SEES and
  // the two must never disagree about which answer key is live.
  const followed = d.gradingMode === 'graded' && !!d.scenarioBranches && d.scenarioBranches.length > 0;
  const scenarioRow = followed ? pickScenarioBranch(d.scenarioBranches!, scenarioId) : null;
  const effectiveOptions = rolesForMode(d.gradingMode, d.gradingMode !== 'graded'
    ? d.options
    : followed
      /* The same five arguments `evaluateMCQ` passes. They were absent here while the
         evaluator had them, so a row with nested branching showed the teacher its unbranched
         key and graded the student on the branched one. */
      ? resolveScenarioOptions(d.options, d.scenarioBranches, scenarioId, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
      : resolveEffectiveOptions(d.options, d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation')));
  const matchedBranch = !followed && d.gradingMode === 'graded'
    ? resolveMatchedBranch(d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
    : null;
  /* A nested branch may carry its own feedback, and `feedbackForScenarioRow` is what falls
     back to the row's when it does not — the same call `evaluateMCQ` makes. Reading
     `scenarioRow.feedback` directly skipped the inner level. */
  const activeFeedbackForDisplay = scenarioRow
    ? feedbackForScenarioRow(scenarioRow, resolvedVariables, angleModeFor(d, 'conditionalEquation')) ?? scenarioRow.feedback
    : matchedBranch ? matchedBranch.feedback : d.feedback;
  const matchedBranchIndex = matchedBranch ? (d.conditionalBranches ?? []).findIndex(b => b.id === matchedBranch.id) : -1;
  const matchedEquationValue = matchedBranch && d.conditionalEquation ? evaluateEquation(d.conditionalEquation, resolvedVariables, angleModeFor(d, 'conditionalEquation')) : null;
  const testResult = hasSubmitted ? evaluateMCQ({ selectedIds: submittedSelectedIds }, d, resolvedVariables, scenarioId) : null;
  const resolvedD: MCQBlockData = {
    ...d,
    options: effectiveOptions.map(o => ({
      ...o,
      text: d.optionsAsEquations
        ? mcqOptionDisplayText(o, true, resolvedVariables, locale, angleModeFor(d, `options.${o.id}.formula`))
        : processVariableChipHTML(o.text, rolledValues),
    })),
  };
  /**
   * The one answer both surfaces read. Built from `resolvedD` so a matched branch's reassigned
   * roles are already in force — reachability is a property of the setup a teacher is LOOKING at,
   * not of the static top-level shape underneath it.
   */
  const reachability = mcqReachability(resolvedD, resolvedD.options);
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  const isVisibleTargeted = (s: FeedbackState) => {
    if (s.type !== 'targeted') return false;
    if (!s.trigger.startsWith('option_')) return true;
    const isAbsent = s.trigger.endsWith('_absent');
    const id = isAbsent ? s.trigger.slice('option_'.length, -'_absent'.length) : s.trigger.slice('option_'.length);
    const opt = effectiveOptions.find(o => o.id === id);
    if (!opt) return false;
    /**
     * Multi-select FIRST, and in every mode: a single pick cannot omit an option while still
     * being a pick, so an absent trigger has nothing to describe. Turning multi-select off with
     * absent states authored used to leave them on screen in graded, where the check was only
     * about the role — cards referring to a condition the block could no longer produce.
     */
    if (isAbsent && !d.allowMultiple) return false;
    // Outside graded the roles are all neutral, so the role gates below collapse: every option
    // can carry a present state, and any of them an absent one.
    if (d.gradingMode !== 'graded') return true;
    return isAbsent ? opt.role === 'correct' : opt.role !== 'correct';
  };
  // 'correct' is handled entirely separately below (expanded into per-option chips for
  // allowMultiple) — excluded from legendStates so it's never also emitted as one joined chip here.
  const { listed: legendStates, catchAll: participationCatchAll } =
    legendStatesFor(d.gradingMode, activeFeedbackForDisplay.states, isVisibleTargeted);
  const testChips: TestBarChip[] = legendStates.map(s => {
    /**
     * In GRADED an `_absent` state always references a correct-role option, so it is coloured by
     * that role rather than by whatever evaluation the teacher set — the same green+slash
     * "correct option not selected" chip everywhere.
     *
     * Outside graded it references any option and there is no role to colour it by, so it is
     * neutral like everything else. Forcing 'correct' there would put a tick on "they did not
     * pick this" in a mode with nothing to be right about.
     */
    const isAbsent = s.type === 'targeted' && s.trigger.endsWith('_absent');
    const isAbsentTargeted = isAbsent && d.gradingMode === 'graded';
    return {
      id: s.id,
      condition: mcqStateCondition(s, resolvedD, t),
      /**
       * The SLASH is what separates "Rope" from "Rope", and it is not decoration — it negates the
       * CONDITION ("Rope was not selected"), not the value. Graded could get away without it on
       * this row because the role coloured the chip green; outside graded both chips are neutral,
       * so without the slash the two opposite conditions rendered identically.
       */
      slashed: isAbsent,
      evaluation: isAbsentTargeted ? 'correct' as const : displayEvaluation(d.gradingMode, s.evaluation),
      active: firedIds.has(s.id),
      category: s.trigger === 'unanswered' ? 'unanswered' as const
        : s.trigger === 'any_answer' ? 'any_answer' as const
        : s.trigger === 'any_response' ? 'any_response' as const
        : 'targeted' as const,
    };
  });
  // The 'correct' trigger's full boolean condition, expanded into one chip per option instead of
  // a single joined label — allowMultiple's exact-match requires every correct option present
  // AND every incorrect option absent, with neutral options optional either way; a single joined
  // chip only ever showed what must be present, hiding half the actual condition. Mirrors the
  // feedback panel's own `defaultCorrectInline` breakdown exactly (same three buckets, same
  // colors, and the same "and" between them), just rendered as separate TestBarChip pills instead
  // of one inline row. Single-select
  // has no such combination — the one correct option's label already fully specifies correctness
  // — so it keeps the original single mcqStateCondition chip.
  const correctState = d.gradingMode === 'graded' ? activeFeedbackForDisplay.states.find(s => s.trigger === 'correct') : undefined;
  if (correctState) {
    const correctFired = firedIds.has(correctState.id);
    // Asked of the shared derivation rather than worked out here. This is the seam the whole phase
    // is about: the bar and the panel now read one answer instead of computing two, and that answer
    // is tested against `evaluateMCQ` itself. It also picks up a rule neither surface had — that
    // single-select with two correct options can never be satisfied.
    testChips.push(...toTestBarChips(mcqChips('correct', reachability, d.allowMultiple, t), {
      id: correctState.id,
      category: 'correct',
      active: correctFired,
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    }));
  }
  // "Uncovered incorrect option" fan-out — same reasoning as Dropdown's own version: a
  // catch-all Default Incorrect state that has no dedicated per-option feedback covering some
  // non-correct option shows one synthetic chip per uncovered option, so the legend never hides
  // which specific wrong picks would actually fall through to the generic message.
  const incorrectAnyState = d.gradingMode === 'graded' ? activeFeedbackForDisplay.states.find(s => s.trigger === 'incorrect') : undefined;
  if (incorrectAnyState) {
    const incorrectAnyFired = firedIds.has(incorrectAnyState.id);
    // Which term the submission actually landed on: a wrong option it selected, or a correct one it
    // left out. Both are ways into Default Incorrect, and the legend has always lit only the one
    // that happened.
    const liveIncorrect = new Set([
      ...reachability.uncoveredWrong.filter(o => submittedSelectedIds.includes(o.id)).map(o => o.id),
      ...reachability.uncoveredAbsent.filter(o => !submittedSelectedIds.includes(o.id)).map(o => o.id),
    ]);
    testChips.push(...toTestBarChips(mcqChips('incorrect', reachability, d.allowMultiple, t), {
      id: incorrectAnyState.id,
      category: 'incorrect',
      active: incorrectAnyFired,
      activeSourceIds: liveIncorrect,
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    }));
  }

  /**
   * The catch-all, fanned out exactly as graded's Default Incorrect is just above.
   *
   * It used to arrive through `legendStates` as ONE chip reading "Any Answer", which is the single
   * thing the feedback panel stopped saying when the catch-all learned to name what it still
   * catches. Same derivation as the panel's now, so the two cannot word it differently.
   */
  if (participationCatchAll) {
    testChips.push(...toTestBarChips(
      catchAllChips(d.gradingMode, legendStates.some(s => s.type === 'targeted'), t,
        () => mcqChips('incorrect', reachability, d.allowMultiple, t, d.gradingMode)), {
        id: participationCatchAll.id,
        category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
        active: firedIds.has(participationCatchAll.id),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
  }
  // A media option's `text` is its Label, never rich-text-capable (see SortableOptionRow's Label
  // input) — excluded so a stray chip left over in old data can't resurrect equations-mode UI.
  const hasChips = htmlHasVariableChips(d.question) || (d.optionsAsEquations
    ? d.options.some(o => getEquationVariableNames(o.formula ?? '').length > 0)
    : d.options.some(o => !o.media && htmlHasVariableChips(o.text)));
  const hasRollable = hasRollableVariables(variables);
  return { resolvedVariables, effectiveOptions, matchedBranch, matchedBranchIndex, matchedEquationValue, testResult, resolvedD, testChips, hasChips, hasRollable };
}

// The one canonical "what a student sees" layout — question, then the answer control — shared by
// every place that renders a student-facing view of this block (currently the teacher-facing
// Preview and the editor's own live-test widget; the eventual student interface should reuse this
// same shape once it consumes @agavi/blocks, rather than re-deriving the layout independently).
// Identical role to Dropdown's own `DropdownAnswerDisplay`, using real radio/checkbox semantics
// (role='radiogroup'/'group' of role='radio'/'checkbox' buttons, roving tabindex) instead of a
// single select, since MCQ's control is a list of options rather than one dropdown. Deliberately
// never colors an option by its role — that would leak the answer key to whoever's looking; only
// the fired-feedback `children` below ever reveals correctness, exactly matching Dropdown's own
// convention.
// Fetches the placed asset for a single media option and renders it — image gets an actual
// thumbnail (this is the real "look and pick" surface the whole feature exists for), audio gets a
// real (ghosted/non-scrubbable, Play-button-only) waveform via the same WaveformPlayer the Sound
// block's own collapsed card already uses — see that component's own doc comment for why
// non-scrubbable: this sits inside a selectable option button, and a draggable waveform would
// fight the button's own click-to-select handling, but Play alone doesn't have that conflict.
// `compact` sizes both down for the passive collapsed-canvas summary row instead of the full
// test/Preview tile size. `alt=''`/no separate label on the image — the accessible name for the
// option is set once, on the wrapping button in MCQAnswerDisplay, to avoid double-announcing.
function MCQMediaOptionContent({ media, compact, ownerName }: { media: NonNullable<MCQOption['media']>; compact?: boolean;
  /** The option's own name, so its embedded Play button says which clip it plays. */
  ownerName?: string }) {
  const { t } = useTranslation();
  // Seeded from the cache, so an asset already in memory is drawn in the FIRST frame rather
  // than after the effect's promise settles — see `peekAsset`, and image-block for the why.
  const [asset, setAsset] = useState<AssetSummary | null>(() => peekAsset(media.assetId) ?? null);
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    assetsApi.get(media.assetId)
      .then((a) => { if (!cancelled) setAsset(a); })
      .catch((err) => {
        if (cancelled) return;
        // Was previously a silent no-op — a failed fetch (network blip, a stale/deleted
        // assetId) just left this permanently blank with zero indication anything was wrong,
        // which is exactly the kind of dead end that makes "why isn't my image showing" hard to
        // debug. Logged, not just swallowed, plus a visible fallback state below.
        console.error('MCQMediaOptionContent: failed to load asset', media.assetId, err);
        setLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, [media.assetId]);

  // Fixed square — only ever used for a generic placeholder/error icon, never real image
  // content, so cropping concerns don't apply here.
  const placeholderCls = compact ? 'h-8 w-8' : 'h-20 w-20 sm:h-28 sm:w-28';
  if (loadFailed) {
    return (
      <div className={`flex ${placeholderCls} shrink-0 items-center justify-center rounded-md bg-ws-error-bg`}>
        <AlertTriangle className='h-4 w-4 text-ws-error-fg' aria-hidden='true' />
      </div>
    );
  }

  if (media.kind === 'image') {
    const src = asset?.urls.display ?? asset?.urls.thumb ?? asset?.urls.original;
    // A forced square crop (object-cover) silently deletes content from anything non-square —
    // a spectrum image is exactly the failure case: long and thin, with the diagnostic detail
    // running along its full length, so center-cropping to a square would cut real data, not
    // just padding. Capping height and letting width follow the image's own aspect ratio never
    // crops; max-w-full is the same safety net in the other direction for anything unusually wide.
    const maxHeightCls = compact ? 'max-h-8' : 'max-h-20 sm:max-h-28';
    // No `shrink-0` here (unlike every other option-content element) — this is what forced a
    // real overflow: a `shrink-0` image sized purely from its own height-capped aspect ratio
    // ignores however much width the flex row actually has left after the indicator/gap, and
    // spills past the tile's edge instead of shrinking to fit, since `max-w-full` only bounds
    // against the image's own containing block, not the remaining flex space beside a sibling.
    const imgCls = `${maxHeightCls} max-w-full h-auto w-auto min-w-0 rounded-md object-contain`;
    return src ? (
      /*
       * NO `motionDescription` passed, deliberately: `alt=''` here because the option's accessible
       * name lives on the tile, so a description wired to this image would hang off something a
       * reader never reaches. The tile carries it instead, beside the name.
       *
       * `autoplay={false}` because an answer list can hold four of these at once, and four loops
       * competing for attention beside a question is worse than one. `still` on the compact tile:
       * at 32px the control would be bigger than the picture.
       */
      asset ? (
        <AnimatedImage asset={asset} alt='' autoplay={false} still={compact} align='start' className={imgCls} />
      ) : (
        <img src={src} alt='' className={imgCls} />
      )
    ) : (
      <div className={`flex ${placeholderCls} shrink-0 items-center justify-center rounded-md bg-ws-hover`}>
        <ImageIcon className='h-4 w-4 text-ws-muted' aria-hidden='true' />
      </div>
    );
  }
  // Just play + waveform — no skip/speed controls, which don't earn their space on a compact
  // selectable option tile the way they do on the Sound block's own full player. A definite width
  // is required here (see WaveformPlayer's own `width` prop doc) since this sits in a single row
  // with no other sibling content to derive an implicit width from.
  return <WaveformPlayer src={asset?.urls.audio} t={t} height={compact ? 28 : 48} width={compact ? 96 : 160} interactive={false} showSpeed={false} hideSkip ownerName={ownerName} />;
}

/**
 * WHAT AN IMAGE OPTION IS CALLED, motion included.
 *
 * ## Why the motion text is in the NAME and not a description
 *
 * It was an `aria-describedby` pointing at a hidden span, which is the textbook shape and is wrong
 * for this content. A description is announced LAST — after the role and the state — and it is the
 * first thing a reader's verbosity settings drop; JAWS and NVDA both switch descriptions off. That
 * is an acceptable home for supplementary detail and not for the text alternative an animation
 * owes under WCAG 1.2.1. Folded into the name, it is announced immediately after the alt text,
 * before "radio button, not checked", and cannot be suppressed.
 *
 * ## Why the words "Animation" and "Motion" are in it
 *
 * Normally naming the medium in a label is the redundancy every alt-text guide warns about, because
 * the ROLE already says it. Neither applies here.
 *
 * "Animation" replaces "Image" because the role never says either: this is a `radio`, and nothing
 * else tells a listener the content is a picture, let alone a moving one. "Image" was already
 * supplying the first half of that for exactly this reason; an animation announced as an image is
 * worse than unlabelled, because it is wrong.
 *
 * "Motion" is load-bearing precisely BECAUSE the two are now one string. The property boundary that
 * used to separate them — a pause, a change of voice — is gone, so without the word a listener
 * hears two sentences and cannot tell that the second describes CHANGE rather than more of the
 * still. The motion description is not an interpretation of the picture; it is the content.
 */
function mediaImageName(
  opt: MCQOption,
  idx: number,
  animated: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  /* One composer, shared with Ordering, so the two blocks cannot drift on how a picture is named.
     See `pictureName` for why the word "Image" or "Animation" belongs in the name HERE and not on
     a loose `<img>`. */
  const named = pictureName(opt.media?.altText, opt.media?.motionDescription, animated, t);
  if (named) return named;
  /* The positional fallback is this block's alone: a loose picture has no "option 3" to fall back
     on, and an option with no alt text still has to be told apart from its neighbours. */
  return t(animated ? 'blocks.options.animationOptionFallback' : 'blocks.options.imageOptionFallback', { number: idx + 1 });
}

function MCQAnswerDisplay({
  question, options, optionOrder, rolledValues, value, onChange, allowMultiple, disabled, horizontal, scenario, children,
}: {
  question: string;
  /** Lay the options in a wrapping ROW instead of a stack — `MCQBlockData.horizontalOptions`. */
  horizontal?: boolean;
  /** The follower's stem, ghosted, above the question — see `AnswerDisplayFrame`. Passed by the
   *  editor's test surfaces only; Preview has the leader block itself on the page. */
  scenario?: React.ReactNode;
  options: MCQOption[];
  optionOrder: string[] | null;
  rolledValues: Record<string, string>;
  value: string[];
  onChange: (ids: string[]) => void;
  allowMultiple: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  /* Which option images move — asked here rather than passed in, because every caller of this
     component would otherwise have to thread it through, and the hook already de-duplicates by
     asset id. See `mediaImageName`, which is the only reason this is needed on the display side. */
  const animatedAssetIds = useAnimatedAssetIds(
    useMemo(() => options.flatMap(o => (o.media?.kind === 'image' ? [o.media.assetId] : [])), [options]),
  );
  const ordered = reorderByIds(options, optionOrder);
  // Roving-tabindex requires DOM focus to move WITH the tabIndex/checked state on every
  // arrow-key step, not just visually — otherwise focus silently strands on a button that just
  // became tabIndex={-1} and unchecked, which reads as broken to a screen reader and makes the
  // next Tab press skip past both the old and new selection. `value`/`onChange` are controlled by
  // the parent, so the actual DOM node for the new index isn't guaranteed to exist synchronously
  // after `onChange` — same reasoning as `MCQTriggerDropdown`'s own `requestAnimationFrame` focus
  // call just above.
  const optionBtnRefs = useRef<(HTMLButtonElement | HTMLDivElement | null)[]>([]);

  function toggle(id: string) {
    if (disabled) return;
    if (allowMultiple) onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
    else onChange([id]);
  }

  function handleKeyDown(e: React.KeyboardEvent, idx: number) {
    if (allowMultiple) return; // checkboxes need no roving-focus arrow behavior — Tab moves between them normally
    let next: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (idx + 1) % ordered.length;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (idx - 1 + ordered.length) % ordered.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = ordered.length - 1;
    if (next === null) return;
    e.preventDefault();
    const nextOpt = ordered[next]!;
    onChange([nextOpt.id]);
    const focusIdx = next;
    requestAnimationFrame(() => { optionBtnRefs.current[focusIdx]?.focus(); });
  }

  const selectedIdx = !allowMultiple ? ordered.findIndex(o => value.includes(o.id)) : -1;
  const focusIdx = selectedIdx >= 0 ? selectedIdx : 0;

  return (
    <AnswerDisplayFrame question={question} rolledValues={rolledValues} scenario={scenario}>
      <div
        role={allowMultiple ? 'group' : 'radiogroup'}
        aria-label={t('blocks.mcq.answerOptionsLabel')}
        /*
         * A STACK by default, a wrapping ROW when the teacher asks for one.
         *
         * The thing that was tried and reverted here was a multi-column GRID that reflowed its
         * column count by viewport — unasked for, so it read as chaotic. This is the opposite:
         * authored per block, the same arrangement everywhere, and visible to the teacher in
         * the editor while they set it.
         *
         * `flex-wrap`, never a scroller: on a narrow screen the row becomes a stack again,
         * which is the honest degradation. Short options sit side by side where there is room,
         * and nothing is ever pushed out of reach. `items-stretch` so two options of unequal
         * length still present equal-height targets rather than a ragged row.
         */
        className={horizontal
          /* `justify-center`, measured: without it the options pack to the LEFT inside a column
             that is itself centred, so a three-option row sat in the left half with 242px of
             dead space beside it — centred by the column and visibly not centred to the eye.
             The vertical stack does not need it: its buttons already fill the column's width. */
          ? 'flex flex-row flex-wrap items-stretch justify-center gap-2'
          : 'flex flex-col gap-2'}
      >
        {ordered.map((opt, idx) => {
          const checked = value.includes(opt.id);
          // A media option has no visible text (its `text` is the internal label, never
          // shown to students) — the button needs SOME accessible name regardless, so an
          // assistive-tech user can still tell options apart and select one. Falls back to a
          // positional "Option N" only for this purpose — never shown visually, unlike the
          // teacher-authoring surfaces' own "blank stays visually blank" rule, which is about a
          // sighted teacher not mistaking incomplete content for real content; a screen-reader
          // user has no visual fallback to fall back on.
          /**
           * A media option says WHAT KIND of thing it is, then which one.
           *
           * "Option 3" was true and useless: it did not say the option was a picture or a sound,
           * so a listener could not tell why there was nothing to read, and every media option in
           * the block announced in the same shape as every text one.
           *
           * Images lead with their alt text and are introduced by the word Image — the alt text is
           * content, and "Image:" says the content is a picture, which the role (checkbox) cannot.
           * With no alt text there is nothing to introduce, so it falls back to position.
           *
           * Sound is position only, deliberately and permanently: the transcript field that would
           * name it does not exist for audio options, because a transcript specific enough to tell
           * two "which sound is X" options apart is specific enough to give away the answer.
           */
          const mediaAccessibleName = !opt.media ? undefined
            : opt.media.kind === 'audio' ? t('blocks.options.soundOptionLabel', { number: idx + 1 })
            : mediaImageName(opt, idx, animatedAssetIds.has(opt.media.assetId), t);
          const indicator = (
            <span className={`flex h-4 w-4 shrink-0 items-center justify-center border-2 transition-colors ${
              allowMultiple ? 'rounded-sm' : 'rounded-full'
            } ${checked ? 'border-ws-primary bg-ws-primary' : 'border-ws-border'}`}>
              {checked && <Check className='h-3 w-3 text-ws-btn-on-primary' strokeWidth={3} />}
            </span>
          );

          /*
           * No border/box on any option, media or text — selection is conveyed by the indicator
           * alone (filled circle/checkmark), matching how plain text options always worked before
           * media options existed.
           *
           * A frame WAS tried here and taken out again. The ask it came from — "the stacks are
           * becoming large enough that it may be difficult to distinguish one option from another"
           * — is about the EDITOR, where an option is a pill plus three fields and the rows run
           * together. This is the student's view: one picture per option, an indicator beside it,
           * and nothing else competing. The frame belongs where the crowding is, and `SortableOptionRow`
           * has it.
           */
          const tileClassName = `flex items-center gap-3 text-left ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`;

          // Media options render a nested interactive control of their own (WaveformPlayer's
          // Play button), which HTML forbids inside a <button>. A <div> with the same
          // role/aria-checked/keyboard handling is the legal equivalent — same precedent the
          // Sound block already uses for its own clickable-container-with-nested-button case.
          if (opt.media) {
            return (
              <div
                key={opt.id}
                ref={(el) => { optionBtnRefs.current[idx] = el; }}
                role={allowMultiple ? 'checkbox' : 'radio'}
                aria-checked={checked}
                aria-label={mediaAccessibleName}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : (allowMultiple || idx === focusIdx ? 0 : -1)}
                onClick={() => !disabled && toggle(opt.id)}
                onKeyDown={e => {
                  if (disabled) return;
                  // Let a nested interactive control (the audio option's own Play button)
                  // handle its own keys — otherwise Enter/Space bubbling up from it would
                  // toggle selection instead of playing, since this div's own handler would
                  // preventDefault() the button's native activation first.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(opt.id); return; }
                  handleKeyDown(e, idx);
                }}
                /* The teacher's own words are in this tile's NAME, not on the picture inside it —
                   that image carries `alt=''` on purpose. `hasAuthoredName` looks for an `<img>`
                   with alt text, so without this marker a media option fell out of the "Your
                   Content" scope entirely: a teacher reviewing what they had written was shown
                   every option except the ones they had described. Audio is deliberately NOT
                   marked: its name is ours ("Sound clip 3"), and claiming it as theirs would tell
                   them they had written something they had not. */
                {...(opt.media.kind === 'image' && opt.media.altText?.trim() ? { 'data-authored': '' } : {})}
                className={tileClassName}
              >
                {indicator}
                <MCQMediaOptionContent media={opt.media} ownerName={mediaAccessibleName} />
              </div>
            );
          }

          return (
            <button
              key={opt.id}
              ref={(el) => { optionBtnRefs.current[idx] = el; }}
              type='button'
              role={allowMultiple ? 'checkbox' : 'radio'}
              aria-checked={checked}
              aria-label={mediaAccessibleName}
              disabled={disabled}
              tabIndex={allowMultiple || idx === focusIdx ? 0 : -1}
              onClick={() => toggle(opt.id)}
              onKeyDown={e => handleKeyDown(e, idx)}
              className={`${tileClassName} disabled:cursor-not-allowed`}
            >
              {indicator}
              <PreviewHtml html={opt.text} as='span' className='text-sm text-ws-text [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic [&_u]:underline [&_s]:line-through [&_a]:text-ws-text-link [&_a]:underline [&_p:empty]:min-h-[1.25rem]' />
            </button>
          );
        })}
      </div>
      {children}
    </AnswerDisplayFrame>
  );
}

// ─── Option row ───────────────────────────────────────────────────────────────

interface SortableOptionRowProps {
  opt: MCQOption;
  optionCount: number;
  allowMultiple: boolean;
  roleLocked: boolean;
  showAnchor: boolean;
  armedOptionId: string | null;
  setArmedOptionId: (id: string | null) => void;
  onRoleClick: (id: string) => void;
  onToggleAnchor: (id: string) => void;
  onTextChange: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  optionEditorsRef: { current: Map<string, Editor> };
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
  /** When set, the content editor is a plain FormulaField (mathjs expression, richtext fully
   * disabled) instead of the usual RichTextEditor — see MCQBlockData.optionsAsEquations' doc
   * comment. `onFormulaChange`/`formulaHasError`/`variables`/`rolledValues`/`angleMode` are only
   * ever consulted in this mode. Identical contract to Dropdown's own `formulaMode`. */
  formulaMode?: boolean;
  onFormulaChange?: (id: string, formula: string) => void;
  formulaHasError?: boolean;
  /**
   * This option's content is identical to another option's, so the pair is indistinguishable to a
   * student. The issues panel already NAMES the collision; this points at which rows it means,
   * which the panel cannot do — it has no way to say "these two".
   */
  hasDuplicate?: boolean;
  variables?: TaskVariable[];
  rolledValues?: Record<string, string>;
  angleMode?: AngleMode;
  experienceId: string;
  taskId: string;
  taskBlocks: Block[];
  onMediaChange: (id: string, media: MCQOption['media'], seedLabel?: string) => void;
}

function SortableOptionRow({ opt, optionCount, allowMultiple, roleLocked, showAnchor, armedOptionId, setArmedOptionId, onRoleClick, onToggleAnchor, onTextChange, onRemove, optionEditorsRef, activeEditorRef, setActiveEditor, formulaMode, onFormulaChange, formulaHasError, variables, rolledValues, angleMode, experienceId, taskId, taskBlocks, onMediaChange , hasDuplicate}: SortableOptionRowProps) {
  /* Warns when this row is the only thing choosing a version somewhere later —
     see `useDrivenRowDependents`. Undefined for the overwhelming majority. */
  const strandWarning = useDrivenRowDeleteWarning(opt.id);
  /* See `useArmSettle`: the warning below is what moves the button. */
  const armSettled = useArmSettle(armedOptionId === opt.id);
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: opt.id });
  // Same rule as every other error marker: wait for the row to stop changing before the ring
  // first appears, then hold it until the collision is actually gone.
  const showDuplicate = useDelayedStickyError(!!hasDuplicate, opt.formula ?? opt.text);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  /** Two presses to drop a placed asset, matching `MediaOverlayControls` on the Image and Sound
   *  blocks. Row-local, not the row-delete's `armedOptionId`: arming one must not arm the other. */
  const [mediaArmed, setMediaArmed] = useState(false);
  // Seeded from the cache — see `peekAsset`, and image-block for the why.
  const [cachedAsset, setCachedAsset] = useState<AssetSummary | null>(
    () => (opt.media?.assetId ? peekAsset(opt.media?.assetId) ?? null : null),
  );
  // The Label field is a plain input (never a rich-text editor — see its render below), so it
  // needs its own local focus target instead of optionEditorsRef's Map of tiptap Editors.
  const labelInputRef = useRef<HTMLInputElement>(null);
  // The input's OWN displayed value, seeded once from opt.text (stripped of any legacy rich-text
  // markup a plain-text option might have carried before media was ever attached) and updated only
  // by the user's own keystrokes from then on — deliberately NOT re-derived from opt.text via
  // stripHtmlForLabel on every render. That used to be the input's `value` directly, but
  // stripHtmlForLabel ends in `.trim()`, so the instant a space was the LAST character typed
  // (which it always is, right when you type it), the next render trimmed it back off before the
  // following keystroke could land — spaces could never survive mid-word, silently squashing
  // "hello world" into "helloworld". opt.text itself is still updated on every keystroke via
  // onTextChange below, so persistence is unaffected.
  const [labelDraft, setLabelDraft] = useState(() => stripHtmlForLabel(opt.text));

  // Lazily resolve the placed asset (fresh signed URLs + filename) whenever the option only has
  // an assetId persisted — same pattern as image-block.tsx/sound-block.tsx's own cachedAsset.
  useEffect(() => {
    if (!opt.media?.assetId) { setCachedAsset(null); return; }
    if (cachedAsset?.id === opt.media.assetId) return;
    let cancelled = false;
    assetsApi.get(opt.media.assetId).then((a) => { if (!cancelled) setCachedAsset(a); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opt.media?.assetId]);

  function handleMediaSelect(asset: AssetSummary, chosen: { altText?: string; transcript?: string }) {
    const existingLabel = stripHtmlForLabel(opt.text).trim();
    const seededLabel = existingLabel || asset.filename.replace(/\.[^.]+$/, '');
    onMediaChange(
      opt.id,
      { kind: asset.kind, assetId: asset.id, animated: !!asset.animated, altText: chosen.altText, transcript: chosen.transcript },
      existingLabel ? undefined : seededLabel,
    );
    setCachedAsset(asset);
    setShowLibraryModal(false);
    // Re-seed the label draft now, at the exact moment this row transitions into media mode —
    // opt.text may still hold rich-text markup from before media was attached (or be a no-op if
    // it's already plain from a prior edit). Seeded from the asset's own filename when there is no
    // label yet: this field is NEVER shown to students (see MCQAnswerDisplay), so unlike an answer
    // value a default here cannot mislead anyone — it only has to be distinguishable, and the
    // teacher's own filename already is. That makes the missing-Label defect something you meet
    // only by deliberately clearing it, rather than on every single insert.
    setLabelDraft(seededLabel);
    // The cursor lands here so the seeded text can be typed straight over.
    requestAnimationFrame(() => { labelInputRef.current?.focus(); });
  }

  /** Four controls in a column need an edge; one editor box already is one. */
  const mediaFramed = !!opt.media && !formulaMode;

  return (
    <div
      data-field-id={opt.id}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className='flex min-w-0 flex-col gap-1'
    >
      <div
        className={`flex min-w-0 items-center gap-2 ${isDragging ? 'opacity-40' : ''} ${
          armedOptionId === opt.id ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''
        }`}
      >
        <div
          {...listeners}
          {...attributes}
          aria-label={t('blocks.mcq.dragOption')}
          className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
        >
          <GripVertical className='h-4 w-4' />
        </div>

        <button
          type='button'
          onClick={() => onRoleClick(opt.id)}
          disabled={roleLocked}
          aria-label={
            allowMultiple
              ? opt.role === 'correct'   ? t('blocks.roleCycle.correct')
                : opt.role === 'incorrect' ? t('blocks.roleCycle.incorrect')
                : t('blocks.roleCycle.neutral')
              : opt.role === 'correct' ? t('blocks.mcq.markedCorrect')
                : t('blocks.mcq.markSetCorrect')
          }
          className='-mx-3 flex shrink-0 items-center justify-center p-3 disabled:cursor-not-allowed disabled:opacity-40'
        >
          <span className={`pointer-events-none flex h-5 w-5 items-center justify-center transition-colors ${
            allowMultiple ? 'rounded-sm' : 'rounded-full'
          } ${
            opt.role === 'correct' ? 'border-2 border-ws-primary bg-ws-primary' :
            opt.role === 'incorrect' ? 'bg-ws-muted/20' :
            'border-2 border-ws-border'
          }`}>
            {opt.role === 'correct' && <Check className='h-3.5 w-3.5 text-ws-btn-on-primary' strokeWidth={3} />}
            {opt.role === 'incorrect' && <X className='h-3.5 w-3.5 text-ws-muted' strokeWidth={3} />}
            {opt.role === 'neutral' && <Minus className='h-3 w-3 text-ws-muted' strokeWidth={2} />}
          </span>
        </button>

        {/* A ring, not a recoloured border: the text editor's border is drawn by tiptap's own
            `border-ws-border` class, and a second border-colour utility racing it would be decided
            by CSS source order rather than by intent. A ring also costs no layout.

            It does NOT clear while the row is focused, unlike FormulaField's error border. That rule
            exists so a value is not called malformed mid-keystroke, and it does not transfer: a
            duplicate is a relationship between two rows, not a half-typed value, and the moment you
            click into one to fix it is the moment you most need to still see which other row it
            collides with. Both rings stay up until the collision is gone. */}
        {/* A MEDIA option is framed; a text one is not.
            A text option is one editor box, and the box is its own edge. A media option is a pill,
            an alt-text field, sometimes a motion description and always a label — four controls deep,
            and stacked three options high they run together into one undifferentiated column with no
            way to see where an option starts. The frame draws that line.
            The duplicate ring still rides on top: a ring paints outside the border box, so the two
            layers read as a framed option that is ALSO flagged, rather than fighting for the edge. */}
        <div className={`flex min-w-0 flex-1 flex-col gap-1 ${
          mediaFramed ? 'rounded-lg border border-ws-border bg-ws-btn/40 p-2' : 'rounded'
        } ${showDuplicate ? 'ring-1 ring-ws-error-fg' : ''}`}>
          {formulaMode ? (
            // Checked BEFORE `opt.media` — equations mode suppresses media entirely rather than
            // letting it coexist per-option (2026-08-02 standing rule, mirrors Ordering's identical
            // `SortableItemRow` fix). The toggle handler already strips every option's media the
            // moment this mode turns on, so by the time this branch could be reached for a
            // non-blank-media option, that's stale props from a render in flight, not a real steady
            // state — this ordering is the belt to that handler's suspenders.
            <FormulaField
              fieldId={`options.${opt.id}.formula`}
              value={opt.formula ?? ''}
              onChange={(formula) => onFormulaChange?.(opt.id, formula)}
              variables={variables ?? []}
              rolledValues={rolledValues ?? {}}
              ariaLabel={t('blocks.options.optionFormulaLabel')}
              placeholder={t('blocks.options.optionFormulaPlaceholder')}
              hideEval
              hasError={formulaHasError}
              angleMode={angleMode}
              onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
            />
          ) : opt.media ? (
            <>
              {/* WHAT GOES, highlighted — the pill, the alt text and the motion description, which
                  clearing the media takes with it. The label does NOT: it is the option's own
                  identity and survives the media being removed, so painting it would promise a
                  deletion that is not going to happen.
                  The same warning paint every other armed delete in the editor uses, on the thing
                  being deleted rather than only on the button: a red button says "this control is
                  destructive", and the paint says "this is what it will destroy".
                  `p-1.5` is PERMANENT, not applied with the paint: the outline is a hairline drawn on
                  this box's own edge, and with the fields flush against that edge it landed on top of
                  their borders — two 1px lines in different colours sharing a pixel row. The padding
                  holds it clear. Reserved rather than added when armed, because space that appears on
                  the first press shifts every field below it (see `ARMED_DELETE_BLEED`'s own note on
                  arm-hint layout shift). */}
              <div className={`flex flex-col gap-1 rounded p-1.5 ${mediaArmed ? ARMED_DELETE_PAINT : ''}`}>
              <div className='flex items-center gap-1.5'>
                <button
                  type='button'
                  onClick={() => setShowLibraryModal(true)}
                  aria-label={t('blocks.mcq.swapMedia')}
                  className='flex min-w-0 flex-1 items-center gap-2 rounded-md border border-ws-border bg-ws-btn px-2 py-1 text-left hover:bg-ws-hover'
                >
                  {opt.media.kind === 'image' ? (
                    cachedAsset?.urls.thumb ? (
                      <img src={cachedAsset.urls.thumb} alt='' className='h-7 w-7 shrink-0 rounded object-cover' />
                    ) : (
                      <ImageIcon className='h-5 w-5 shrink-0 text-ws-muted' aria-hidden='true' />
                    )
                  ) : (
                    <Volume2 className='h-5 w-5 shrink-0 text-ws-muted' aria-hidden='true' />
                  )}
                  <span className='min-w-0 flex-1 truncate text-xs text-ws-text'>
                    {cachedAsset?.filename ?? t('blocks.mcq.mediaOptionLoading')}
                  </span>
                </button>
                {/* NOT a trash can. Arming it was right — clearing an option's media is the same act
                    as clearing the Image block's, which has taken two presses since the overlay
                    controls went in — but a SECOND trash landed inches from the row's own, one
                    meaning "drop the picture, keep the option" and the other "drop the option".
                    Two identical glyphs for two different scopes is the X-against-X problem
                    `MediaOverlayControls` already records, in the other direction. The glyph now
                    names what goes: the picture, or the clip. The arming stays. */}
                <IconButton
                  type='button'
                  variant='destructive'
                  size='sm'
                  isArmed={mediaArmed}
                  aria-label={mediaArmed ? t('blocks.actions.confirmDelete') : t('blocks.mcq.removeMedia')}
                  onClick={() => {
                    if (!mediaArmed) { setMediaArmed(true); return; }
                    setMediaArmed(false);
                    onMediaChange(opt.id, undefined);
                    // This button unmounts once media clears (the row reverts to the plain-text
                    // editor) — land focus there instead of dropping it, same as handleMediaSelect
                    // does for the reverse (add-media) direction above.
                    requestAnimationFrame(() => { optionEditorsRef.current.get(opt.id)?.commands.focus(); });
                  }}
                  onPointerLeave={(e) => { if (e.pointerType !== 'touch' && mediaArmed) setMediaArmed(false); }}
                  onBlur={() => setMediaArmed(false)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && mediaArmed) { e.stopPropagation(); setMediaArmed(false); } }}
                >
                  {opt.media.kind === 'image'
                    ? <ImageOff className='h-3.5 w-3.5' />
                    : <VolumeX className='h-3.5 w-3.5' />}
                </IconButton>
              </div>
              {/* Alt-text only — image options need it (required, see the error check above);
                  audio deliberately has no transcript input here at all (not just optional): a
                  transcript specific enough to distinguish two "which sound is X" options is
                  specific enough to leak the answer, so there's no safe text to author. AT
                  distinguishability for audio options instead falls back to a positional label
                  (see MCQAnswerDisplay's mediaAccessibleName). Directly editable here (not only
                  pickable while inserting/swapping in the library modal) — mirrors Image block's
                  own always-visible alt-text field. */}
              {opt.media.kind === 'image' && (
                  <TextField
                    variant='prose'
                    value={opt.media.altText ?? ''}
                    onChange={v => onMediaChange(opt.id, { ...opt.media!, altText: v })}
                    ariaLabel={t('blocks.image.altText')}
                    placeholder={t('blocks.image.altText')}
                    onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                    className='text-xs' />
              )}
              {/* Only for an option whose image MOVES. Alt text describes the first frame, and for
                  an animation the content is in what changes -- the same WCAG 1.2.1 obligation the
                  Image block carries, asked for in the same words. Sits between the alt text and
                  the internal label: the two things a reader is given, then the one only the
                  teacher ever sees. */}
              {opt.media.kind === 'image' && cachedAsset?.animated && (
                  <TextField
                    variant='prose'
                    value={opt.media.motionDescription ?? ''}
                    onChange={v => onMediaChange(opt.id, { ...opt.media!, motionDescription: v })}
                    ariaLabel={t('blocks.image.motionDescription')}
                    placeholder={t('blocks.image.motionDescription')}
                    onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                    className='text-xs' />
              )}
              </div>
              {/* The (always-required) label — internal identifier for feedback/Flow/reports,
                  never shown to students once media is present. Same field as the plain-text
                  case (`opt.text`), just relabeled and given a distinct placeholder. Deliberately
                  a PLAIN input, not a RichTextEditor — same shape and reasoning as Keyword's own
                  option input: a variable chip inserted here (reachable via the shared toolbar's
                  "Insert Variable" button acting on whatever editor was last active) used to trip
                  this block's "options reference a variable" detection, silently offering/enabling
                  "Treat options as equations" for a media option that can never have a formula —
                  and once that got toggled on and the media was later removed, the option was
                  stuck rendering a formula box with no text field left to remove the variable from.
                  A plain input can't register as the active editor (onFocus clears it, exactly
                  like Keyword's own input) and can't hold chip markup at all, so this bug class is
                  now structurally impossible rather than something every future check must guard
                  against. `stripHtmlForLabel` is only for migrating any already-existing rich-text
                  content (incl. a stray bracketed chip) into plain display text — plain typing from
                  here on writes straight into `text`, no HTML. */}
                <TextField
                  variant='literal'
                  inputRef={labelInputRef}
                  value={labelDraft}
                  onChange={v => { setLabelDraft(v); onTextChange(opt.id, v); }}
                  onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                  ariaLabel={t('blocks.mcq.optionLabelPlaceholder')}
                  placeholder={t('blocks.mcq.optionLabelPlaceholder')}
                  className='w-full' />
            </>
          ) : (
            <div className='flex min-w-0 items-center gap-1.5'>
              <div className='min-w-0 flex-1'>
                <RichTextEditor fieldId={`${opt.id}.text`}
                  content={opt.text}
                  onChange={(html) => onTextChange(opt.id, html)}
                  placeholder={t('blocks.options.optionPlaceholder')}
                  editorClassName='min-h-6'
                  hideToolbar
                  onEditorReady={(e) => { optionEditorsRef.current.set(opt.id, e); }}
                  onEditorFocus={() => {
                    const e = optionEditorsRef.current.get(opt.id);
                    if (e) { activeEditorRef.current = e; setActiveEditor(e); }
                  }}
                />
              </div>
              <IconButton
                type='button'
                size='sm'
                aria-label={t('blocks.mcq.insertMedia')}
                onClick={() => setShowLibraryModal(true)}
              >
                <Images className='h-3.5 w-3.5' />
              </IconButton>
            </div>
          )}
        </div>

        {showLibraryModal && (
          <LibraryModal
            assetKind='any'
            experienceId={experienceId}
            currentAssetId={opt.media?.assetId ?? null}
            currentTaskId={taskId}
            currentTaskBlocks={taskBlocks}
            onSelect={handleMediaSelect}
            onClose={() => setShowLibraryModal(false)}
          />
        )}

        {showAnchor && (
          <button
            type='button'
            onClick={() => onToggleAnchor(opt.id)}
            aria-pressed={opt.anchored}
            aria-label={opt.anchored ? t('blocks.options.unpinOption') : t('blocks.options.pinOption')}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors ${
              opt.anchored ? 'bg-ws-primary/10 text-ws-primary' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            }`}
          >
            <Pin className='h-4 w-4' fill={opt.anchored ? 'currentColor' : 'none'} />
          </button>
        )}

        <IconButton
          type='button'
          variant='destructive'
          size='md'
          isArmed={armedOptionId === opt.id}
          disabled={optionCount <= 1}
          /*
             SAID TWICE, ON PURPOSE: in the armed label for a reader, and in the warning
             beside the button for everyone else. It used to be the label alone, which made
             it invisible — see `StrandWarning` for why that was chosen and why it was
             wrong. `title` stays for a pointer resting on the icon itself. */
          aria-label={armedOptionId === opt.id
            ? [t('blocks.options.confirmRemoveOption'), strandWarning].filter(Boolean).join('. ')
            : t('blocks.options.removeOption')}
          title={armedOptionId === opt.id ? strandWarning : undefined}
          className='shrink-0'
          onClick={() => {
            if (armedOptionId === opt.id) { onRemove(opt.id); setArmedOptionId(null); }
            else { setArmedOptionId(opt.id); }
          }}
          onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armedOptionId === opt.id && armSettled()) setArmedOptionId(null); }}
          onBlur={() => { if (armedOptionId === opt.id) setArmedOptionId(null); }}
          onKeyDown={(e) => { if (e.key === 'Escape' && armedOptionId === opt.id) { e.stopPropagation(); setArmedOptionId(null); } }}
        >
          <Trash2 className='h-4 w-4' />
        </IconButton>
      </div>

      {/*
        * BELOW THE ROW, not inside it. Beside the fields it squeezed the answer to make room
        * for a squeezed warning, which helps nobody — owner: "squishing the actual answer to
        * put a squished warning is not helpful". The wrapper carries the sortable ref and the
        * field id so the row keeps being draggable and reachable from the issue list; the
        * armed paint stays on the row, which is the thing being deleted.
        */}
      {armedOptionId === opt.id && strandWarning && <StrandWarning text={strandWarning} />}
    </div>
  );
}

// ─── Trigger UI ───────────────────────────────────────────────────────────────


function MCQTriggerChip({ option }: { option: TriggerOption }) {
  const chipScope = useChipScope();
  const chipColor =
    chipToneClass(option.evaluation ?? 'neutral');
  const isAbsent = option.value.endsWith('_absent');
  // Same rule as MCQTriggerDropdown's own selected-button display above: once an option is
  // chosen, show its real text or nothing — never the positional "Option N" label, which
  // belongs to the picker menu only. Bracketed + icon-prefixed for a media option's Label — it's
  // never what's shown to students, so it must never read like real option text either. Rendered
  // as plain text (stripHtmlForLabel), not dangerouslySetInnerHTML, since the Label is a plain
  // input (see SortableOptionRow) — this also cleanly migrates any leftover HTML from before
  // that fix, rather than risking it being interpreted as live markup.
  const label = option.preview && (
    option.mediaKind ? (
      <span className='flex min-w-0 items-center gap-1 truncate'>
        <MediaKindIcon kind={option.mediaKind} className='h-3 w-3 shrink-0' />
        <span className='truncate ws-formula-preview'>[{stripHtmlForLabel(option.preview)}]</span>
      </span>
    ) : (
      <span className='min-w-0 truncate ws-formula-preview' dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(renderChipsForDisplay(option.preview.replace(/<\/?p>/g, '').trim(), chipScope)) }} />
    )
  );
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      {option.evaluation === 'incorrect' && <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {option.evaluation === 'correct'   && <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {(option.evaluation === 'neutral' || !option.evaluation) && <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />}
      <ChipDivider />
      <SlashedLabel slashed={isAbsent} className={TRIGGER_CHIP_TEXT_CLASS}>{label}</SlashedLabel>
    </span>
  );
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// Module-level (not per-instance) — shared across every mounted MCQBlock, entries keyed by each
// instance's own `block.data` object identity, so instances never collide. See memoizedByKey's
// own doc comment for why this exists: mcqIssues' checkOptionChipCollisions call was found
// live (Firefox profiler) to re-run its full brute-force variable-domain sampling on every
// keystroke anywhere in the task, not just this block's own fields.
const mcqIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();
const mcqTestDataCache: IssueCache<MCQTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft.
 *
 * Preview's chips describe the SUBMITTED answer, because that is what the student has been told
 * about. Behind the Scenes wants the other question -- what would fire if they pressed Submit
 * now -- and the two part company the moment someone edits after submitting. Keyed on the draft,
 * so it costs one derivation per distinct answer tried rather than one per keystroke.
 */
const mcqDraftChipsCache: IssueCache<MCQTestData> = new WeakMap();

// ─── Main component ───────────────────────────────────────────────────────────

export default function MCQBlock({ block, experienceId, taskId, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange, blocks, onRename }: MCQBlockProps) {
  const { t, i18n } = useTranslation();
  /* The top rung, asked once for the whole editor. It gates only whether a mechanism may be
     ADOPTED; each toggle below keeps its own `|| <already on>` so one in use can be switched
     off. See `useVariableMechanismAllowed` for why the insert gate does not imply this. */
  const mayVarMechanism = useVariableMechanismAllowed();
  /*
   * MAY A RECALL LIVE IN THIS BLOCK — the destination half of the recall rung.
   *
   * The same question the toolbar's `[` button asks, asked again here because the equations
   * toggle is a MECHANISM and a mechanism gate is never implied by an insert gate: a chip
   * arrives by paste and by a rung lowered after authoring, and either would otherwise open a
   * control the capability does not grant.
   */
  const mayRecallHere = useRecallAffordance() === 'allowed';
  const scope = useAnswerRefScope();
  /* Which `[ara_…]` tokens may become live chips again when the toggle goes OFF — an id naming
     no alias is a reference to nothing, and must stay text. Every alias, not just the numeric
     ones: a token that should never have been in a formula is HEALED by turning the toggle off,
     and rebuilding the chip is the repair. */
  const knownAliasIds = useMemo(
    () => new Set((scope?.aliases ?? []).map(a => a.id)), [scope]);
  /*
   * AND WHICH OF THEM A FORMULA CAN ACTUALLY USE.
   *
   * A calculated option is arithmetic. `aliasKind` is the one derivation of "number or words",
   * the same one the recall panel's `#`/`A` letter reads, so the toggle and the field agree
   * about which recalls belong in an equation instead of each deciding for itself.
   */
  const numericAliasIds = useMemo(
    () => new Set((scope?.aliases ?? [])
      .filter(a => aliasKind(a, (scope?.tasks ?? []) as Parameters<typeof aliasKind>[1]) === 'number')
      .map(a => a.id)),
    [scope]);
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  // ONE parse per render for every render-path reader below. `initData` used to mint option ids,
  // which made calling it repeatedly a correctness bug and not just waste; it is pure now, and
  // this keeps it cheap. Handlers that need the persisted data still call it themselves — they
  // run outside render, where a stale memo would be the greater hazard.
  /*
   * TWO pointers: what the test surface is showing, and what the editor is writing.
   *
   * They used to be one, which meant stepping the test panel re-seeded every editor field from a
   * version the teacher had not asked to open. See `version-in-force.ts` for the full argument.
   * They meet in exactly one place, the test panel's Edit button, which copies test to editor on
   * purpose.
   */
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  const storedData = useMemo(() => initData(block.data), [block.data]);
  /**
   * The editor's opening state, THROUGH the version it is pointed at.
   *
   * Every `useState` below seeds from this ref, and it used to hold the raw block — which for a
   * banked block is the vestigial base copy that no version uses. So opening the editor on a bank
   * showed the base question under a heading that said "Version 1 of 3": the pager was right and
   * the fields beneath it were content nobody had authored. Editing from there then wrote those
   * fields into whichever version was selected, quietly overwriting it with the base text.
   *
   * `applyOptionsVersion` is identity for a block with no versions, so this is unchanged for every
   * block that has never had one, and `pickVersion` reads an absent id as "the first version" —
   * which is what the heading already claims on open.
   */
  const initial = useRef(applyOptionsVersion(storedData, editorVersion));
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read `push()` depends on; a ref cannot re-render, so restored HTML needs state to
   * reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedOptions, setOptions] = useState<MCQOption[]>(initial.current.options);
  const [editedAllowMultiple, setAllowMultiple] = useState(initial.current.allowMultiple);
  const [editedRandomizeOrder, setRandomizeOrder] = useState(initial.current.randomizeOrder);
  const [editedShape, setShape] = useState<NonNullable<MCQBlockData['shape']>>(initial.current.shape ?? 'vertical');
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedConditionalEquation, setConditionalEquation] = useState<string | undefined>(initial.current.conditionalEquation);
  const [editedConditionalBranches, setConditionalBranches] = useState<ConditionalRoleBranch[] | undefined>(initial.current.conditionalBranches);
  // Unchecking "Correctness depends on variables" clears the equation/branches from state — but a
  // teacher who rechecks it before closing the editor shouldn't lose what they'd typed. Holds the
  // last-known equation/branches across an off→on cycle within the same editing session; cleared
  // for real in handleDone, so it never survives an exit while the toggle is left off.
  /**
   * The branch set switched off but not yet discarded, so an accidental untick (or deleting the last
   * variable chip) doesn't destroy the work. Seeded from `conditionalBranchesDraft` on the saved
   * block, which is what lets it survive a reload as well as a toggle. Dropped on close, and that is
   * what makes the removal permanent (recovery is undo).
   */
  const savedConditionalDraftRef = useRef<MCQBlockData['conditionalBranchesDraft']>(initial.current.conditionalBranchesDraft);
  /**
   * Is the banked set OURS to offer back, or left over from a session long finished?
   *
   * `savedConditionalDraftRef` is seeded from the saved block so that closing clears it, which is
   * what garbage-collects one left behind. But offering it back is a different question. The
   * restore below already says "from the last time THIS SESSION turned the toggle off" — this is
   * what makes that true, where seeding alone had it resurrect a set banked weeks ago.
   *
   * Owner's reasoning, and it is the whole point: come back much later and a stored branching
   * correct you have long forgotten is confusing, not helpful. An untick is recoverable for as
   * long as you are still looking at the block; after that, recovery is undo.
   */
  const bankedThisSessionRef = useRef(false);
  /**
   * WHAT LINKING AND UNLINKING PUT ASIDE FOR EACH OTHER, for as long as the editor stays open.
   *
   * The two acts are destructive in opposite directions — linking clears the equation and its
   * branches (the block's equation cannot select among scenarios), unlinking drops the rows — and
   * without this each throws the other's work away for good. A teacher who unlinked to look at
   * something and relinked would have every scenario's answer back at its copied default.
   *
   * Same contract as `savedConditionalDraftRef`: recoverable while you are still looking at the
   * block, dropped on close, and after that recovery is undo.
   *
   * `leaderId` is carried with the rows because rows are keyed by the LEADER's variant ids: give
   * them back against a different leader and `syncScenarioBranches` would match nothing anyway,
   * so the check makes the intent explicit rather than relying on that.
   */
  const linkSwapRef = useRef<{
    scenario?: { leaderId: string; rows: ScenarioRoleBranch<MCQFeedback>[] | undefined };
    numeric?: { equation: string | undefined; branches: ConditionalRoleBranch[] | undefined };
  }>({});
  /**
   * One answer setup per scenario the leader can show — see `docs/linked-scenarios.md` §4.1.
   *
   * Local state like every other edited field, rather than written straight to stored data the
   * way `followsBlockId` is. The two are not the same kind of thing: linking is a single
   * structural act, while these rows carry roles and feedback that the ordinary editing surfaces
   * write to on every click and keystroke. Holding them here is what lets a scenario row reuse
   * `activeBranch`'s existing read/write path verbatim instead of growing a parallel one.
   */
  const [editedScenarioBranches, setScenarioBranches] = useState<ScenarioRoleBranch<MCQFeedback>[] | undefined>(initial.current.scenarioBranches);
  // "Treat options as equations" — see MCQBlockData.optionsAsEquations' doc comment for the full
  // contract (formula evaluated for display, correctness stays 100% manual, both select modes).
  const [editedOptionsAsEquations, setOptionsAsEquations] = useState(initial.current.optionsAsEquations ?? false);
  // Degrees/radians disambiguation for any trig call in either conditionalEquation or an option's
  // formula — one shared mode for the whole block, not per-formula. See MCQBlockData.angleMode.
  const [editedAngleMode, setAngleMode] = useState<AngleMode>(initial.current.angleMode ?? 'degrees');
  const [activeBranchIndex, setActiveBranchIndex] = useBranchJumpTarget(block.id);
  /**
   * The version being EDITED, which is not the version a student is shown.
   *
   * Display is chips, block test is this one, Preview is the session's draw — the same three-mode
   * rule variables already follow (`project_variable_resolution_three_modes`). So "pick a version
   * to test" is this selector and needs no control of its own.
   *
   * ONE pointer for both of this block's own surfaces — the editor and the test card are the
   * same component instance, so picking a version in the heading and testing it are the same act.
   * Preview is separate and reads the session draw instead.
   *
   * Undefined until a version is picked, which `pickVersion` reads as "the first one".
   */
  /**
   * Which version the TEST surface is on, derived once for every surface that reports or targets
   * it.
   *
   * The canvas card's notice, the phone Test tab's notice and the "edit this branch" jump all
   * need the same answer. The position arithmetic that used to sit beside this now lives in
   * `VersionBranchNotice`, which is the only thing that displayed it.
   */
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];

  // Stable ref so the hook's onUpdate callback always sees current values
  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml, options: editedOptions, allowMultiple: editedAllowMultiple,
    randomizeOrder: editedRandomizeOrder, shape: editedShape,
    gradingMode: editedGradingMode,
    conditionalEquation: editedConditionalEquation, conditionalBranches: editedConditionalBranches,
    optionsAsEquations: editedOptionsAsEquations, angleMode: editedAngleMode,
    scenarioBranches: editedScenarioBranches,
  };
  /**
   * Push one version's content into the editor's local state.
   *
   * Shared by `useRestoredData` (an undo, or another device's save) and by switching version,
   * which is the same act from the editor's point of view: the content underneath it changed and
   * every field has to be re-seeded from the new truth.
   */
  const seedEditorFrom = (next: MCQBlockData) => {
      setOptions(next.options);
      setAllowMultiple(next.allowMultiple);
      setRandomizeOrder(next.randomizeOrder);
      setShape(next.shape ?? 'vertical');
      setGradingMode(next.gradingMode);
      setConditionalEquation(next.conditionalEquation);
      setConditionalBranches(next.conditionalBranches);
      setScenarioBranches(next.scenarioBranches);
      setOptionsAsEquations(next.optionsAsEquations ?? false);
      setAngleMode(next.angleMode ?? 'degrees');
      savedConditionalDraftRef.current = next.conditionalBranchesDraft;
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  };

  const restored = useRestoredData<MCQBlockData>({
    raw: block.data,
    isEditing,
    // Through the version, so the editor shows the one it is pointed at rather than the block's
    // vestigial base copy. Identity for a block with no versions.
    derive: (raw) => applyOptionsVersion(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...applyOptionsVersion(initData(block.data), editorVersion), ...edited } as MCQBlockData,
    apply: seedEditorFrom,
  });

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(applyOptionsVersion(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    // Seed this instance synchronously and mark it done, so the effect above is left to the OTHER
    // instance rather than running twice here.
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(applyOptionsVersion(from, id));
  };

  /**
   * Open the editor on whatever the TEST surface is showing. The one bridge between the pointers.
   *
   * Without it the Edit button was a lie: it opened the editor on whichever version the editor
   * happened to be pointed at, which after any reroll is not the one the teacher is looking at.
   */
  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = (branchId?: string) => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump(branchId);
  };

  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const options = isEditing ? editedOptions : restored.data.options;
  /* Which option images move. Asked of the ASSETS, because a media option stores only an id --
     see `useAnimatedAssetIds` for why this is not denormalised onto the option. Read from the
     live options, so adding an animated one starts asking for its description straight away. */
  const animatedAssetIds = useAnimatedAssetIds(
    useMemo(() => options.flatMap(o => (o.media?.kind === 'image' ? [o.media.assetId] : [])), [options]),
  );
  const allowMultiple = isEditing ? editedAllowMultiple : restored.data.allowMultiple;

  /**
   * LEARN WHETHER EACH MEDIA option MOVES, once, and write it down.
   *
   * The motion-description check is an ERROR and reads `media.animated` so it can run without a
   * network — only the server knows whether a `.webp` animates, and a check the experience board
   * cannot run is a check the publication gate cannot read. A option picked from now on records
   * it; one authored before the field existed learns it here.
   *
   * `normalization: true` keeps it OFF THE UNDO STACK: it travels the same write path a teacher's
   * edit does, and without the flag merely opening a block leaves an undo step nobody made.
   * Written only when the stored answer actually differs, so a block that already knows emits
   * nothing at all.
   */
  useEffect(() => {
    const live = options;
    if (!live.some(o => o.media?.kind === 'image')) return;
    const healed = live.map(o => (o.media?.kind === 'image'
      && animatedAssetIds.has(o.media.assetId) !== (o.media.animated === true)
      ? { ...o, media: { ...o.media, animated: animatedAssetIds.has(o.media.assetId) } }
      : o));
    if (healed.every((o, i) => o === live[i])) return;
    onUpdate({ ...block.data, options: healed }, { normalization: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animatedAssetIds, block.data]);
  const randomizeOrder = isEditing ? editedRandomizeOrder : restored.data.randomizeOrder;
  const shape = isEditing ? editedShape : (restored.data.shape ?? 'vertical');
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const conditionalEquation = isEditing ? editedConditionalEquation : restored.data.conditionalEquation;
  const conditionalBranches = isEditing ? editedConditionalBranches : restored.data.conditionalBranches;
  const scenarioBranches = isEditing ? editedScenarioBranches : restored.data.scenarioBranches;
  const optionsAsEquations = isEditing ? editedOptionsAsEquations : (restored.data.optionsAsEquations ?? false);
  const angleMode = isEditing ? editedAngleMode : (restored.data.angleMode ?? 'degrees');

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /**
   * EVERY field write goes through here, because `onUpdate` REPLACES `block.data` wholesale.
   *
   * `handleUpdateBlock` does `blocks.map(b => b.id === id ? { ...b, data } : b)`, so a write that
   * builds its object field by field deletes everything it forgot to name. Two of them forgot
   * `versions`, and the one in the feedback model's `onUpdate` is reached by `seedEditorFrom` —
   * which is what switching version calls. So pressing Edit to open version 2 re-seeded the
   * editor, the feedback model normalised, and the resulting write destroyed the entire bank.
   * Undo recovered it, which is the only reason it looked like a glitch rather than data loss.
   *
   * Taking a PARTIAL and merging it onto the stored block is the whole point: a caller can only
   * state what it is changing, so there is nothing left to forget. `writeOptionsVersion` then
   * routes the version-owned fields into the version being edited, and is identity for a block
   * that has no versions.
   */
  const emitFields = (next: Partial<MCQBlockData>) => {
    const base = initData(block.data);
    emit(writeOptionsVersion(
      base, editorVersion, { ...base, ...next } as MCQBlockData,
    ) as unknown as Record<string, unknown>);
  };

  /**
   * Follow a leader, or stop following one.
   *
   * The rows are a PROJECTION of the leader's variants (§4.1), so this never asks the teacher to
   * build a list — it reads the leader's bank and writes one row per variant, each a copy of the
   * answer setup the block already had, marked unreviewed because a copy is not a decision.
   *
   * Linking also clears the NUMERIC branch setup. A scenario and an equation are two selectors
   * and the block's own cannot select among scenarios. Leaving it behind would mean a block whose
   * answer key depends on which of two systems you looked at.
   *
   * MULTI-SELECT needs no special case anywhere in here, which is worth stating because it is
   * the one way MCQ differs from Dropdown. A row carries a roles MAP, and a map has no
   * cardinality constraint — "three correct in this scenario" is written exactly as one is.
   */
  /**
   * A bank and a link cannot coexist (§2.2), so linking a banked block asks first — §7.1.
   *
   * Held as the pending leader id rather than a boolean: the answer arrives from the modal,
   * and by then the menu has closed.
   */
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  function linkToLeader(followsBlockId: string | undefined, remap?: 'remap' | 'empty') {
    const current = initData(block.data);
    // A bank is the one case that needs a decision before anything is written.
    if (followsBlockId && !remap && (current.versions?.length ?? 0) > 1) {
      setPendingLink(followsBlockId);
      return;
    }
    if (!followsBlockId) {
      // Put the rows aside, and hand back whatever linking took.
      if (current.followsBlockId) {
        linkSwapRef.current.scenario = { leaderId: current.followsBlockId, rows: current.scenarioBranches };
      }
      const restoredNumeric = linkSwapRef.current.numeric;
      linkSwapRef.current.numeric = undefined;
      setScenarioBranches(undefined);
      setConditionalEquation(restoredNumeric?.equation);
      setConditionalBranches(restoredNumeric?.branches);
      emit(versionStructure(block.data, {
        ...current,
        followsBlockId: undefined,
        scenarioBranches: undefined,
        conditionalEquation: restoredNumeric?.equation,
        conditionalBranches: restoredNumeric?.branches,
      }));
      return;
    }
    const leader = (blocks ?? []).find(b => b.id === followsBlockId);
    const leaderVersions = (leader?.data as { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    /*
     * COLLAPSE THE BANK TO VERSION 1 FIRST, whichever way the teacher answered.
     *
     * A block can hold one question, and once it follows a scenario there is no bank to pick
     * from, so SOMETHING has to become that question. Left to `initData`'s own copy it would be
     * the vestigial one `writeOptionsVersion` freezes when the first version is made — usually
     * version 1's text, but only by accident of how the bank was built. Saying "version 1" and
     * meaning it is what lets the modal state the consequence plainly, and it is what makes the
     * two answers differ in exactly one thing: the ANSWERS.
     */
    const base = (current.versions?.length ?? 0) > 1
      ? applyOptionsVersion(current, current.versions![0]!.id)
      : current;
    // Whatever this is about to clear, kept for an unlink.
    if (current.conditionalEquation !== undefined || current.conditionalBranches !== undefined) {
      linkSwapRef.current.numeric = {
        equation: current.conditionalEquation, branches: current.conditionalBranches,
      };
    }
    const banked = linkSwapRef.current.scenario;
    /*
     * REMAP: the block's own versions become its scenario rows, paired by position. Every row
     * comes back unreviewed, which is what makes a guess safe to offer.
     */
    const { branches } = remap === 'remap'
      ? remapVersionsToScenarios<RoleVersion<MCQFeedback>, ScenarioRoleBranch<MCQFeedback>>(
        leaderVersions, current.versions,
        v => ({ roles: v.roles, feedback: v.feedback }),
        () => ({ roles: Object.fromEntries(base.options.map(o => [o.id, o.role])), feedback: base.feedback }),
      )
      : syncScenarioBranches<ScenarioRoleBranch<MCQFeedback>>(
        leaderVersions,
        // The rows this block already has, or the ones an unlink put aside for this leader.
        current.scenarioBranches ?? (banked?.leaderId === followsBlockId ? banked.rows : undefined),
        /*
         * THE EDITOR'S `feedback`, NOT `current.feedback`. `current` is `initData(block.data)` —
         * the raw stored object — and a block whose feedback has never been written back still
         * holds `{ states: [] }` there, because the No Answer/Correct/Incorrect defaults are
         * produced by `useInputBlockFeedback`'s normalization on mount and only reach storage on
         * the next save. Link a brand-new MCQ before touching anything else and every scenario
         * would be born with no feedback at all, copied faithfully from an empty source.
         */
        () => ({
          roles: Object.fromEntries(base.options.map(o => [o.id, o.role])),
          feedback,
        }),
      );
    /*
     * A row with no feedback gets the block's, whatever produced it. The template above only
     * runs for rows being CREATED; a row banked by an unlink, or written by an earlier build,
     * reaches this line empty. Healing at the write makes relinking a repair.
     */
    const healed = branches.map(b => (b.feedback?.states?.length ? b : { ...b, feedback }));
    // Local state too, or the editor would render from `editedScenarioBranches` — still empty —
    // while the saved block already held the rows.
    setScenarioBranches(healed);
    setConditionalEquation(undefined);
    setConditionalBranches(undefined);
    emit(versionStructure(block.data, {
      // `base`, not `current`: version 1's question and roles become the block's, which is what
      // the modal promised and what makes the bank's collapse legible.
      ...base,
      feedback: remap === 'remap' ? (base.feedback ?? feedback) : feedback,
      followsBlockId,
      scenarioBranches: healed,
      // The bank cannot survive the link (§2.2) — remapped into the rows, or discarded on the
      // teacher's say-so. Either way the decision was made above, never here.
      versions: undefined,
      conditionalEquation: undefined,
      conditionalBranches: undefined,
    }));
  }

  /**
   * Every write to the scenario rows, state and storage together.
   *
   * Two writes that must not come apart: the editor renders from state and the card grades off
   * storage, so a handler that updated one would show a change that did not take.
   */
  function pushScenarioRows(rows: ScenarioRoleBranch<MCQFeedback>[] | undefined) {
    setScenarioBranches(rows);
    emitFields({ scenarioBranches: rows });
  }

  // In equation mode the equation is `option.formula`, not `option.text` — see `mcqOptionLabel`.
  const { settleLive, settleStored } = useIssueSettling(
    { equation: conditionalEquation, answers: optionsAsEquations ? options.map(o => o.formula ?? '').join('\u0000') : undefined },
    { equation: storedData.conditionalEquation, answers: storedData.optionsAsEquations ? storedData.options.map(o => o.formula ?? '').join('\u0000') : undefined },
  );

  const currentDataRef = useRef({ options, allowMultiple, randomizeOrder, gradingMode, conditionalEquation, conditionalBranches });
  currentDataRef.current = { options, allowMultiple, randomizeOrder, gradingMode, conditionalEquation, conditionalBranches };

  // Feedback is stale (suppressed, not deleted) once its target option's role no
  // longer matches the trigger's direction — e.g. it was since marked correct.
  // Hidden here rather than reassigned to a different option in the picker.

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  const { feedback, setFeedback, resyncFeedback, getTitle } = useInputBlockFeedback({
    initialRaw: normalizeMCQLegacy(initial.current.feedback),
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
    },
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      const { options, allowMultiple, randomizeOrder, gradingMode, conditionalEquation, conditionalBranches } = currentDataRef.current;
      emitFields({ question: questionHtmlRef.current, options, allowMultiple, randomizeOrder, gradingMode, feedback: nextFeedback, conditionalEquation, conditionalBranches });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;
  /**
   * The scenario row on screen, chosen by THE LEADER'S OWN TEST POINTER.
   *
   * Not a pointer of this block's. At runtime a whole group resolves from one key —
   * `@version:<leaderId>` (§3.1) — and the editor has no business using a different rule. Read
   * through `useVersionInForce` on the LEADER, which is the very pointer that leader's own card
   * is already paging, so three things that must agree become one that cannot disagree: the
   * leader's prose on the canvas, this block's live test, and the row being authored.
   *
   * Keyed on this block when it follows nothing, where it is simply this block's own test
   * pointer and goes unread. A hook cannot be called conditionally.
   */
  /**
   * The leader's variants, read LIVE from the leader block rather than copied here — §4.1's
   * projection rule. A stored copy would be the same fact in two places, and the copy would go
   * stale the first time a scenario is renamed.
   */
  /** The leader itself, not just its variants — the ghosted stem renders by its TYPE. */
  const leaderBlock = (blocks ?? []).find(b => b.id === storedData.followsBlockId);
  const leaderVersions = ((blocks ?? []).find(b => b.id === storedData.followsBlockId)?.data as
    { versions?: DisplayVersion[] } | undefined)?.versions;
  const [leaderShownRaw, setLeaderShown] = useVersionInForce(storedData.followsBlockId ?? block.id, 'test');
  const shownScenarioId = leaderShownRaw ?? leaderVersions?.[0]?.id;
  /**
   * WHAT THE TEST BAR'S PAGER PAGES. This block's own bank, or — when it follows one — the
   * leader's scenarios, on the shared pointer above. A follower has no bank of its own, so
   * without this the bar has no pager at all and there is no way to test a second scenario.
   */
  const scenarioTestPager = storedData.followsBlockId
    ? { versions: (leaderVersions ?? []) as { id: string }[], activeId: shownScenarioId,
      onSelect: setLeaderShown, noun: 'scenario' as const }
    : null;

  /**
   * Gated on gradingMode for exactly the reason `activeBranch` is: an answer key is a graded-mode
   * fact, and a block switched to Participation has nothing for these rows to say.
   */
  const activeScenario = gradingMode === 'graded' && scenarioBranches && scenarioBranches.length > 0
    ? (scenarioBranches.find(b => b.scenarioId === shownScenarioId) ?? scenarioBranches[0])
    : undefined;
  /** The leader variant the active row answers, so the pager can name it. */
  const activeScenarioVersion = (rowId: string | undefined) =>
    (rowId ? scenarioInForce(leaderVersions, rowId) : null) as { html?: string; name?: string } | null;
  /**
   * The leader's variant on the shared test pointer, for the TEST surface's ghosted copy of
   * the stem — deliberately not gated on graded mode. A participation block still asks about
   * the scenario, and a teacher testing one still needs to read it.
   */
  const shownScenarioVersion = storedData.followsBlockId
    ? scenarioInForce(leaderVersions, shownScenarioId) : null;

  /** The projection, kept live — a variant added to the leader becomes a row here (§4.1). */
  useScenarioRowProjection<ScenarioRoleBranch<MCQFeedback>>({
    followsBlockId: storedData.followsBlockId,
    blocks,
    rows: scenarioBranches,
    template: () => ({
      roles: Object.fromEntries(options.map(o => [o.id, o.role])),
      feedback,
    }),
    onSync: pushScenarioRows,
  });

  /** A bank and a link cannot coexist (§2.2), so a banked block asks before linking — §7.1. */
  const remapModal = pendingLink ? (
    <ScenarioRemapModal
      versionCount={storedData.versions?.length ?? 0}
      variantCount={((blocks ?? []).find(b => b.id === pendingLink)?.data as
        { versions?: unknown[] } | undefined)?.versions?.length ?? 0}
      leaderName={(blocks ?? []).find(b => b.id === pendingLink)?.name ?? ''}
      onCancel={() => setPendingLink(null)}
      onRemap={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'remap'); }}
      onLinkEmpty={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'empty'); }}
    />
  ) : null;

  /** ONE link control, rendered by whichever heading currently owns it — see the SCENARIO
   *  heading's note for why it moves. */
  const scenarioLinkMenu = (
    <ScenarioLinkMenu
      block={block}
      blocks={blocks}
      value={storedData.followsBlockId}
      onChange={linkToLeader}
      emit={emit}
      applyVersion={applyOptionsVersion}
    />
  );
  /**
   * ONE pager, rendered in two places — the answer-setup box and the feedback panel.
   *
   * `VersionSelector` in browse mode, not a second chevron pair: two pagers a few inches apart
   * must never disagree about which variant is on screen, and browse mode (no `onAdd`, no
   * `onRemove`) is also the projection rule (§4.1) in the UI — a follower's rows are the
   * leader's to add and remove.
   */
  const scenarioPager = activeScenario ? (
    <VersionSelector
      versions={leaderVersions ?? []}
      activeId={activeScenario.scenarioId}
      onSelect={setLeaderShown}
      noun='scenario'
    />
  ) : null;

  // ── Branch-scoped feedback indirection ──────────────────────────────────────
  // Every read/write below that deals with feedback CONTENT (No Answer/Correct/Incorrect/
  // Targeted) goes through these instead of `feedback`/`updateFeedbackState`/`deleteFeedbackState`
  // directly, so the same JSX-construction code works unchanged whether branching is on or off —
  // identical role to Dropdown's own `activeBranch`/`activeFeedbackStates` indirection. Gated on
  // gradingMode itself (not just conditionalBranches existing) for the same reason Dropdown's is:
  // branches are authored exclusively in graded mode, and switching away must make every
  // downstream reader fall back to the plain static/top-level shape.
  const activeBranch = gradingMode === 'graded' && conditionalBranches && conditionalBranches.length > 0
    ? conditionalBranches[Math.min(activeBranchIndex, conditionalBranches.length - 1)]
    : undefined;
  /**
   * WHICHEVER answer setup the panel is editing, stripped to the two things every editing
   * surface below actually touches: the roles and the feedback.
   *
   * This is the whole reason a scenario follower needs almost no new UI. A numeric branch and a
   * scenario row differ only in what SELECTS them — a comparison against a variable, or a
   * variant of the leader — and the editor downstream of this line never asks that question.
   * Writes still dispatch (the two live in different arrays and key on different fields), but
   * every read is one path.
   */
  const activeSetup: { roles: Record<string, MCQOption['role']>; feedback: MCQFeedback } | undefined
    = activeScenario ?? activeBranch;
  const activeFeedbackStates = activeSetup ? activeSetup.feedback.states : feedback.states;

  function setActiveFeedbackStates(nextStates: FeedbackState[]) {
    if (activeScenario) {
      // Writing a scenario's feedback IS reviewing it — the same act the dismiss X stands for.
      // A teacher who has just written this scenario's response has decided.
      pushScenarioRows(markScenarioReviewed(
        (scenarioBranches ?? []).map(b =>
          b.scenarioId === activeScenario.scenarioId ? { ...b, feedback: { states: nextStates } } : b),
        activeScenario.scenarioId,
      ));
    } else if (activeBranch) {
      const nextBranches = (conditionalBranches ?? []).map(b => b.id === activeBranch.id ? { ...b, feedback: { states: nextStates } } : b);
      setConditionalBranches(nextBranches);
      push(options, allowMultiple, randomizeOrder, gradingMode, feedback, nextBranches, conditionalEquation);
    } else {
      const next = { states: nextStates };
      setFeedback(next);
      push(options, allowMultiple, randomizeOrder, gradingMode, next, conditionalBranches, conditionalEquation);
    }
  }

  function updateActiveFeedbackState(id: string, patch: Partial<FeedbackState>) {
    setActiveFeedbackStates(activeFeedbackStates.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }

  function deleteActiveFeedbackState(id: string) {
    const state = activeFeedbackStates.find(s => s.id === id);
    if (!state || state.type === 'default') return;
    setActiveFeedbackStates(activeFeedbackStates.filter(s => s.id !== id));
  }

  // Role-per-option for whichever branch is active — same construction the option-list editor
  // already builds inline for its own display; centralized here so title-collision/staleness
  // checks against the active branch's own feedback can share it too.
  const effectiveOptionsForActive: MCQOption[] = rolesForMode(gradingMode, activeSetup
    ? options.map(o => ({ ...o, role: activeSetup.roles[o.id] ?? ('incorrect' as const) }))
    : options);

  function isTargetedStaleForActive(s: FeedbackState): boolean {
    /**
     * A blank only fires in OPTIONAL. Graded and survey catch it as `unanswered` BEFORE anything
     * targeted runs, so the card could never fire there — it sat in the panel looking authored
     * and resolving to nothing.
     *
     * Hidden rather than deleted, like every other stale targeted state: switching back to
     * optional brings it and its text back, which is the same promise targeted feedback makes
     * across every other mode change.
     */
    if (s.trigger.endsWith('_absent')) {
      const id = s.trigger.slice('option_'.length, -'_absent'.length);
      const opt = effectiveOptionsForActive.find(o => o.id === id);
      if (!opt) return true;
      /**
       * The block SHAPE first, and in every mode: a single pick cannot omit an option while
       * still being a pick. Graded only asked about the role, so switching multi-select off left
       * every absent card sitting there describing a condition nothing could produce.
       *
       * Stale, not deleted — switch multi-select back on and the cards come back with their
       * text, the same treatment the blank gets when it leaves optional.
       */
      if (!allowMultiple) return true;
      // Outside graded nothing is correct, so the role gate below would hide them all.
      if (gradingMode !== 'graded') return false;
      return opt.role !== 'correct';
    }
    if (s.trigger.startsWith('option_')) {
      const id = s.trigger.slice('option_'.length);
      return effectiveOptionsForActive.find(o => o.id === id)?.role === 'correct';
    }
    return false;
  }

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);

  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const [armedOptionId, setArmedOptionId] = useState<string | null>(null);
  const sharedToolbar = useSharedEditorToolbar();
  
  const { setActiveEditor, activeEditorRef } = sharedToolbar;
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const questionEditorRef = useRef<Editor | null>(null);
  const optionEditorsRef = useRef<Map<string, Editor>>(new Map());
  const gradedSnapshotRef = useRef<MCQOption[] | null>(null);
  const branchPrevButtonRef = useRef<HTMLButtonElement | null>(null);
  const addOptionButtonRef = useRef<HTMLButtonElement | null>(null);

  // Live test state for the collapsed canvas card — only meaningful while isSelected (this
  // block's own editor is open elsewhere). Identical contract to Dropdown's own test state:
  // testSelectedIds is the widget's own live value; submittedSelectedIds is what evaluation
  // actually runs against — the two only diverge in submit-gated mode (continuous === false).
  // Arrays throughout (not Dropdown's single id) since MCQ can be multi-select.
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);

  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>({});
  // Preview must show ONE task-level roll shared by every block (see TaskPreviewRollContext): a task
  // whose blocks all read {n} has to agree about n, and seeding Preview from this block's own roll made
  // them disagree. Block-level testing keeps its private roll plus reroll, which is the whole point of
  // it — a teacher steps through combinations for the one block they're testing.
  const taskPreviewRoll = useTaskPreviewRoll();
  // Memoised for its IDENTITY, not its cost: `?? {}` minted a fresh object every render, so
  // anything depending on `rolledValues` re-ran on every render in Preview. Harmless where the
  // consumer writes to a ref; wasteful everywhere else.
  /* RECALLED ANSWERS RIDE THE SAME MAP — see `useRolledWithRecalls`, which the value-family
     blocks have done all along. Without it an option formula holding `[ara_…]` evaluated against
     a scope that had never heard of it and came out blank. */
  const rolledValues = useRolledWithRecalls(useMemo(
    () => (isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues),
    [isPreview, taskPreviewRoll, ownRolledValues],
  ));
  /**
   * Seeded from the last attempt on a revisit -- see `usePriorAnswer`. Walking back into a task
   * does not wipe it, and this is the half that was missing: the counts and the roll already
   * survived, so a student sent to remediation and back kept their leniency but lost their work.
   */
  /*
   * TOLERANT OF BOTH SHAPES. The attempt used to hold a bare `string[]`, which disagreed with
   * `MCQAnswer` — the evaluator's own declared input has always been `{ selectedIds }`. It now
   * records the object, so an in-flight session that still holds the old array keeps working.
   */
  const priorRaw = usePriorAnswer<string[] | { selectedIds?: string[] }>();
  const prior = Array.isArray(priorRaw) ? priorRaw : priorRaw?.selectedIds;
  const [testSelectedIds, setTestSelectedIds] = useState<string[]>(prior ?? []);
  const [submittedSelectedIds, setSubmittedSelectedIds] = useState<string[]>(prior ?? []);
  const [hasSubmitted, setHasSubmitted] = useState(priorRaw !== undefined);
  /*
   * The SUBMITTED value, never the draft: recording the draft would make the attempt a snapshot
   * of the student's typing rather than of what they chose to hand in.
   *
   * The chosen options' TEXT travels beside their ids, resolved as the student saw it. Two
   * consumers, two fields: the id groups for export, the text is what a cross-task reference
   * quotes. Captured here rather than looked up later from the option id, because an option's
   * text can hold a variable chip — so two students saw different words, and recovering that
   * afterwards means re-deriving the seed and re-running the roll.
   */
  const reportedAnswer = useMemo(() => (hasSubmitted ? {
    selectedIds: submittedSelectedIds,
    selectedTexts: submittedSelectedIds.map(id => {
      const opt = options.find(o => o.id === id);
      return opt ? processVariableChipHTML(opt.text, rolledValues) : '';
    }),
  } : undefined), [hasSubmitted, submittedSelectedIds, options, rolledValues]);
  useReportAnswer(reportedAnswer);
  const [continuous, setContinuous] = useState(true);
  // Phone-only Edit/Test toggle inside the full-screen editor — identical contract to Dropdown's
  // own `phoneTestMode`, owned here so the "jump to editing this branch" action can flip straight
  // back to 'edit' mode itself, same instance, no cross-mount signal needed.
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  // Student view shuffles option order (per shuffleWithAnchors) when randomizeOrder is on — the
  // live-test widget needs to reflect that too. Shuffled ONCE per test session (entering
  // isSelected), not recomputed every render — identical contract to Dropdown's own
  // `testOptionOrder`, including reading `block.data` fresh (via initData) rather than the local
  // `options`/`randomizeOrder` state, since the canvas card and the open editor are two separate
  // mounted instances of this component.
  const [testOptionOrder, setTestOptionOrder] = useState<string[] | null>(null);
  useEffect(() => {
    // Also seeded for `isPreview` — the student-facing Preview render needs a real rolled value
    // and a stable shuffle snapshot exactly like the isSelected test widget does.
    if (isSelected) {
      setRolledValues(rollVariableValues(variables));
      const current = initData(block.data);
      setTestOptionOrder(current.randomizeOrder ? shuffleWithAnchors(current.options).map(o => o.id) : null);
    // NOT in Preview. This branch exists for the EDITOR, where deselecting a block clears the
      // test widget it was driving. Preview has no selection, so `isSelected` is false forever and
      // this ran on mount, wiping the answer a revisit had just restored. Preview's own "start
      // again" is the remount `previewResetKey` forces, not this.
      } else if (!isPreview) {
      setTestSelectedIds([]);
      setSubmittedSelectedIds([]);
      setHasSubmitted(false);
      setContinuous(true);
      setTestOptionOrder(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isPreview]);
  // Phone's own entry/exit into the Test tab — mirrors the isSelected effect above exactly
  // (isSelected is always false on phone, since block selection there is tracked via
  // mobileEditingId, not selectedBlockId).
  useEffect(() => {
    if (phoneTestMode === 'test') {
      setRolledValues(rollVariableValues(variables));
      const current = initData(block.data);
      setTestOptionOrder(current.randomizeOrder ? shuffleWithAnchors(current.options).map(o => o.id) : null);
    // NOT in Preview, for the same reason as the `isSelected` effect above: this clears the
    // test widget when the PHONE editor leaves its Test tab, and on a desktop Preview
    // `phoneTestMode` is 'edit' forever, so it ran on mount and wiped the restored answer.
    } else if (!isPreview) {
      setTestSelectedIds([]);
      setSubmittedSelectedIds([]);
      setHasSubmitted(false);
      setContinuous(true);
      setTestOptionOrder(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneTestMode]);
  // Preview's single bottom Submit (see task-content.tsx's own doc comment on the signal) —
  // reacts to the signal CHANGING, not a per-block Submit click, since Preview has no per-block
  // Submit button any more. Reuses the exact same submittedSelectedIds/hasSubmitted state (and
  // therefore the exact same feedback-firing/resolvedD machinery) the live test widget already
  // uses — Preview is just a different trigger for the same evaluation, not a separate one.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) {
      setSubmittedSelectedIds(testSelectedIds);
      setHasSubmitted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  function handleTestChange(ids: string[]) {
    setTestSelectedIds(ids);
    if (continuous) { setSubmittedSelectedIds(ids); setHasSubmitted(true); }
  }
  function handleTestSubmit() {
    setSubmittedSelectedIds(testSelectedIds);
    setHasSubmitted(true);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) { setSubmittedSelectedIds(testSelectedIds); setHasSubmitted(true); }
  }
  function rerollTestOptionOrder() {
    const current = initData(block.data);
    setTestOptionOrder(current.randomizeOrder ? shuffleWithAnchors(current.options).map(o => o.id) : null);
  }
  function handleTestReset() {
    setTestSelectedIds([]);
    setSubmittedSelectedIds([]);
    setHasSubmitted(false);
    /* NOT re-randomised. The testing block is a TEACHER'S INSTRUMENT: Reset means "same question,
       let me try a different answer", so it must change nothing about the question — including the
       option order. Re-shuffling here made a test unrepeatable, because a different outcome could
       come from the new answer or from the new layout and nothing said which. "A fresh student
       gets a fresh order" is true, and it belongs to Preview, where a reset really is a new
       attempt. Reroll is how a teacher asks for a new order on purpose. */
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleTestReroll() {
    setRolledValues(rollVariableValues(variables));
    rerollTestOptionOrder();
  }

  function push(opts = options, multi = allowMultiple, rand = randomizeOrder, mode = gradingMode, fdbk = feedback, cb = conditionalBranches, eq = conditionalEquation, asEq = optionsAsEquations, angMode = angleMode) {
    // The retained draft rides along on every ordinary save, so editing anything else cannot
    // drop it. Deliberately not a parameter: a defaulted parameter cannot express "clear this".
    const next = { question: questionHtmlRef.current, options: opts, allowMultiple: multi, randomizeOrder: rand, shape, gradingMode: mode, feedback: fdbk, conditionalEquation: eq, conditionalBranches: cb, conditionalBranchesDraft: savedConditionalDraftRef.current, optionsAsEquations: asEq, angleMode: angMode };
    /*
     * Split the edit back into the version it belongs to.
     *
     * The editor renders from version-resolved data, so `next` is a whole block with the version
     * already merged in. `writeOptionsVersion` sends the question, feedback, roles and branches
     * to the version being edited and leaves the answer SPACE — the options' identity and text,
     * the count, the grading mode — on the block, where it is shared. Identity for a block with
     * no versions, so this is unchanged for every block that has never had one.
     */
    emitFields(next);
  }

  /**
   * Persists branching as CLEARED. Needed because `push` cannot express it: a JS default parameter
   * fires on `undefined`, so `push(..., undefined, undefined)` does not clear the branches, it
   * restores `cb = conditionalBranches` — the very values being removed. Toggling branching off
   * therefore looked like it worked (the UI reads local state, already set to undefined) while the
   * saved block kept its branches: the canvas card, which grades off the saved block, went on
   * resolving a branch the editor said was off, and reopening the block showed the toggle back on.
   *
   * The authored set is still recoverable for as long as this editor stays open — the draft is held
   * in `savedConditionalDraftRef` and re-ticking the box restores it. Closing drops the draft, and
   * from there recovery is undo.
   */
  /**
   * Every write that changes the branch state itself, stated explicitly. It cannot go through `push`:
   * a JS default parameter fires on `undefined`, so `push(..., undefined, undefined)` restores
   * `cb = conditionalBranches` rather than clearing it — the trap that made "turn branching off"
   * silently never stick, leaving the canvas card grading against branches the editor said were off.
   */
  function pushBranchState(
    branches: ConditionalRoleBranch[] | undefined,
    equation: string | undefined,
    draft: MCQBlockData['conditionalBranchesDraft'],
  ) {
    emitFields({ question: questionHtmlRef.current, options, allowMultiple, randomizeOrder, shape, gradingMode, feedback, conditionalEquation: equation, conditionalBranches: branches, conditionalBranchesDraft: draft, optionsAsEquations, angleMode });
  }

  function handleGradingMode(mode: MCQBlockData['gradingMode']) {
    let nextOptions = options;
    if (gradingMode === 'graded' && mode !== 'graded') {
      // Leaving graded — remember the correct/incorrect setup and lock everything to neutral.
      gradedSnapshotRef.current = options;
      nextOptions = options.map(o => ({ ...o, role: 'neutral' as const }));
      setOptions(nextOptions);
    } else if (gradingMode !== 'graded' && mode === 'graded') {
      // Returning to graded — restore the prior setup instead of losing it.
      const snapshot = gradedSnapshotRef.current;
      const roleById = snapshot ? new Map(snapshot.map(o => [o.id, o.role])) : null;
      nextOptions = options.map((o, i) => ({
        ...o,
        role: (roleById ? (roleById.get(o.id) ?? 'incorrect') : (i === 0 ? 'correct' : 'incorrect')) as MCQOption['role'],
      }));
      setOptions(nextOptions);
    }

    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(mode);
    push(nextOptions, allowMultiple, randomizeOrder, mode, { states: nextFeedbackStates });
  }

  /** Applies `transform` to the top-level feedback AND every branch's own feedback — needed
   * whenever an option's identity changes (text/formula edit, reorder, delete) since a blank
   * option's fallback label is position-based and any option's title embeds its current text.
   * Identical role to Dropdown's own `updateAllFeedbackSets`. */
  function updateAllMcqFeedbackSets(
    transform: (states: FeedbackState[]) => FeedbackState[],
  ): { feedback: MCQFeedback; branches: ConditionalRoleBranch[] | undefined } {
    return {
      feedback: { states: transform(feedback.states) },
      branches: conditionalBranches?.map(b => ({ ...b, feedback: { states: transform(b.feedback.states) } })),
    };
  }

  function updateOptionText(id: string, text: string) {
    const next = options.map((o) => (o.id === id ? { ...o, text } : o));
    setOptions(next);
    // Keep any feedback targeting this option's title in sync with its new text — otherwise
    // renaming "Red" to "Crimson" would leave a stale "Selected Red" title behind. Every branch's
    // own feedback needs this too, not just whichever one is active in the scroller.
    const { feedback: nextFeedback, branches: nextBranches } = updateAllMcqFeedbackSets(
      states => resyncMcqTargetedTitles(next, states, t, optionsAsEquations)
    );
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, allowMultiple, randomizeOrder, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  /** Mirrors updateOptionText exactly, for the `formula` field instead of `text` — `text` is
   * deliberately left untouched here, see the toggle contract on MCQBlockData.optionsAsEquations
   * for why both fields persist independently. */
  function updateOptionFormula(id: string, formula: string) {
    const next = options.map((o) => (o.id === id ? { ...o, formula } : o));
    setOptions(next);
    const { feedback: nextFeedback, branches: nextBranches } = updateAllMcqFeedbackSets(
      states => resyncMcqTargetedTitles(next, states, t, optionsAsEquations)
    );
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, allowMultiple, randomizeOrder, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  /** Sets/clears an option's media — mutually exclusive with `formula` (an option is never both
   * an equation and a media option), so attaching media clears any pre-existing formula. `text`
   * is untouched either way — it's always the option's label regardless of what's displayed, so
   * no title resync is needed here the way updateOptionText/updateOptionFormula need one. */
  /**
   * `seedLabel` is applied in the SAME update as the media, not by a following onTextChange call.
   * Both updaters map over the `options` captured in this render, so two calls in one tick both
   * rebuild from the pre-media snapshot and the second silently drops `media` — the image and its
   * alt-text box vanished the instant a label was seeded. One update, one setOptions.
   */
  function updateOptionMedia(id: string, media: MCQOption['media'], seedLabel?: string) {
    const next = options.map((o) => (o.id === id
      ? { ...o, media, formula: media ? undefined : o.formula, ...(seedLabel !== undefined ? { text: seedLabel } : {}) }
      : o));
    setOptions(next);
    push(next, allowMultiple, randomizeOrder, gradingMode, feedback, conditionalBranches);
  }

  function addOption() {
    const newOpt = gradingMode === 'graded' && !allowMultiple ? { ...makeOption(), role: 'incorrect' as const } : makeOption();
    const next = [...options, newOpt];
    // A newly added option is otherwise invisible to every existing branch's own `roles` map
    // (unmapped ids just pass through unchanged in resolveEffectiveOptions) — backfilled here as
    // explicit 'neutral' so "was this option ever reviewed for this branch" is always auditable
    // rather than ambiguous, mirroring removeOption's own reconciliation below on the opposite
    // (deletion) side.
    const nextBranches = conditionalBranches?.map(b => ({ ...b, roles: { ...b.roles, [newOpt.id]: 'neutral' as const } }));
    setOptions(next);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, allowMultiple, randomizeOrder, gradingMode, feedback, nextBranches ?? conditionalBranches);
  }

  function removeOption(id: string) {
    if (options.length <= 1) return;
    optionEditorsRef.current.delete(id);
    const removedRole = options.find((o) => o.id === id)?.role;
    let next = options.filter((o) => o.id !== id);
    if (removedRole === 'correct') {
      next = allowMultiple
        ? next.map((o) => ({ ...o, role: 'neutral' as const }))
        : next.map((o, i) => ({ ...o, role: (i === 0 ? 'correct' : 'incorrect') as MCQOption['role'] }));
    }
    // Deleting an option also deletes any feedback tied to it (both option_X and
    // option_X_absent) — recoverable only through undo, not silently left behind as an
    // orphaned, mislabeled card. Remaining feedback titles are resynced too. Applied across
    // every branch's own feedback, not just whichever is active — an option disappearing
    // affects all of them.
    const { feedback: nextFeedback, branches: nextBranchesFromFeedback } = updateAllMcqFeedbackSets(
      states => resyncMcqTargetedTitles(next, states.filter(s => !(s.type === 'targeted' && (s.trigger === `option_${id}` || s.trigger === `option_${id}_absent`))), t, optionsAsEquations)
    );
    // A branch's OWN `roles` map isn't touched by the reassignment above (that's only the static
    // `options` array) — left alone, a branch whose only correct pick was just deleted would
    // silently end up with no correct option at all. Mirrors the SAME per-mode distinction the
    // static (no-branch) case above just made: multi-select drops to all-neutral (no
    // auto-promotion — which remaining option would even be the "right" substitute is genuinely
    // ambiguous for a set), single-select promotes the first remaining option, matching Dropdown's
    // own single-select-only promotion. Either way the now-meaningless stale entry for the
    // removed option id itself is dropped.
    const nextBranches = nextBranchesFromFeedback?.map(b => {
      const roles: Record<string, MCQOption['role']> = Object.fromEntries(Object.entries(b.roles).filter(([optId]) => optId !== id));
      if (b.roles[id] === 'correct' && next.length > 0) {
        if (allowMultiple) { for (const optId of Object.keys(roles)) roles[optId] = 'neutral'; }
        else roles[next[0]!.id] = 'correct';
      }
      return { ...b, roles };
    });
    setOptions(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, allowMultiple, randomizeOrder, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
    // The just-removed row's own remove button (whatever had focus) unmounts with it, dropping
    // keyboard focus to <body> — refocus the one control guaranteed to still exist afterward.
    addOptionButtonRef.current?.focus();
  }

  function handleRoleClick(id: string) {
    if (gradingMode !== 'graded') return;
    let next: MCQOption[];
    if (allowMultiple) {
      const CYCLE: MCQOption['role'][] = ['neutral', 'correct', 'incorrect'];
      next = options.map((o) => {
        if (o.id !== id) return o;
        const idx = CYCLE.indexOf(o.role);
        return { ...o, role: CYCLE[(idx + 1) % CYCLE.length]! };
      });
    } else {
      if (options.find(o => o.id === id)?.role === 'correct') return;
      next = options.map(o => ({ ...o, role: (o.id === id ? 'correct' : 'incorrect') as MCQOption['role'] }));
    }
    setOptions(next);
    // Every targeted feedback's evaluation mirrors its target option's role — keep them in
    // sync whenever a role changes, rather than letting the two silently drift apart. Only the
    // top-level (static) feedback is affected here — a branch's own roles are completely
    // independent of the static ones, resynced separately by handleBranchRoleClick.
    const nextFeedback = { states: resyncMcqTargetedEvaluations(next, feedback.states) };
    setFeedback(nextFeedback);
    push(next, allowMultiple, randomizeOrder, gradingMode, nextFeedback);
  }

  function handleAllowMultiple(val: boolean) {
    let next = options;
    let nextFeedback = feedback;
    let nextBranches = conditionalBranches;
    if (!val && gradingMode === 'graded') {
      const firstCorrect = options.find(o => o.role === 'correct') ?? options[0]!;
      next = options.map(o => ({ ...o, role: (o.id === firstCorrect.id ? 'correct' : 'incorrect') as MCQOption['role'] }));
      setOptions(next);
      nextFeedback = { states: resyncMcqTargetedEvaluations(next, feedback.states) };
      setFeedback(nextFeedback);
      // Every branch's own role map collapses to single-select the same way the static list
      // just did above — each branch keeps whichever option IT already had marked correct
      // (falling back to the first option if it had none), not necessarily the same winner the
      // static list picked, since branches are reviewed independently. Previously left untouched,
      // which meant switching to single-select while a branch had several options marked correct
      // immediately surfaced `mcqBranchRoleIssue`'s 'multiple-correct' error with no way to reach
      // a valid setup except editing every branch by hand — the same silent-guess-vs-loud-error
      // tradeoff the static list already resolves in favor of a working default, so branches now
      // get the identical treatment instead of a special-cased exception.
      if (conditionalBranches) {
        nextBranches = conditionalBranches.map(b => {
          const branchFirstCorrect = next.find(o => b.roles[o.id] === 'correct') ?? next[0]!;
          const nextRoles = Object.fromEntries(
            next.map(o => [o.id, (o.id === branchFirstCorrect.id ? 'correct' : 'incorrect') as MCQOption['role']])
          );
          const effectiveOptionsForBranch = next.map(o => ({ ...o, role: nextRoles[o.id]! }));
          return { ...b, roles: nextRoles, feedback: { states: resyncMcqTargetedEvaluations(effectiveOptionsForBranch, b.feedback.states) } };
        });
        setConditionalBranches(nextBranches);
      }
    }
    setAllowMultiple(val);
    push(next, val, randomizeOrder, gradingMode, nextFeedback, nextBranches);
  }

  function handleRandomize(val: boolean) {
    setRandomizeOrder(val);
    push(options, allowMultiple, val);
  }

  function handleOptionDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const from = options.findIndex((o) => o.id === active.id);
    const to = options.findIndex((o) => o.id === over.id);
    if (from === -1 || to === -1) return;
    const next = arrayMove(options, from, to);
    setOptions(next);
    const { feedback: nextFeedback, branches: nextBranches } = updateAllMcqFeedbackSets(
      states => resyncMcqTargetedTitles(next, states, t, optionsAsEquations)
    );
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, allowMultiple, randomizeOrder, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  function toggleAnchor(id: string) {
    const next = options.map((o) => (o.id === id ? { ...o, anchored: !o.anchored } : o));
    setOptions(next);
    push(next);
  }

  /** Full copy, fresh ids, no content changes — used only for the very first branch, which isn't
   * a fork of anything: it's the same feedback that already existed, simply moving to live under
   * a branch instead of at the top level. Identical role to Dropdown's own
   * `cloneFeedbackWithFreshIds`. */
  function cloneFeedbackWithFreshIds(source: MCQFeedback): MCQFeedback {
    return { states: source.states.map(s => ({ ...s, id: crypto.randomUUID() })) };
  }

  /** Structure carries over, content resets to blank if customized or carries forward unchanged
   * if it still matches that slot's default — identical role to Dropdown's own
   * `seedBranchFeedback`, used for every branch after the first. */
  function seedBranchFeedback(source: MCQFeedback): MCQFeedback {
    return {
      states: source.states.map(s => {
        const defaultContent = s.type !== 'default' ? '' :
          s.trigger === 'unanswered' ? `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>` :
          s.trigger === 'incorrect' ? `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>` :
          // Branching only exists in graded mode, so the positive slot inside a branch is always
          // the 'correct' trigger, never any_answer/any_response.
          positiveSlotContent.correct;
        return { ...s, id: crypto.randomUUID(), content: resetContentIfCustomized(s.content, defaultContent) };
      }),
    };
  }

  function makeConditionalBranch(seedFrom: MCQFeedback, mode: 'migrate' | 'fork'): ConditionalRoleBranch {
    return {
      id: crypto.randomUUID(),
      operator: '==',
      threshold: '0',
      roles: Object.fromEntries(options.map(o => [o.id, o.role])),
      feedback: mode === 'migrate' ? cloneFeedbackWithFreshIds(seedFrom) : seedBranchFeedback(seedFrom),
    };
  }

  function toggleConditionalCorrectness(enabled: boolean) {
    if (enabled) {
      // Restore the draft from the last time this session turned the toggle off, if any —
      // otherwise start fresh, same as before.
      const draft = bankedThisSessionRef.current ? savedConditionalDraftRef.current : undefined;
      const equation = draft?.equation ?? '';
      const branches = draft?.branches ?? [makeConditionalBranch(feedback, 'migrate')];
      setConditionalEquation(equation);
      setConditionalBranches(branches);
      setActiveBranchIndex(0);
      savedConditionalDraftRef.current = undefined;
      push(options, allowMultiple, randomizeOrder, gradingMode, feedback, branches, equation);
    } else {
      // Banked under its own key and out of `conditionalBranches` at once. Recovery from an
      // accidental untick needs the set to survive, but the canvas card grades off the saved block,
      // so leaving it in place would have the card resolving a branch the editor says is off. A
      // separate key gives both, and unlike a memory-only draft it survives a reload.
      const draft = conditionalBranches !== undefined
        ? { equation: conditionalEquation ?? '', branches: conditionalBranches }
        : savedConditionalDraftRef.current;
      savedConditionalDraftRef.current = draft;
      bankedThisSessionRef.current = draft !== undefined;
      setConditionalEquation(undefined);
      setConditionalBranches(undefined);
      pushBranchState(undefined, undefined, draft);
    }
  }

  // A variable's presence in the QUESTION is what makes branch-based correctness meaningful —
  // identical safety net to Dropdown's own equivalent effect: clears orphaned branches the moment
  // the last variable chip is removed, rather than leaving them silently consulted by evaluateMCQ
  // with no way for the teacher to see or edit them. Standing rule (2026-08-02, applied uniformly
  // across MCQ/Dropdown/Ordering): "Correctness depends on variables" is a fact about the
  // QUESTION's premise/scenario changing — branching's role map is always a hand-authored
  // declaration keyed on the shared equation's value, never something derived from evaluating an
  // option, so a variable merely appearing in an option carries no signal that the scenario has
  // conditions under which correctness changes. Question-only, deliberately NOT
  // hasVariableInOptions (that gates "Treat options as equations" instead, a few lines below) —
  // the two toggles are a one-to-one correlation with where the variable actually lives, not an OR
  // across both locations.
  useEffect(() => {
    if (!isEditing) return;
    const hasVariableNow = htmlHasVariableChips(questionHtmlRef.current);
    if (!hasVariableNow && conditionalBranches !== undefined) toggleConditionalCorrectness(false);
    // Deliberately NO equivalent auto-clear for "Treat options as equations" — unlike branching
    // (whose precondition, the question, is a different field than the branch UI it gates, so
    // losing it while your cursor is elsewhere is harmless), losing the last option variable
    // WHILE actively re-authoring a formula (e.g. clearing one option's formula to retype it) used
    // to instantly flip every option back to rich text out from under the teacher's cursor — far
    // more disruptive than a stale/unreachable toggle. Once on, this toggle now only ever turns
    // off via the explicit checkbox click below (see its own `hasVariableInOptions || `
    // OR-with-current-state render condition, which is what keeps that checkbox reachable even
    // after every option variable is gone, instead of the old auto-clear's job of preventing an
    // unreachable stuck-on toggle).
  });

  function updateConditionalEquation(val: string) {
    setConditionalEquation(val);
    push(options, allowMultiple, randomizeOrder, gradingMode, feedback, conditionalBranches, val);
  }

  function addConditionalBranch() {
    const branches = conditionalBranches ?? [];
    const seedFrom = branches.length > 0 ? branches[branches.length - 1]!.feedback : feedback;
    const next = [...branches, makeConditionalBranch(seedFrom, 'fork')];
    setConditionalBranches(next);
    setActiveBranchIndex(next.length - 1);
    push(options, allowMultiple, randomizeOrder, gradingMode, feedback, next);
  }

  function removeConditionalBranch(id: string) {
    const next = (conditionalBranches ?? []).filter(b => b.id !== id);
    setConditionalBranches(next);
    setActiveBranchIndex(i => Math.max(0, Math.min(i, next.length - 1)));
    push(options, allowMultiple, randomizeOrder, gradingMode, feedback, next);
    branchPrevButtonRef.current?.focus();
  }

  function updateConditionalBranch(id: string, patch: Partial<Pick<ConditionalRoleBranch, 'operator' | 'threshold' | 'thresholdHigh'>>) {
    const next = (conditionalBranches ?? []).map(b => b.id === id ? { ...b, ...patch } : b);
    setConditionalBranches(next);
    push(options, allowMultiple, randomizeOrder, gradingMode, feedback, next);
  }

  /** Applies MCQ's own established `allowMultiple` fork (the exact same cycle `handleRoleClick`
   * already uses for the static case) to the branch-scoped role click, instead of Dropdown's
   * radio-only version — single-select still fully overwrites the branch's role map on every
   * click (exactly one correct); multi-select only ever touches the clicked option's own entry,
   * cycling neutral→correct→incorrect, since a branch's role map may legitimately need several
   * options marked correct at once. */
  /**
   * WHAT A ROLE CLICK MEANS, for a numeric branch and a scenario row alike.
   *
   * Shared because the two handlers below would otherwise hold two copies of the one rule that
   * is genuinely MCQ's own: single-select replaces the whole map (exactly one winner, everything
   * else incorrect) while multi-select cycles the clicked option through three roles and leaves
   * its neighbours alone. Two copies is two chances for a scenario to disagree with a branch
   * about what clicking the same button does.
   *
   * `null` means the click is a no-op — single-select cannot unset its one correct option by
   * clicking it again, because that would leave the question unanswerable.
   */
  function rolesAfterRoleClick(
    roles: Record<string, MCQOption['role']>, optionId: string,
  ): Record<string, MCQOption['role']> | null {
    if (allowMultiple) {
      const CYCLE: MCQOption['role'][] = ['neutral', 'correct', 'incorrect'];
      const cur = roles[optionId] ?? 'neutral';
      return { ...roles, [optionId]: CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length]! };
    }
    if (roles[optionId] === 'correct') return null;
    return Object.fromEntries(options.map(o => [o.id, (o.id === optionId ? 'correct' : 'incorrect') as MCQOption['role']]));
  }

  /** Every targeted feedback's evaluation mirrors its target option's role WITHIN this setup —
   *  same idea as `handleRoleClick`'s resync for the static case, scoped to one row's own
   *  targeted feedback and its own role snapshot. */
  function resyncedForRoles(roles: Record<string, MCQOption['role']>, states: FeedbackState[]): FeedbackState[] {
    return resyncMcqTargetedEvaluations(
      options.map(o => ({ ...o, role: roles[o.id] ?? ('incorrect' as const) })), states);
  }

  /** Display only — it changes nothing about what is correct, so no feedback resync. */
  function handleShape(next: NonNullable<MCQBlockData['shape']>) {
    setShape(next);
    // Not a `push` parameter: `push`'s defaults read the state this has only just set, which
    // React has not applied yet. `emitFields` merges, so naming the one field is safe here.
    emitFields({ shape: next });
  }

  function handleBranchRoleClick(branchId: string, optionId: string) {
    const next = (conditionalBranches ?? []).map(b => {
      if (b.id !== branchId) return b;
      const nextRoles = rolesAfterRoleClick(b.roles, optionId);
      if (!nextRoles) return b;
      return { ...b, roles: nextRoles, feedback: { states: resyncedForRoles(nextRoles, b.feedback.states) } };
    });
    setConditionalBranches(next);
    push(options, allowMultiple, randomizeOrder, gradingMode, feedback, next);
  }

  /**
   * Set this scenario's answer key — `handleBranchRoleClick` keyed by variant instead of by id.
   *
   * Deciding a row's roles IS reviewing it, so the unreviewed mark goes: §4.4's flag means
   * "copied, not decided", and this is the decision.
   */
  function handleScenarioRoleClick(scenarioId: string, optionId: string) {
    const next = (scenarioBranches ?? []).map(b => {
      if (b.scenarioId !== scenarioId) return b;
      const nextRoles = rolesAfterRoleClick(b.roles, optionId);
      if (!nextRoles) return b;
      return { ...b, roles: nextRoles, feedback: { states: resyncedForRoles(nextRoles, b.feedback.states) } };
    });
    pushScenarioRows(markScenarioReviewed(next, scenarioId));
  }

  /**
   * Set a NESTED branch's answer key — `handleScenarioRoleClick` one level further in.
   *
   * The row still gets marked reviewed, not the branch: §4.4's flag is about the row, and a
   * teacher who has authored a comparison and its answers has decided that variant.
   */
  function handleNestedRoleClick(scenarioId: string, branchId: string, optionId: string) {
    const next = (scenarioBranches ?? []).map(row => {
      if (row.scenarioId !== scenarioId) return row;
      return {
        ...row,
        conditionalBranches: (row.conditionalBranches ?? []).map(b => {
          if (b.id !== branchId) return b;
          const nextRoles = rolesAfterRoleClick(b.roles, optionId);
          if (!nextRoles) return b;
          // A nested branch's feedback is optional, and falls back to the row's — so resyncing
          // reads whichever set is actually in force rather than assuming its own.
          const states = (b.feedback ?? row.feedback).states;
          return { ...b, roles: nextRoles, feedback: { states: resyncedForRoles(nextRoles, states) } };
        }),
      };
    });
    pushScenarioRows(markScenarioReviewed(next, scenarioId));
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = activeFeedbackStates.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /** The card the Add button last produced — the one thing that opens a feedback card on mount.
   *  State, not a ref: it is read during render, and a ref mutated in a discarded render keeps a
   *  value that render threw away. */
  /**
   * The card the Add button last produced — the one thing that opens a feedback card on mount.
   *
   * Keyed BY MODE. Reordering the panel's sections on a mode switch remounts these cards, and a
   * bare id would still name one of them, so the last-added card sprang open on every switch —
   * the behaviour this was meant to remove. State, not a ref: it is read during render, and a ref
   * mutated in a discarded render keeps a value that render threw away.
   */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    const usedTriggers = new Set(activeFeedbackStates.filter(s => s.type === 'targeted').map(s => s.trigger));

    /**
     * A blank is one of the answers an OPTIONAL block can receive, so it belongs in the same list
     * as the options — which is also what lets `any_response` close once the list is complete.
     *
     * First in the trigger MENU, where not answering reads before any of the answers. The CARD
     * is appended like any other: the list order is the teacher's, and these are drag-reorderable
     * precisely so they can decide it.
     */

    // Outside graded no option is correct, so every one of them can carry a targeted state.
    const available = gradingMode === 'graded'
      ? effectiveOptionsForActive.find(o => o.role !== 'correct' && !usedTriggers.has(`option_${o.id}`))
      : effectiveOptionsForActive.find(o => !usedTriggers.has(`option_${o.id}`));
    const firstNonCorrect = available;
    /**
     * Absent needs an option to be absent FROM, which in graded means a CORRECT one — that is
     * the restriction that makes it read as "you left out a required answer". Outside graded the
     * restriction goes and the trigger keeps its plain meaning, "they did not pick this", so any
     * option can carry one. Multi-select only either way: a single pick cannot omit anything
     * while still being a pick.
     */
    const firstCorrectAbsent = allowMultiple
      ? effectiveOptionsForActive.find(o =>
          (gradingMode !== 'graded' || o.role === 'correct') && !usedTriggers.has(`option_${o.id}_absent`))
      : undefined;
    const firstOpt = firstNonCorrect ?? firstCorrectAbsent;
    if (!firstOpt) return;
    const isAbsent = firstOpt === firstCorrectAbsent;
    const trigger = isAbsent ? `option_${firstOpt.id}_absent` : `option_${firstOpt.id}`;
    const idx = options.findIndex(o => o.id === firstOpt.id);
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: mcqTargetedTitle(firstOpt, idx, isAbsent, t, optionsAsEquations),
      trigger, content: '',
      // Non-absent triggers derive evaluation from the target option's own role (kept in sync
      // going forward by resyncMcqTargetedEvaluations); "Not Selected" has no role to derive
      // from, so it starts at the same free-cycle default as before.
      evaluation: isAbsent ? 'neutral' : (firstOpt.role === 'neutral' ? 'neutral' : 'incorrect'),
    };
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    setActiveFeedbackStates(rebuildStates([...targeteds, newState]));
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    setActiveFeedbackStates(rebuildStates(arrayMove(targeteds, from, to)));
  }

  function handleDone() {
    push();
    // Closing is what makes a switched-off branch set unrecoverable: the retained draft is
    // dropped from the saved block and undo becomes the only way back.
    if (savedConditionalDraftRef.current !== undefined && conditionalBranches === undefined) pushBranchState(undefined, undefined, undefined);
    savedConditionalDraftRef.current = undefined;
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  /** Named the same way Ordering names its own, so the two read as one idea. */
  const SHAPES: { id: NonNullable<MCQBlockData['shape']>; label: string }[] = [
    { id: 'vertical', label: t('blocks.mcq.shapeVertical') },
    { id: 'horizontal', label: t('blocks.mcq.shapeHorizontal') },
  ];

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // Genuinely interactive now, not a disabled snapshot — Preview behaves exactly like the live
    // test widget (pick option(s), see real feedback once submitted), just triggered by the
    // page-level bottom Submit (see the previewSubmitSignal effect above) instead of a per-block
    // one. Reads through the same resolution pipeline computeMCQTestData/buildNormalizedMCQData
    // give the live test widget — a variable chip decoratively substituted, or an option formula
    // actually evaluated to a number when optionsAsEquations is on, exactly as a real student
    // would see it. rolledValues/testOptionOrder are seeded by the same effect the isSelected
    // branch below uses, gated on `isPreview` too (see its own comment).
    const rawD = storedData;
    // The version THIS student drew, from the task roll — see `versionRollKey`. Absent for a
    // block with no bank, which `pickVersion` reads as "the only one there is".
    const d = buildNormalizedMCQData(rawD, t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const { resolvedD, testResult } = memoizedByKey(
      /* The DRAWN VERSION is part of the key. Without it this cache answered for whichever
         version it saw first: stepping the pager rebuilt `d` and then threw it away for a
         stale result, so the card's content never moved and a branch line from a version
         that had one survived onto a version that did not. Rerolling appeared to 'fix' it
         only because `rolledValues` was already in the key. */
      mcqTestDataCache, block.data,
      [variables, t, rolledValues, submittedSelectedIds, hasSubmitted, i18n.language,
       taskPreviewRoll?.[versionRollKey(block.id)],
       /* The LEADER's draw is part of the key too, for the same reason this block's own is: the
          scenario decides the answer key, so a cache that ignored it would grade one scenario's
          answer against another's roles. */
       drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)],
      () => computeMCQTestData(d, variables, t, rolledValues, submittedSelectedIds, hasSubmitted, i18n.language,
        // The ONE task roll both blocks read, which is what makes the leader's prose and this
        // block's answer key describe the same scenario (§3.1).
        drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)),
    );
    /* The same derivation asked about the DRAFT: `hasSubmitted` forced true so the chips light
       as though this answer had been handed in. Reported, never rendered -- Preview still shows
       a student nothing until they submit. */
    const { testChips: draftChips, testResult: draftStates } = memoizedByKey(
      mcqDraftChipsCache, block.data,
      [variables, t, rolledValues, testSelectedIds, i18n.language,
       taskPreviewRoll?.[versionRollKey(block.id)], drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)],
      () => computeMCQTestData(d, variables, t, rolledValues, testSelectedIds, true, i18n.language, drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)),
    );
    return (
      <MCQAnswerDisplay
        question={resolvedD.question}
        options={resolvedD.options}
        optionOrder={testOptionOrder}
        rolledValues={rolledValues}
        value={testSelectedIds}
        onChange={setTestSelectedIds}
        allowMultiple={d.allowMultiple}
        horizontal={d.shape === 'horizontal'}
      >
        {/* See keyword-block: the block is the only thing that knows which of its conditions
            are in force once a version, a roll or a scenario is in play. */}
        <ReportTestChips chips={draftChips} states={draftStates} />
        {renderMCQFiredFeedback(testResult, rolledValues)}
      </MCQAnswerDisplay>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    const rawD = storedData;
    // Same backfill reasoning as Dropdown's own collapsed branch — a block just created via
    // "Add MCQ block" persists `data: {}`, and this instance reads `block.data` directly rather
    // than the editor's own already-normalized `feedback` state.
    //
    // Through the version in force, so the card tests what the heading is pointed at rather than
    // always the first — and so Reroll below can move it.
    const d = buildNormalizedMCQData(rawD, t, shownVersionId);
    const {
      matchedBranch, matchedBranchIndex, matchedEquationValue,
      testResult, resolvedD, testChips, hasChips, hasRollable,
    } = memoizedByKey(
      // Keyed on the shown version too — see the preview branch's note.
      mcqTestDataCache, block.data,
      [variables, t, rolledValues, submittedSelectedIds, hasSubmitted, i18n.language,
       shownVersionId, shownScenarioId],
      /*
       * On the CARD, the scenario is whichever one the pager is on — not a draw. A draw here
       * would be a different scenario from the one the editor is set up for, every time.
       */
      () => computeMCQTestData(d, variables, t, rolledValues, submittedSelectedIds, hasSubmitted, i18n.language,
        activeScenario?.scenarioId),
    );
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const mcqAriaLabel = (() => {
      const q = d.question ? d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      // Through `rolesForMode` like every visual read of a role: the card announced "Left (Marked
      // as correct)" in a mode with no correct answer, which is the same leak the glyphs had and
      // the only one a sighted check could not catch.
      const opts = rolesForMode(d.gradingMode, d.options).filter(o => o.text).map(o => {
        const text = o.text.replace(/<[^>]+>/g, ' ').trim();
        const roleNote = o.role === 'correct' ? ` (${t('blocks.mcq.markedCorrect')})` : o.role === 'incorrect' ? ` (${t('blocks.mcq.markedIncorrect')})` : '';
        return text + roleNote;
      }).slice(0, 4).join(', ');
      return t('blocks.mcq.editAriaBase') + (q ? `: ${q}` : '') + (opts ? `. ${opts}` : '') + `. ${displayMode.label}`;
    })();
    const displayChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';

    // Every currently-active issue for this block, computed once — read by this bar (top-priority
    // one only) and the test-panel diagnostics list (all of them), so the two surfaces can never
    // disagree about wording — identical role to Dropdown's own `dropdownIssues` consumption.
    /* Keyed on the shown version, like the test data beside it. The branching checks now span
       every version and no longer care, but the rest of `mcqIssues` still reads version-
       resolved data — a blank question or a missing alt text belongs to one version — so without
       this the panel kept reporting the version it first saw. */
    /* What a recall chip in these options could show. Rebuilt per render and therefore keyed
       into the cache below by CONTENT — its identity would recompute every block's issues on
       every render, and leaving it out would hold a stale warning after the source's options
       were edited in the other task. */
    const recalls = recallOptionSpaces(d.options.map(o => o.text), scope, variables);
    const issues = settleStored(memoizedByKey(mcqIssuesCache, block.data, [recallSpacesKey(recalls), variables, t, shownVersionId, animatedAssetIds], () => mcqIssues(d, { ...issueCtx, recalls })));
    const issueBar = collapsedIssueBar(issues);
    const mcqBarNode = (() => {
      if (issueBar) return issueBar;
      if (isMain) return null;
      return collapsedModeBar(d.gradingMode, t, 'mcq');
    })();

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<SquareCheck aria-hidden='true' className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.mcq.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={displayChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={mcqAriaLabel}
        answerBarSlot={() => mcqBarNode}
        testEvaluation={testResult ? (testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: testChips,
          onReset: handleTestReset,
          /* Versions are rollable too, so a banked block offers Reroll even with no variable
             chips in it — which is the canoe case exactly: the wording varies, the numbers do
             not. */
          /* OFFERED WHENEVER ANYTHING IS RANDOM, which includes `randomizeOrder` on its own.
       Now that Reset keeps the order, a block that shuffles but has no variable and no bank would
       otherwise have no way to see a second arrangement at all. Same reasoning Sort's own gate
       records. */
    onReroll: ((hasChips && hasRollable) || randomizeOrder) ? handleTestReroll : undefined,
          // Which branch resolved for this test, with a one-click jump straight to editing it —
          // identical role to Dropdown's own topNotices; teacher-only diagnostic, so it belongs
          // in this panel, not `children` (the frozen, student-facing content area).
          /* `undefined` when there is nothing to say, so a plain block renders no container at
             all — the consumer gives this slot its own spacing. */
          /*
           * ONE row for everything this test resolved to — the version it drew and, inside it,
           * the branch that matched. Two bordered boxes stacked above a small card spent more
           * height on diagnostics than on the question, and they are one fact anyway: "here is
           * what you are looking at". `undefined` when there is nothing to report, so a plain
           * block renders no container at all.
           */
          /*
           * LAYOUT FOLLOWS CONTENT.
           *
           * Two rows exist only to stop the pager being dragged around: the condition readout's
           * width changes with every roll — a formula, its value and a threshold, all
           * variable-length — so sharing a centred row with the chevrons moved them under the
           * teacher's cursor. With no bank there is no pager, nothing to protect, and a second row
           * is just a line of wasted height in a panel that has none to spare. So a branching
           * block with no versions keeps the original single row, Edit inline with the condition.
           */
          /* One row for everything this test resolved to — the version it drew and, inside it,
             the branch that matched. Shared with the phone bar below and with every other banked
             block: see `version-branch-notice.tsx` for the layout's reasoning. */
          topNotices: (
            <VersionBranchNotice
              /* A follower pages the LEADER's scenarios here, not a bank of its own — without
                 this the bar had no pager at all and there was no way to test a second
                 scenario. See `scenarioTestPager`. */
              versions={scenarioTestPager?.versions ?? shownVersions}
              activeVersionId={scenarioTestPager ? scenarioTestPager.activeId : shownVersionId}
              onSelectVersion={scenarioTestPager?.onSelect ?? setTestVersion}
              noun={scenarioTestPager?.noun ?? 'version'}
              editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={matchedBranch && matchedBranchIndex !== -1 ? {
                index: matchedBranchIndex,
                total: d.conditionalBranches?.length ?? 0,
                equation: d.conditionalEquation ?? '',
                rolledValues,
                value: matchedEquationValue,
                operator: matchedBranch.operator,
                threshold: matchedBranch.threshold,
                thresholdHigh: matchedBranch.thresholdHigh,
              } : null}
              onEdit={() => {
                if (matchedBranch && matchedBranchIndex !== -1) requestBranchJump(block.id, matchedBranchIndex);
                editShownVersion(matchedBranch?.id);
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={issues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {isSelected ? (
          <MCQAnswerDisplay
            question={d.question}
            options={resolvedD.options}
            optionOrder={testOptionOrder}
            rolledValues={rolledValues}
            value={testSelectedIds}
            onChange={handleTestChange}
            allowMultiple={d.allowMultiple}
            horizontal={d.shape === 'horizontal'}
            scenario={<ScenarioStimulus leader={leaderBlock} version={shownScenarioVersion as Record<string, unknown> | null} ghosted />}
          >
            {renderMCQFiredFeedback(testResult, rolledValues)}
          </MCQAnswerDisplay>
        ) : (
          <>
            <BlockQuestion html={d.question} spaced showEmptyNote />
            {/* The CLOSED card follows the same layout choice — it is a summary of the block,
                and a summary that stacked what a student will see in a row would misreport the
                one thing this setting is about.
                In the same COLUMN as the live control too, and centred the same way: the two
                states are one card, and a summary running the full width while the test surface
                sits capped and centred read as the block changing shape when you selected it. */}
            <div className={`${ANSWER_COLUMN_CLASS} ${d.shape === 'horizontal'
              ? 'flex flex-row flex-wrap items-start justify-center gap-x-4 gap-y-1.5'
              : 'flex flex-col gap-1.5'}`}
            >
              {rolesForMode(d.gradingMode, d.options).map((opt) => (
                <div key={opt.id} className='flex min-w-0 items-center gap-2'>
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${d.allowMultiple ? 'rounded-sm' : 'rounded-full'} transition-colors ${
                    opt.role === 'correct' ? 'border-2 border-ws-primary bg-ws-primary' :
                    opt.role === 'incorrect' ? 'bg-ws-muted/20' :
                    'border-2 border-ws-border'
                  }`}>
                    {opt.role === 'correct' && <Check className='h-2.5 w-2.5 text-ws-btn-on-primary' strokeWidth={3} />}
                    {opt.role === 'incorrect' && <X className='h-2.5 w-2.5 text-ws-muted' strokeWidth={3} />}
                    {opt.role === 'neutral' && <Minus className='h-2.5 w-2.5 text-ws-muted' strokeWidth={2} />}
                  </span>
                  <div className='min-w-0 flex-1 overflow-hidden'>
                    {/* When optionsAsEquations is on, opt.text is never populated (the option's
                        real authored content lives in opt.formula instead — see the toggle's own
                        contract) — show the raw, unresolved formula here rather than opt.text,
                        matching the option-editing row's own preview. Not the evaluated result:
                        this collapsed canvas summary has no rolled variables to evaluate against
                        outside Preview/Test mode, and a value baked from today's roll would go
                        stale the moment variables re-roll.
                        A formula is a plain `{name}` text string, not an HTML chip node —
                        PreviewHtml/renderChipsForDisplay only transforms `[data-variable-chip]`
                        elements, so it left a formula's own `{name}` references as literal
                        squiggly-brace text, unstyled. FormulaWithValues (already used for the
                        branch-condition equation elsewhere in this file) is the equivalent
                        chip-render for a formula string — `{}` (not the live rolledValues) forces
                        every reference to a bare styled chip, never an evaluated value, matching
                        "always chip on the display card" for the text branch too. */}
                    {d.optionsAsEquations ? (
                      // Checked before `opt.media` — equations mode suppresses media entirely, so
                      // an option can never genuinely have both at once (see the toggle handler's
                      // own doc comment); this ordering just means stale pre-fix data (an option
                      // that has both, saved before that invariant existed) resolves the same
                      // consistent way here as it does in the live editor's own row.
                      <span className='text-sm text-ws-text'>
                        <FormulaWithValues formula={opt.formula ?? ''} rolledValues={{}} />
                      </span>
                    ) : opt.media ? (
                      // Real thumbnail/waveform (compact-sized), not just an icon — this is the
                      // "display card," so it should look like the actual content, same as every
                      // other option type here. Label still shown alongside, bracketed, as the
                      // teacher-facing identifier — no positional "Option N" fallback for a blank
                      // one, same "blank stays visually blank" rule every other option display in
                      // this file already follows.
                      <span className='flex min-w-0 items-center gap-2 text-sm text-ws-text'>
                        <MCQMediaOptionContent media={opt.media} compact />
                        <span className='min-w-0 flex-1 truncate'>{stripHtmlForLabel(opt.text) ? `[${stripHtmlForLabel(opt.text)}]` : ''}</span>
                      </span>
                    ) : (
                      <PreviewHtml html={opt.text} as='span' className='text-sm text-ws-text [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic [&_u]:underline [&_s]:line-through [&_a]:text-ws-text-link [&_a]:underline [&_p:empty]:min-h-[1.25rem]' />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Editing — feedback panel props ────────────────────────────────────────

  const noAnswerSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'unanswered')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        evalLocked={false}
        lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        title={t('blocks.feedbackDefaults.noAnswerTitle')}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {
          const ORDER = ['neutral', 'incorrect'] as const;
          const cur = state.evaluation === 'correct' ? 'neutral' : state.evaluation as 'neutral' | 'incorrect';
          updateActiveFeedbackState(state.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const allTriggers = new Set(activeFeedbackStates.filter(s => s.type === 'targeted').map(s => s.trigger));
  const canAdd = gradingMode === 'graded'
    ? effectiveOptionsForActive.some(o =>
      o.role !== 'correct'
        ? !allTriggers.has(`option_${o.id}`)
        : allowMultiple && !allTriggers.has(`option_${o.id}_absent`))
    // Every option as a PRESENT target, every option as an ABSENT one in multi-select, plus the
    // blank in optional — see addTargetedState for why a blank belongs in this list at all.
    : effectiveOptionsForActive.some(o => !allTriggers.has(`option_${o.id}`))
      || (!!allowMultiple && effectiveOptionsForActive.some(o => !allTriggers.has(`option_${o.id}_absent`)));

  // Mode-scoped: optional is INERT and shows no targeted state at all, and `set_answer` is
  // hidden in graded where Correct already says it. Hidden, never deleted — see `targetedFiresIn`.
  const visibleTargetedStates = visibleTargeted(activeFeedbackStates, gradingMode).filter(s => !isTargetedStaleForActive(s));

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, visibleTargetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={visibleTargetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {visibleTargetedStates.map(state => {
            const ORDER = ['neutral', 'incorrect'] as const;
            const usedTriggers = new Set(
              activeFeedbackStates.filter(s => s.type === 'targeted' && s.id !== state.id).map(s => s.trigger)
            );
            const triggerOptions: TriggerOption[] = effectiveOptionsForActive.flatMap((opt, i) => {
              const optEval = (opt.role === 'incorrect' ? 'incorrect' : 'neutral') as TriggerOption['evaluation'];
              const baseLabel = `${t('blocks.mcq.triggerOptionLabel')} ${i + 1}`;
              // bracketizeVariableChips (not stripHtmlForLabel) — this preview keeps rich
              // formatting (bold/italic/color) intact around the option's own markup, since a
              // teacher may be relying on that formatting to tell two otherwise-identical-text
              // options apart when picking a trigger; only the variable-chip's bare-name
              // static serialization needs fixing up to the `{name}` convention every
              // feedback-facing surface follows.
              // Same media exception as mcqOptionLabel above — a media option's text is always
              // the preview source, since it never has a formula. Skips bracketizeVariableChips
              // for it too — the Label is a plain input (see SortableOptionRow), never rich-text/
              // chip-capable, so running it through an HTML-chip-rewrite would only risk mangling
              // (or leftover-HTML-decoding) genuinely plain text; MCQTriggerDropdown/MCQTriggerChip
              // render a media option's preview as plain text for the same reason.
              const rawPreview = ((optionsAsEquations && !opt.media) ? opt.formula : opt.text) || undefined;
              // Chips, not bracketized text: a formula's own `{name}` and a chip span's bare-name
              // serialization both used to reach the menu as literal braces.
              const preview = rawPreview && !opt.media
                ? labelChipHtml(rawPreview, { asFormula: !!optionsAsEquations, knownNames: new Set(variables.map(v => v.name)) })
                : rawPreview;
              return [
                ...(opt.role !== 'correct' && !usedTriggers.has(`option_${opt.id}`) ? [{
                  value: `option_${opt.id}`,
                  label: allowMultiple ? `${baseLabel} — ${t('blocks.mcq.triggerSelected')}` : baseLabel,
                  preview,
                  mediaKind: opt.media?.kind,
                  evaluation: optEval,
                }] : []),
                // The words "(not selected)" live in `label` and nowhere else. `label` is what the
                // picker MENU rows show, and a menu row draws no slash, so there the words are the
                // only thing distinguishing an absent trigger from its selected twin. Every other
                // surface — the trigger button and the collapsed card's chip — draws the slash over
                // the option's own preview, and used to append the words on top of it. Saying it
                // twice doubled the chip's width for no added meaning, and the overflow was what
                // pushed the chip and the words into each other.
                ...(allowMultiple && (gradingMode !== 'graded' || opt.role === 'correct') && !usedTriggers.has(`option_${opt.id}_absent`) ? [{
                  value: `option_${opt.id}_absent`,
                  label: `${baseLabel} (${t('blocks.mcq.triggerAbsent')})`,
                  preview,
                  mediaKind: opt.media?.kind,
                  // Green only in graded, where it targets a CORRECT option left out. Outside
                  // graded it targets any unselected option and there is no role to colour it by.
                  evaluation: (gradingMode === 'graded' ? 'correct' : 'neutral') as TriggerOption['evaluation'],
                }] : []),
              ];
            });
            const currentOpt = triggerOptions.find(o => o.value === state.trigger) ?? triggerOptions[0];
            // "Not Selected" (_absent) triggers have no underlying option role to derive from
            // (there's nothing in the option list representing "left unselected"), so only they
            // stay freely cyclable — every present-option trigger locks to that option's role.
            const isAbsentTrigger = state.trigger.endsWith('_absent');
            return (
              <SortableFeedbackCard
                key={state.id}
                sortId={state.id}
                title={state.title}
                content={state.content}
                evaluation={displayEvaluation(gradingMode, state.evaluation)}
                /* Collapse on a grading-mode switch: these cards do not remount, so one the teacher
                   had collapsed sprang back open every time they flipped modes to compare. */
                collapseSignal={gradingMode}
                startExpanded={justAddedKey(state.id) === justAddedStateId}
                titleLocked
                /* `_absent` is the one cyclable targeted evaluation, and it is graded-only —
                   it fires when a CORRECT option was not picked. Outside graded there is no
                   correct, so nothing is cyclable and the control locks. */
                evalLocked={gradingMode !== 'graded' || !isAbsentTrigger}
                /* THE SHARED PICKER. MCQ is the option-driven pole: an option's role IS its
                   correctness, so the tone and the symbol come from the SELECTED OPTION and no
                   `evaluation` is passed. The `_absent` slash crosses the whole control, because a
                   line through only the answer says the opposite of "this correct answer was not
                   selected". The preview is a node from the one shared renderer, which is what
                   makes an equation render and a media option carry its glyph. */
                triggerZone={<TriggerDropdown value={state.trigger} options={triggerOptions.map(o => ({
                  value: o.value,
                  label: o.label,
                  preview: o.preview ? mcqOptionPreviewNode({ media: o.mediaKind ? { kind: o.mediaKind } : undefined, text: o.preview }) : undefined,
                  evaluation: o.evaluation,
                }))} slashWhen={(v) => v.endsWith('_absent')} onChange={trigger => {
                  const isAbsent = trigger.endsWith('_absent');
                  const id = isAbsent ? trigger.slice('option_'.length, -'_absent'.length) : trigger.slice('option_'.length);
                  const idx = effectiveOptionsForActive.findIndex(o => o.id === id);
                  const opt = idx === -1 ? undefined : effectiveOptionsForActive[idx];
                  updateActiveFeedbackState(state.id, {
                    trigger,
                    title: opt ? mcqTargetedTitle(opt, idx, isAbsent, t, optionsAsEquations) : state.title,
                    evaluation: (!isAbsent && opt) ? (opt.role === 'neutral' ? 'neutral' : 'incorrect') : state.evaluation,
                  });
                }} />}
                triggerZoneSummary={currentOpt ? <MCQTriggerChip option={currentOpt} /> : undefined}
                onTitleChange={() => {}}
                onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
                onEvaluationCycle={isAbsentTrigger ? () => updateActiveFeedbackState(state.id, { evaluation: ORDER[(ORDER.indexOf(state.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! }) : () => {}}
                onDelete={() => deleteActiveFeedbackState(state.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
  /**
   * The same derivation the test bar reads, which is the point of the phase — and it corrects this
   * side too.
   *
   * The rule here used to count a NEUTRAL option as a route to Default Incorrect only once Correct
   * was already inactive. The evaluator disagrees: picking the neutral alone leaves the correct
   * option unselected, so `isCorrect` is false and the catch-all fires. A block with one correct
   * and one neutral option and no incorrect one therefore reported Default Incorrect as Unreachable
   * while it was firing — the mirror image of the test bar's own lie, and invisible for the same
   * reason, that each surface only ever agreed with itself.
   */
  const panelReach = mcqReachability({ allowMultiple, gradingMode, feedback: { states: activeFeedbackStates } }, effectiveOptionsForActive);
  const indexOf = (id: string) => effectiveOptionsForActive.findIndex(o => o.id === id);
  const uncoveredIncorrect = panelReach.uncoveredWrong.map(opt => ({ opt, idx: indexOf(opt.id) }));
  // Multi-select only: selecting a strict subset of the required correct options is
  // also incorrect, unless "absent" feedback exists to catch that specific option's
  // omission. Without this, Default Incorrect could be wrongly marked Inactive even
  // though under-selecting still reaches it at evaluation time.
  const uncoveredCorrectAbsent = panelReach.uncoveredAbsent.map(opt => ({ opt, idx: indexOf(opt.id) }));
  const defaultIncorrectItems = [
    ...uncoveredIncorrect.map(({ opt, idx }) => ({ opt, idx, missing: false as const })),
    ...uncoveredCorrectAbsent.map(({ opt, idx }) => ({ opt, idx, missing: true as const })),
  ];
  // The same two lists the test legend draws — see `mcqChips`. What used to sit here was a second
  // construction of them from `uncoveredCorrect` / `allIncorrectOptions` / `allNeutralOptions`,
  // which is how the panel came to disagree with the legend about which correct options are
  // REQUIRED (the legend asks reachability, which is what `evaluateMCQ` is tested against) and
  // about how an optional option is drawn.
  //
  // `panelReach` rather than the bar's `reachability`, deliberately: it is built from the
  // branch-resolved but NOT variable-resolved options, so the panel at rest shows `{n}` as a chip
  // while the legend shows the rolled value. That is the three-mode resolution rule, not drift.
  const chipRow = (chips: ConditionChips) => (
    <ConditionChipRow chips={chips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
  );
  const defaultIncorrectInline = chipRow(mcqChips('incorrect', panelReach, allowMultiple, t));
  const defaultCorrectInline = chipRow(mcqChips('correct', panelReach, allowMultiple, t));

  /**
   * The catch-all LISTS what it still catches, exactly as Default Incorrect does in graded.
   *
   * A generic "Any Answer" pill left the teacher to work out which answers were "any" — and once
   * targeted states cover some of the options, that is a different set from one minute to the
   * next. Enumerating is MCQ's own Default Incorrect behaviour, and it is available here for the
   * same reason: the option set is CLOSED, so what falls through is a short known list.
   *
   * It also makes the "Any Answer" versus "Any Other Answer" question moot. The chips say what
   * the card catches; the title is only its name.
   */
  const participationCatchAllInline = chipRow(catchAllChips(gradingMode, visibleTargetedStates.length > 0, t,
    () => mcqChips('incorrect', panelReach, allowMultiple, t, gradingMode)));
  const anyAnswerInline = participationCatchAllInline;
  const anyResponseInline = participationCatchAllInline;
  /**
   * An unreachable positive state shows the standard UnreachableChip in place of its trigger,
   * the same treatment compass and numerical already use — not a dimmed version of a trigger
   * that cannot happen. Survey's Any Answer closes once every option has a targeted state;
   * optional's Any Response never does, because a blank always reaches it.
   */
  const positiveUnreachable = !isReachable(panelReach, positiveTriggerForMode(gradingMode));
  const positiveTriggerZone = positiveUnreachable
    ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
    : gradingMode === 'survey' ? anyAnswerInline
    : gradingMode === 'optional' ? anyResponseInline
    : defaultCorrectInline;

  const correctSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        // From the derivation, not from counting the display list. Same answer for the case that
        // list stood in for — no correct option at all — plus the defensive one it could not
        // express, single-select holding two correct options, which the editor prevents but
        // pre-guard data can still carry.
        inactive={positiveUnreachable}
        triggerZone={positiveTriggerZone}
        triggerZoneSummary={positiveTriggerZone}
        title={catchAllTitle(gradingMode, visibleTargetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /**
         * The tick goes the moment a target exists — the catch-all must not affirm an answer the
         * teacher ignored more strongly than one they wrote for. With no targets the block is
         * still a plain questionnaire and keeps it. Same rule `catchAllChips` applies to the chip
         * beside it, so the card and its trigger cannot disagree.
         */
        evaluation={gradingMode !== 'graded' && visibleTargetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const incorrectSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        inactive={defaultIncorrectItems.length === 0}
        triggerZone={defaultIncorrectInline}
        triggerZoneSummary={defaultIncorrectInline}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Live in-editor issues ────────────────────────────────────────────────
  // The exact same computation the collapsed bar and test-panel diagnostics use, run against the
  // editor's own live state instead of `initData(block.data)` — identical role to Dropdown's own
  // `editorIssues`/`liveD`. Also the same shape computeMCQTestData needs for the phone Test tab
  // below, so it's named once and reused rather than rebuilt per call site.
  /* Enumerated, so a field left out is a check that silently never runs — `scenarioBranches`
     was the one that made a follower's live test grade against the block's unscoped key. */
  const liveD: MCQBlockData = { question: questionHtmlRef.current, options, allowMultiple, randomizeOrder, shape, gradingMode, feedback, conditionalEquation, conditionalBranches, optionsAsEquations, angleMode, followsBlockId: storedData.followsBlockId, scenarioBranches };
  const editorIssues = settleLive(mcqIssues(liveD, issueCtx));
  // ONE derivation, read from the issues themselves. These sets used to re-run the duplicate and
  // collision checks a second time purely to decide which rows to ring, which is the pattern
  // `BlockIssue.fields` exists to end: the check that found the fault now says which rows it was
  // in, so the ring and the message cannot describe different rows.
  const ringedFieldIds = issueFieldIds(editorIssues);
  // Rendered as the frame's own sticky `warningBarSlot`, replacing the old per-mode
  // survey/optional/no-correct-answer banner — identical role to Dropdown's own
  // `editorIssues`/`warningBarSlot`. Every issue reaches the bar, missing-feedback-text included:
  // the per-card indicator only exists on the Feedback tab, while this bar renders on both.
  const hasEditorError = (...keys: string[]) => editorIssues.some(i => i.severity === 'error' && keys.includes(i.key));

  // ── Phone "Test" tab ─────────────────────────────────────────────────────────
  // Only ever rendered while `isPhone` (InputBlockFrame ignores it otherwise), and only replaces
  // the editingSlot content. Identical role to Dropdown's own phone Test tab: same
  // computeMCQTestData the desktop collapsed card uses, fed `liveD` instead of `initData(block.data)`.
  const phoneTest = isPhone
    ? computeMCQTestData(liveD, variables, t, rolledValues, submittedSelectedIds, hasSubmitted, i18n.language,
      activeScenario?.scenarioId)
    : null;
  const phoneHasChipsRollable = phoneTest ? (
    (htmlHasVariableChips(liveD.question) || (liveD.optionsAsEquations
      ? liveD.options.some(o => getEquationVariableNames(o.formula ?? '').length > 0)
      : liveD.options.some(o => !o.media && htmlHasVariableChips(o.text)))) &&
    hasRollableVariables(variables)
  ) : false;
  const phoneTestSlot = phoneTest ? (
    <MCQAnswerDisplay
      question={liveD.question}
      options={phoneTest.resolvedD.options}
      optionOrder={testOptionOrder}
      rolledValues={rolledValues}
      value={testSelectedIds}
      onChange={handleTestChange}
      allowMultiple={allowMultiple}
      /* The live editor value, not the stored one: the phone's Edit and Test are two tabs over
         ONE session, so flipping the switch has to show on the next tab immediately. */
      horizontal={shape === 'horizontal'}
      /* The phone needs this most: the leader is never on screen beside the block. */
      scenario={<ScenarioStimulus leader={leaderBlock} version={shownScenarioVersion as Record<string, unknown> | null} ghosted />}
    >
      {renderMCQFiredFeedback(phoneTest.testResult, rolledValues)}
    </MCQAnswerDisplay>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    // Versions are rollable too — same reason as the desktop bar's gate.
    onReroll: (phoneHasChipsRollable || randomizeOrder) ? handleTestReroll : undefined,
    /* Layout follows content, same as the canvas card's: two rows only where there is a pager to
       protect from the condition line's changing width. Height is scarcer here than anywhere. */
    /* The same row as the canvas card's — one component, so the two can never drift. */
    topNotices: (
      <VersionBranchNotice
        versions={scenarioTestPager?.versions ?? shownVersions}
        activeVersionId={scenarioTestPager ? scenarioTestPager.activeId : shownVersionId}
        /* The EDITOR's pointer here, unlike the canvas card's. Not an inconsistency: on a phone,
           Edit and Test are two tabs over ONE editing session, and this tab renders `liveD` — the
           editor's own live fields — so a student sees edits as they are typed. A separate test
           pointer would leave the pager naming one version while the content showed another.
           A FOLLOWER has no pointer of its own either way: the leader's is the group's. */
        onSelectVersion={scenarioTestPager?.onSelect ?? selectVersion}
        noun={scenarioTestPager?.noun ?? 'version'}
        editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
        stacked
        branch={phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1 ? {
          index: phoneTest.matchedBranchIndex,
          total: conditionalBranches?.length ?? 0,
          equation: conditionalEquation ?? '',
          rolledValues,
          value: phoneTest.matchedEquationValue,
          operator: phoneTest.matchedBranch.operator,
          threshold: phoneTest.matchedBranch.threshold,
          thresholdHigh: phoneTest.matchedBranch.thresholdHigh,
        } : null}
        onEdit={() => {
          if (phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1) {
            setActiveBranchIndex(phoneTest.matchedBranchIndex);
          }
          editShownVersion(phoneTest.matchedBranch?.id);
          setPhoneTestMode('edit');
        }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  // ── Feedback branch scroller ──────────────────────────────────────────────
  // Same shared activeBranchIndex the setup carousel already uses, in a separate control here so
  // switching branches can never accidentally leave the two views out of sync — identical role to
  // Dropdown's own `feedbackBranchScroller`.

  // Graded-only, like every other branching surface (activeBranch, showConditionalUI): branches are
  // read straight from persisted data, so a block authored with branches and later switched to
  // Participation/Optional kept showing this scroller above states those branches can never reach.
  /**
   * The same bar for a follower, naming the scenario instead of a comparison.
   *
   * Feedback is per answer setup either way, so the panel below is already showing one row's
   * feedback and the teacher needs to know which. Browse-only `VersionSelector` again: the rows
   * are the leader's to add and remove.
   */
  const feedbackScenarioScroller = activeScenario ? (
    <div className='flex items-center justify-center gap-2 border-b border-ws-border bg-ws-surface px-4 py-2'>
      <span className='truncate text-xs font-medium text-ws-muted'>{activeScenarioVersion(activeScenario.scenarioId)?.name}</span>
      {scenarioPager}
    </div>
  ) : null;

  const feedbackBranchScroller = (
    /* Six blocks had written this identically; see `FeedbackBranchPager`. */
    <FeedbackBranchPager
      gradingMode={gradingMode}
      branches={conditionalBranches}
      activeIndex={activeBranchIndex}
      onActiveIndexChange={setActiveBranchIndex}
    />
  );

  // ── Editing shell ─────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      icon={<SquareCheck aria-hidden='true' className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.mcq.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.mcq.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
            <CheckboxRow fieldId='allowMultiple'
              checked={allowMultiple}
              onToggle={() => handleAllowMultiple(!allowMultiple)}
              label={t('blocks.mcq.allowMultiple')}
            />
          </OptionsSection>
          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>
            <CheckboxRow fieldId='randomizeOrder'
              checked={randomizeOrder}
              onToggle={() => handleRandomize(!randomizeOrder)}
              label={t('blocks.optionsFields.randomizeOrder')}
            />
            {/* The same control Ordering uses for the same question — a RadioGroup naming both
                layouts, rather than a checkbox stating one as the negation of the other. */}
            <RadioGroup fieldId='shape'
              aria-label={t('blocks.mcq.shapeLabel')}
              value={shape}
              onChange={handleShape}
              options={SHAPES.map(({ id, label }) => ({ value: id, label }))}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.mcq.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      feedbackPanelProps={{
        beforeSlot: feedbackScenarioScroller ?? feedbackBranchScroller,
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore: canAdd,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
          isEmpty: visibleTargetedStates.length === 0,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
            {/* The pager rides the QUESTION heading rather than a row of its own. A bank is a
                niche feature, and given its own row it was the first thing the eye landed on —
                here it is available without being advertised, and the heading matches ANSWER
                SETUP below so the editor gains a section rather than a widget. */}
            {/* The three-way offer, when linking meets an existing bank — §7.1. Rendered from
                here so it is inside the editor it is about. */}
            {remapModal}
            {/* The link control and what it points at, for BOTH kinds of link — see
                `VariesWithSection`, which decides on its own whether a DRIVEN block
                needs it. This was five lines of identical JSX in twelve blocks. */}
            <VariesWithSection
              following={!!activeScenario}
              linkMenu={scenarioLinkMenu}
              stimulus={<ScenarioStimulus
                leader={leaderBlock}
                /* Null when this block follows nothing, which is what lets the stimulus
                   fall through to the DRIVEN row. The `&&` that used to narrow this is
                   gone — the section decides for itself whether to appear. */
                version={activeScenario
                  ? activeScenarioVersion(activeScenario.scenarioId) as Record<string, unknown> | null
                  : null}
              />}
            />
            {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
                the answer setup, because a driven axis varies the question too. Renders its
                children bare when this block is not driven. See `DrivenRowFrame`. */}
            <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
            <EditorSectionHeading
              label={t('blocks.areas.question')}
              /* First heading in the panel unless a scenario is above it. */
              divider={!!activeScenario}
              action={(
                /* `gap-2`: the pager's armed ring bleeds 5px (4px of padding plus a 1px
                   outline, which draws outside the box). `gap-3` cleared it too and pushed the
                   trash past the panel's edge. */
                <div className='flex items-center gap-2'>
                {/* The LINK sits after the pager: the pager belongs to the heading it is
                    paging, so "Question < 1 of 6 >" is one thought, and the link is a separate
                    one that belongs at the end of the row. */}
                {/* The question bank is GONE while this block follows a scenario, not merely
                    inert — §2.2. Versions are many stems each with one answer setup; a scenario
                    is one stem with many. Offering "make another version of this question" to a
                    follower invites building the second structure inside the first, and the two
                    cannot both be the answer key. The rows in ANSWER SETUP are where a follower
                    varies. */}
                {!storedData.followsBlockId && (
                <VersionSelector
                  versions={storedData.versions ?? []}
                  activeId={editorVersionId}
                  onSelect={selectVersion}
                  onAdd={() => {
                    const next = addVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                    emit(versionStructure(block.data, next));
                    // Land on the one just made: a teacher pressed "+" to write a DIFFERENT
                    // question, not to look again at the one they already had.
                    const added = next.versions?.[next.versions.length - 1];
                    if (added) selectVersion(added.id, next);
                  }}
                  onRemove={(id) => {
                    const next = removeVersion(initData(block.data), id);
                    emit(versionStructure(block.data, next));
                    // Land on what is left. `removeVersion` collapses the last two back to a
                    // plain block, so `versions` can be gone entirely — then there is no version
                    // to select and the editor re-seeds from the block itself.
                    const landing = next.versions?.[0];
                    selectVersion(landing?.id, next);
                    /* The test surface may have been pointed at the version just deleted. Its own
                       pointer is independent, but a dangling id is still worth clearing so the
                       card's notice does not report a position inside a bank that shrank. */
                    setTestVersion(landing?.id);
                  }}
                />
                )}
                {/* Silent once there is a Varies-With section, which is where the
                    control moves to — see `QuestionHeadingLink`. Written as
                    `!activeScenario` it knew about scenarios only, so a DRIVEN block
                    showed the link twice and the heading overflowed sideways. */}
                <QuestionHeadingLink
                  following={!!activeScenario}
                  linkMenu={scenarioLinkMenu}
                />
                </div>
              )}
            />

            <BlockQuestionEditor
              value={questionHtml}
              onChange={(html) => { questionHtmlRef.current = html; setQuestionHtml(html); push(); }}
              toolbar={sharedToolbar}
              editorRef={questionEditorRef}
              shouldFocusRef={shouldFocusEditor}
              embedded={embedded}
            />

            <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

            {(() => {
              // Meaningless outside graded mode — survey/optional never consult an option's role.
              // Question-only (see the auto-clear effect's own doc comment for why this is
              // deliberately NOT ORed with hasVariableInOptions below) — "Correctness depends on
              // variables" and "Treat options as equations" are a one-to-one correlation with
              // where the variable lives, not a shared gate.
              /*
               * THE VARIABLE MAY BE IN THE SCENARIO RATHER THAN THE QUESTION.
               *
               * A follower's stem lives in the leader, so a block can have a perfectly static
               * question — "The kayak is traveling to the:" — over a scenario full of chips.
               * Reading only the question hid the branching toggle from exactly the blocks that
               * need it most. The owner put it plainly: "a static question but a variable
               * scenario".
               */
              const scenarioHtml = (shownScenarioVersion as { html?: string } | null)?.html ?? '';
              const hasVariable = htmlHasVariableChips(questionHtmlRef.current) || htmlHasVariableChips(scenarioHtml);
              /*
               * FOLLOWING NO LONGER RULES BRANCHING OUT. It used to, on a misreading of §2.1 —
               * which is about a block LEADING and answering at once, not about a follower
               * comparing two of its own variables. When both are on, the branching nests
               * INSIDE the scenario row: the scenario picks the row, the row's own equation
               * picks the answer.
               */
              const showConditionalUI = hasVariable && gradingMode === 'graded';
              // Specifically about OPTIONS, not the question — independent of showConditionalUI.
              const hasVariableInOptions = mayVarMechanism
                && options.some(o => !o.media && (htmlHasVariableChips(o.text) || getEquationVariableNames(o.formula ?? '').length > 0));
              /*
               * A RECALL OPENS IT TOO, and until now nothing did but a variable.
               *
               * The mechanism is "each option's CONTENT is computed", and a recalled answer is
               * as good a thing to compute from as a rolled variable — better, for error carried
               * forward. But the only door into equations mode was a variable chip in an option,
               * so a teacher with Answer Recall on and Variables off could not put a recall into
               * an option at all. Asked for in those words: "i want to be able to put recall
               * chips into mcq/dropdown options, just like variables".
               *
               * Gated on the RECALL capability, not on the variables mechanism: they are
               * different capabilities and this is the half that belongs to recall.
               */
              /* A NUMBER'S alias, and only that. The mechanism computes a value, so a
                 recall that resolves to words opens nothing — it is already working where it
                 stands, in the option's own text. */
              const hasRecallInOptions = mayRecallHere
                && options.some(o => !o.media && (
                  recallAliasIdsIn(o.text).some(id => numericAliasIds.has(id))
                  || hasRecallChip(o.formula ?? '')));
              const hasTrigAnywhere = TRIG_CALL_PATTERN.test(conditionalEquation ?? '') ||
                options.some(o => TRIG_CALL_PATTERN.test(o.formula ?? ''));
              const isEnabled = conditionalBranches !== undefined;
              const branches = conditionalBranches ?? [];
              const clampedIndex = Math.min(activeBranchIndex, Math.max(0, branches.length - 1));
              // Gated on gradingMode, not just isEnabled — same reasoning as Dropdown's own
              // shadowed local `activeBranch`: this is the one that actually feeds the
              // options-list editor's role display/onRoleClick below.
              const activeBranch = isEnabled && gradingMode === 'graded' ? branches[clampedIndex] : undefined;

              /*
               * WHOSE BRANCH SET IS ON SCREEN — the row's when following, the block's otherwise.
               *
               * The equation has to belong to the ROW, not the block, and that is the whole
               * argument for nesting. In the case this was built for the signs are PROSE —
               * "paddling at +{kayak} on a river flowing at -{river}" — so `{kayak}` is
               * positive in every variant and one block-level equation cannot tell `+ - +`
               * from `- + -`. The row knows its own signs: `{kayak} - {river}` belongs to one
               * row and `{river} - {kayak}` to its mirror.
               */
              const { nesting, effEquation, effEnabled, effBranches, effIndex, effActiveBranch } =
                nestedBranching<MCQFeedback>({
                  activeScenario,
                  blockEquation: conditionalEquation,
                  blockBranches: branches,
                  blockEnabled: isEnabled,
                  activeBranchIndex,
                  graded: gradingMode === 'graded',
                });
              const effHasEquation = !!effEquation?.trim();
              const effEquationInvalid = effHasEquation
                && evaluateEquation(effEquation!, minSampleValues(variables), angleMode) === null;

              /**
               * Every write to the row's own branching, state and storage together.
               *
               * Deciding a row's branching IS reviewing it — §4.4's flag means "copied, not
               * decided", and authoring a comparison is as much a decision as picking an
               * option. Without this a teacher could finish all four branching rows and still
               * see them flagged.
               */
              const writeRow = (patch: Partial<ScenarioRoleBranch<MCQFeedback>>) => {
                if (!activeScenario) return;
                pushScenarioRows(markScenarioReviewed(
                  (scenarioBranches ?? []).map(b =>
                    (b.scenarioId === activeScenario.scenarioId ? { ...b, ...patch } : b)),
                  activeScenario.scenarioId,
                ));
              };

              /**
               * A new branch for the ROW, seeded from the ROW.
               *
               * `makeConditionalBranch` seeds from the block's static option roles, which are
               * the wrong answer key here: the row already holds one, and "migrate" means the
               * first branch inherits what is currently correct. Seeding from the block would
               * silently discard the row's decision the moment branching was switched on.
               */
              const makeRow = (mode: SeedMode) => makeRowBranch<MCQFeedback>({
                mode,
                roles: activeScenario?.roles ?? {},
                feedback: activeScenario!.feedback,
                cloneFeedback: cloneFeedbackWithFreshIds,
                seedFeedback: seedBranchFeedback,
              });

              /**
               * One toggle, two scopes. On a follower it turns the ROW's branching on and off;
               * everywhere else it is the block-level toggle it has always been.
               */
              const toggleRowBranching = () => {
                if (!nesting) { toggleConditionalCorrectness(!isEnabled); return; }
                setActiveBranchIndex(0);
                // No session draft to restore here, unlike the block-level toggle: a row's set
                // is small and belongs to one variant, and banking per row would need a bank
                // per row. Untick is a delete, which undo already covers.
                writeRow(effEnabled
                  ? { conditionalEquation: undefined, conditionalBranches: undefined }
                  : { conditionalEquation: activeScenario!.conditionalEquation ?? '',
                      conditionalBranches: [makeRow('migrate')] });
              };

              /*
               * WHERE THE TOGGLE SITS, which is a claim about WHAT IT APPLIES TO.
               *
               * On a follower it turns branching on for ONE ROW, so standing above the scenario
               * box — beside "Treat options as equations", which really is block-wide — reads as
               * "enable this for every scenario". The owner caught it on sight. Inside the box,
               * under the row's own name and pager, it says what it does.
               */
              const branchingToggle = showConditionalUI ? (
                <BranchingCorrectsRow
                  checked={effEnabled}
                  onToggle={toggleRowBranching}
                />
              ) : null;
              const blockLevelToggle = showConditionalUI && !nesting;

              /* The row the option list edits, INNERMOST FIRST: a nested branch, then the
                 scenario row, then a plain numeric branch. */
              const activeRow = effActiveBranch ?? activeScenario ?? activeBranch;
              const hasEquation = !!conditionalEquation?.trim();
              const equationInvalid = hasEquation && evaluateEquation(conditionalEquation!, minSampleValues(variables), angleMode) === null;

              // Which rows the "two or more options are the same" issue is about. Built from the
              // SAME two helpers `mcqIssues` uses for that check, so the highlight and the
              // message cannot disagree about which options collide. Only when options are not
              // equations — in equation mode a collision is `option-collision`, already painted
              // on the field by `formulaHasError` below.
              const duplicateOptionIds = ringedFieldIds;
              // Gated on the same flag as the issue list. This paints the option's own field red
              // and is computed directly from `checkOptionFormulaIssues`, never through
              // mcqIssues — so the panel waited and the field did not.
              const optionFormulaIssueIds = new Set(
                optionsAsEquations
                  ? checkOptionFormulaIssues(
                      options,
                      variables,
                      angleMode
                    ).issues.flatMap(i => i.optionIds)
                  : []
              );

              // The exact same option editor used whether or not conditional correctness is on —
              // only which role source feeds each row's correct/incorrect button changes.
  // Rendered WITH the list in both states, the pattern Scale already used. Adding an option is an
  // answer-set operation, so the control belongs with the answer set — and when branching is on the
  // answer set lives inside the carousel, which is where the button sits rather than across a
  // border from the list it appends to.
  //
  // It is global, not per-branch: an option added here appears in every condition. That reads as a
  // scope claim only if you believe the carousel bounds a scope, and it does not — the option text,
  // formula and media edited inside it are equally global, and only the role toggle varies.
  const addOptionButton = (
          <AddRowButton
            ref={addOptionButtonRef}
            label={t('blocks.options.addOption')}
            onClick={addOption}
          />
  );

              const optionListJsx = (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleOptionDragEnd}
                  accessibility={dndA11y(t, options.map(x => x.id), 'option')}
    >
                  <SortableContext items={options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                    <div className='flex flex-col gap-3'>
                      {options.map((opt) => (
                        <SortableOptionRow
                          key={opt.id}
                          /* The row the option list actually edits. A scenario and an equation
                             are two selectors and a block answers to one, so one local covers
                             both — `activeScenario` first because a linked block has no
                             numeric branch to fall through to. */
                          opt={{ ...opt, role: roleForMode(gradingMode, activeRow ? activeRow.roles[opt.id] ?? 'incorrect' : opt.role) }}
                          optionCount={options.length}
                          allowMultiple={allowMultiple}
                          roleLocked={gradingMode !== 'graded'}
                          showAnchor={randomizeOrder}
                          armedOptionId={armedOptionId}
                          setArmedOptionId={setArmedOptionId}
                          /* INNERMOST FIRST, matching `activeRow` above — the two must name
                             the same row or the button would show one key and edit another. */
                          onRoleClick={effActiveBranch && activeScenario
                            ? (id) => handleNestedRoleClick(activeScenario.scenarioId, effActiveBranch.id, id)
                            : activeScenario ? (id) => handleScenarioRoleClick(activeScenario.scenarioId, id)
                            : activeBranch ? (id) => handleBranchRoleClick(activeBranch.id, id) : handleRoleClick}
                          onToggleAnchor={toggleAnchor}
                          onTextChange={updateOptionText}
                          onRemove={removeOption}
                          optionEditorsRef={optionEditorsRef}
                          activeEditorRef={activeEditorRef}
                          setActiveEditor={setActiveEditor}
                          formulaMode={optionsAsEquations}
                          onFormulaChange={updateOptionFormula}
                          formulaHasError={optionFormulaIssueIds.has(opt.id)}
                          hasDuplicate={duplicateOptionIds.has(opt.id)}
                          variables={variables}
                          rolledValues={rolledValues}
                          angleMode={angleMode}
                          experienceId={experienceId}
                          taskId={taskId ?? ''}
                          taskBlocks={blocks ?? []}
                          onMediaChange={updateOptionMedia}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              );

              return (
                <>
                  {/* Guarded as a whole, not just child by child: every child here is conditional, so an
                      unguarded wrapper became an empty flex item whose parent gap applied both above and
                      below it — a doubled gap under the Answer Setup rule whenever no variable was in
                      play. Matches Ordering/FITB/Scale/GPS, which already guard theirs. */}
                  {(blockLevelToggle || hasVariableInOptions || hasRecallInOptions || optionsAsEquations || hasTrigAnywhere) && (
                    <div className='flex flex-col gap-2 pl-1'>
                      {blockLevelToggle && branchingToggle}

                      {(hasVariableInOptions || hasRecallInOptions || optionsAsEquations) && (
                        <CheckboxRow fieldId='optionsAsEquations'
                          checked={optionsAsEquations}
                          onToggle={() => {
                            const next = !optionsAsEquations;
                            // Symmetric content-flow, applied uniformly to EVERY option regardless
                            // of what it currently holds (2026-08-02 standing rule — previously media
                            // options were left untouched while turning this on, an inconsistency
                            // with how non-media options behaved):
                            // ON: media is always wiped (equations mode suppresses it, matching
                            // Ordering's own identical rule — see that block's own doc comment) and
                            // an option's plain text transfers into `formula` when that text is
                            // ALREADY a valid, parseable formula (e.g. "{n}+10" with no surrounding
                            // prose) — verified by actually evaluating it against sample values, not
                            // guessed from syntax, so a genuine sentence or an image/sound option
                            // both correctly end up with a BLANK formula, never overwriting an
                            // option that already has its own authored formula.
                            // OFF: every option's CURRENT formula (blank or not) is copied back into
                            // `text` as real rich content (a genuine re-editable chip for any
                            // `{name}` it contains, not literal braces) — so a round trip with
                            // nothing re-authored in between ends up BLANK (not restored to
                            // whatever the option originally was before turning equations mode on:
                            // media/prose that couldn't become a formula is a real, accepted loss),
                            // while a formula the teacher DID author while equations mode was on
                            // survives the switch back as literal editable text.
                            const nextOptions = next
                              ? options.map(o => {
                                  if (o.formula?.trim()) return { ...o, media: undefined };
                                  /*
                                   * BRACKETISED FOR A FORMULA, not stripped for a LABEL. The
                                   * label path leaves a recall chip alone so a feedback title
                                   * reads "You said Chinchilla" — through this toggle that
                                   * dropped the reference and kept the word.
                                   *
                                   * AND A TEXT RECALL STOPS THE TRANSFER OUTRIGHT. A calculated
                                   * option is arithmetic, and `FormulaField` draws a chip only
                                   * for a number — a word's alias became an opaque
                                   * `[ara_<uuid>]` in the field, unreadable and undeletable as
                                   * a unit. Refusing the whole option rather than quietly
                                   * dropping the chip and keeping the rest: `2 + 3 {critter}`
                                   * would otherwise become a perfectly valid 5 that nobody
                                   * wrote. The text is left where it is, which is where a word
                                   * belongs.
                                   */
                                  const stripped = optionTextToFormula(o.text, numericAliasIds);
                                  const wordRecall = recallAliasIdsIn(o.text)
                                    .some(id => !numericAliasIds.has(id));
                                  /* A STAND-IN 1 PER RECALL, which is `probeValuesForRecalls`'
                                     whole job: the question here is "is this arithmetic at all",
                                     not what it comes out as, and a recall has no value at
                                     authoring time. Without it every option holding one read as
                                     un-transferable and was blanked by the toggle. */
                                  const probe: Record<string, number> = { ...minSampleValues(variables) };
                                  for (const id of Object.keys(probeValuesForRecalls(stripped))) probe[id] = 1;
                                  const transferable = !wordRecall && stripped.trim() !== ''
                                    && evaluateEquation(stripped, probe, angleMode) !== null;
                                  return { ...o, media: undefined, formula: transferable ? stripped : '' };
                                })
                              : options.map(o => ({ ...o, text: formulaToRichText(o.formula ?? '', new Set(variables.map(v => v.name)), knownAliasIds) }));
                            setOptionsAsEquations(next);
                            setOptions(nextOptions);
                            // Every targeted state's title is derived from whichever source
                            // (text or formula) is active — flipping the toggle without resyncing
                            // left titles baked under the OLD mode until some unrelated edit
                            // happened to trigger a resync, so Flow (which reads the stored title
                            // directly, never live-recomputed) could show a title mixing both
                            // modes' content depending on authoring order. Same resync every other
                            // option-mutating handler already runs (updateOptionText/
                            // updateOptionFormula/removeOption/handleOptionDragEnd).
                            const { feedback: nextFeedback, branches: nextBranches } = updateAllMcqFeedbackSets(
                              states => resyncMcqTargetedTitles(nextOptions, states, t, next)
                            );
                            setFeedback(nextFeedback);
                            if (nextBranches) setConditionalBranches(nextBranches);
                            push(nextOptions, allowMultiple, randomizeOrder, gradingMode, nextFeedback, nextBranches ?? conditionalBranches, conditionalEquation, next);
                          }}
                          label={t('blocks.options.optionsAsEquationsToggle')}
                        />
                      )}

                    </div>
                  )}

                  {/* The carousel, wherever it belongs — around the options on a plain block,
                      and INSIDE the scenario box on a follower, because there the comparison
                      is the row's. One element either way, so the options' own JSX is written
                      once. */}
                  {(() => {
                  const carousel = (children: React.ReactNode) => (
                    <BranchConditionCarousel
                      seedVariable={(() => {
                        /* A follower's variables are usually in the SCENARIO, not the question
                           — that is the whole reason the gate above widened — so the seed looks
                           there too, and only offers one when there is exactly one to offer. */
                        const n = variableChipNames(questionHtmlRef.current);
                        const fromScenario = variableChipNames(scenarioHtml);
                        const all = [...new Set([...n, ...fromScenario])];
                        return all.length === 1 ? all[0] : undefined;
                      })()}
                      ref={branchPrevButtonRef}
                      equation={effEquation ?? ''}
                      onEquationChange={nesting ? (v => writeRow({ conditionalEquation: v })) : updateConditionalEquation}
                      variables={variables}
                      rolledValues={rolledValues}
                      angleMode={angleMode}
                      equationInvalid={nesting ? effEquationInvalid : equationInvalid}
                      equationWorks={effHasEquation && !(nesting ? effEquationInvalid : equationInvalid)}
                      hasError={hasEditorError('no-equation', 'invalid-equation', 'coverage-gap', `branch-${effActiveBranch!.id}-multi`)}
                      branches={effBranches}
                      activeIndex={effIndex}
                      onActiveIndexChange={setActiveBranchIndex}
                      onBranchChange={nesting
                        ? ((id, patch) => writeRow({ conditionalBranches: effBranches.map(b => (b.id === id ? { ...b, ...patch } : b)) as never }))
                        : updateConditionalBranch}
                      onAdd={nesting
                        ? (() => writeRow({ conditionalBranches: [...effBranches, makeRow('fork')] as never }))
                        : addConditionalBranch}
                      onRemove={nesting
                        ? (id => writeRow({ conditionalBranches: effBranches.filter(b => b.id !== id) as never }))
                        : removeConditionalBranch}
                      onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                    >
                      {children}
                    </BranchConditionCarousel>
                  );
                  const answers = <>{optionListJsx}{addOptionButton}</>;
                  const branched = showConditionalUI && effEnabled && effActiveBranch;

                  // FOLLOWING: the scenario box is the outer frame, and the comparison — when
                  // the row has one — sits inside it. Scenario picks the row, the row's own
                  // equation picks the answer.
                  if (activeScenario) return (
                    <ScenarioSetupFrame
                      scenario={activeScenario}
                      name={activeScenarioVersion(activeScenario.scenarioId)?.name}
                      pager={scenarioPager}
                      unreviewedCount={unreviewedCount(scenarioBranches)}
                      onDismissUnreviewed={() => pushScenarioRows(
                        markScenarioReviewed(scenarioBranches ?? [], activeScenario.scenarioId))}
                    >
                      {/* Extra room only in the nested position: here the toggle is a decision
                          ABOUT the box it stands in, and sitting on the frame's own gap-3 it
                          read as the first row of the answer setup rather than its switch. */}
                      {branchingToggle && <div className='pb-2'>{branchingToggle}</div>}
                      {branched ? carousel(answers) : answers}
                    </ScenarioSetupFrame>
                  );
                  return branched ? carousel(answers) : (
                    /* Nothing at all when this block follows nothing — the frame is what says
                       which scope the answers belong to, and there is no second scope here. */
                    <ScenarioSetupFrame
                      scenario={undefined}
                      name={undefined}
                      pager={null}
                      unreviewedCount={0}
                      onDismissUnreviewed={() => {}}
                    >
                      {answers}
                    </ScenarioSetupFrame>
                  );
                  })()}
                </>
              );
            })()}

            </DrivenRowFrame>
          </div>
      }
    />
  );
}
